<template>
  <div ref="paperWorkspace" class="paper-workspace" :class="{ 'is-detail': detailMode, 'is-learning': isLearningMode, 'is-learning-detail': isLearningMode && detailMode, 'is-daily': isDailyMode, 'is-interaction-locked': interactionLocked }" @click="closeFloatingMenus">
    <template v-if="isDailyMode">
      <header v-if="!dailyPreviewOpen" class="daily-header">
        <div class="daily-title-line">
          <strong>Daily</strong>
          <i></i>
          <span class="daily-title-date">
            <template v-for="(part, index) in dailyTodayHeaderParts">
              <span v-if="part.type == 'text'" :key="'text-' + index" class="daily-title-date-part">{{ part.value }}</span>
              <span v-else-if="part.type == 'line'" :key="'line-' + index" class="daily-title-date-separator line" aria-hidden="true"></span>
              <span v-else :key="'separator-' + index" class="daily-title-date-separator text">{{ part.value }}</span>
            </template>
          </span>
        </div>
        <button v-if="!isDailyTodoTab" class="daily-search-button" type="button" :title="ui.search" @click.stop="$emit('global-search')">
          <img :src="paperIcons.search" alt="" />
        </button>
      </header>

      <section v-if="!dailyPreviewOpen" class="daily-page">
        <aside class="daily-date-panel">
          <header class="daily-calendar-head">
            <strong>{{ dailyCalendarYearText }}</strong>
            <button class="daily-calendar-prev" type="button" @click.stop="changeDailyCalendarMonth(-1)"></button>
            <span>{{ dailyCalendarMonthText }}</span>
            <button class="daily-calendar-next" type="button" @click.stop="changeDailyCalendarMonth(1)"></button>
            <button class="daily-calendar-today" type="button" @click.stop="goDailyToday">
              <img :src="paperIcons.calendarDay" alt="" />
              <span>{{ dailyCalendarIconDay }}</span>
            </button>
          </header>
          <div class="daily-weekdays">
            <span v-for="(label, index) in dailyWeekdayLabels" :key="label + '-' + index">{{ label }}</span>
          </div>
          <a-spin :spinning="showDailyCalendarSpinner" wrapper-class-name="daily-calendar-spin">
            <div class="daily-calendar-grid">
              <button
                v-for="cell in dailyCalendarCells"
                :key="cell.key"
                type="button"
                class="daily-date-cell"
                :class="{ muted: !cell.currentMonth, today: cell.today, selected: cell.selected, 'has-data': cell.hasData, 'week-selected': cell.weekSelected, 'week-start': cell.weekStart, 'week-end': cell.weekEnd }"
                @click.stop="selectDailyCalendarDate(cell.dateValue)"
              >
                <span>
                  <em class="daily-date-day">{{ cell.day }}</em>
                  <b class="daily-date-markers" :class="{ single: cell.markers.length == 1, empty: !cell.hasData }">
                    <img
                      v-for="marker in cell.markers"
                      :key="cell.key + '-' + marker.key"
                      class="daily-date-marker"
                      :class="marker.key"
                      :src="marker.icon"
                      alt=""
                      aria-hidden="true"
                    />
                  </b>
                </span>
              </button>
            </div>
          </a-spin>
        </aside>

        <main ref="dailyMainPanel" class="daily-main-panel">
          <header class="daily-list-head">
            <nav class="daily-tabs" role="tablist">
              <button
                v-for="tab in dailyTabs"
                :key="tab.key"
                type="button"
                :class="{ active: dailyActiveTab == tab.key }"
                @click.stop="setDailyTab(tab.key)"
              >{{ tab.label }}</button>
            </nav>
            <div class="daily-list-actions" @click.stop>
              <div class="daily-filter-wrap" :class="{ open: dailyPeriodMenuOpen, 'drop-left': dailyPeriodMenuDropLeft }">
                <button class="daily-filter-button has-calendar" type="button" @click="toggleDailyPeriodMenu">
                  <img class="daily-filter-calendar" :src="dailyActivePeriodIcon" alt="" />
                  <em>{{ dailyActivePeriodLabel }}</em>
                  <i></i>
                </button>
                <div v-if="dailyPeriodMenuOpen" class="daily-filter-menu daily-period-menu">
                  <button
                    v-for="period in dailyPeriodOptions"
                    :key="period.key"
                    type="button"
                    :class="{ active: dailyPeriod == period.key }"
                    @click="setDailyPeriod(period.key)"
                  >
                    <span class="daily-period-label">
                      <img class="daily-period-icon" :src="period.icon" alt="" />
                      <span>{{ period.label }}</span>
                    </span>
                    <i v-if="dailyPeriod == period.key"></i>
                  </button>
                </div>
              </div>
              <div v-if="isDailyTodoTab" class="daily-filter-wrap daily-finish-wrap" :class="{ open: dailyFinishMenuOpen, 'drop-left': dailyFinishMenuDropLeft }">
                <button class="daily-filter-button" type="button" @click="toggleDailyFinishMenu">
                  <em>{{ dailyActiveFinishLabel }}</em>
                  <i></i>
                </button>
                <div v-if="dailyFinishMenuOpen" class="daily-filter-menu daily-finish-menu">
                  <button
                    v-for="option in dailyFinishOptions"
                    :key="option.key"
                    type="button"
                    :class="{ active: dailyTodoFinishFilter == option.key }"
                    @click="setDailyFinishFilter(option.key)"
                  >
                    <span>{{ option.label }}</span>
                    <i v-if="dailyTodoFinishFilter == option.key"></i>
                  </button>
                </div>
              </div>
              <div v-if="!isDailyTodoTab" class="daily-view-switch" role="group">
                <img :src="dailyViewSwitchIcon" alt="" />
                <button
                  type="button"
                  class="daily-view-hotspot daily-view-list"
                  :aria-label="ui.listView"
                  @click="setDailyViewMode('list')"
                ></button>
                <button
                  type="button"
                  class="daily-view-hotspot daily-view-grid"
                  :aria-label="ui.gridView"
                  @click="setDailyViewMode('grid')"
                ></button>
              </div>
            </div>
          </header>

          <div
            v-if="hasDailySelection"
            ref="dailySelectionLayer"
            class="daily-selection-layer"
            :class="{ dragging: dailySelectionBarDragging }"
            :style="dailySelectionLayerStyle"
            @mousedown.stop="startDailySelectionBarDrag"
            @click.stop
          >
            <div class="daily-selection-bar">
              <button type="button" @click.stop="clearDailySelection"><span></span></button>
              <strong>{{ ui.selectedPrefix }} {{ dailySelectedCount }} {{ ui.selectedSuffix }}</strong>
              <button type="button" @click.stop="downloadSelectedDailyGroups">
                <img :src="paperIcons.download" alt="" />
                <span>{{ ui.download }}</span>
              </button>
            </div>
          </div>

          <section class="daily-content" :class="{ 'is-grid': !isDailyTodoTab && dailyViewMode == 'grid' }">
            <a-spin :spinning="dailyListLoading" wrapper-class-name="daily-list-spin">
              <div class="daily-content-inner">
                <div v-if="!dailyListLoading && dailyDisplayItems.length == 0" class="daily-empty" :class="{ 'is-todo-empty': isDailyTodoTab }">
                  <img :src="isDailyTodoTab ? paperIcons.todoEmpty : paperIcons.recordEmpty" alt="" />
                  <p v-if="!isDailyTodoTab">{{ appEmptyText }}</p>
                </div>

                <div v-if="!dailyListLoading && isDailyTodoTab && dailyTodoItems.length > 0" class="daily-todo-list">
                  <div
                    v-for="item in dailyTodoItems"
                    :key="item.id"
                    class="daily-todo-row"
                    :class="{ done: item.isFinish == 1 }"
                  >
                    <span class="daily-todo-level" :class="getDailyTodoLevelClass(item.level)">
                      <img v-if="Number(item.level) === 0" :src="paperIcons.todoImportantUrgent" alt="" />
                      <img v-else-if="Number(item.level) === 1" :src="paperIcons.todoUrgent" alt="" />
                      <img v-else-if="Number(item.level) === 2" :src="paperIcons.todoImportant" alt="" />
                    </span>
                    <span class="daily-todo-content">
                      <img v-if="item.classification == 1 && item.imageUrl" :src="item.imageUrl" alt="" loading="lazy" decoding="async" />
                      <strong v-else>{{ item.content }}</strong>
                    </span>
                    <span class="daily-todo-date">{{ formatDailyTodoDate(item.date) }}</span>
                  </div>
                </div>

                <div
                  v-if="!dailyListLoading && !isDailyTodoTab && dailyViewMode == 'grid' && dailyGroupedItems.length > 0"
                  ref="dailyGrid"
                  class="daily-grid"
                  :class="{ 'all-empty': dailyGridAllCoversEmpty }"
                >
                  <button
                    v-for="(item, index) in dailyGroupedItems"
                    :key="item.id"
                    type="button"
                    class="daily-card"
                    :class="getDailyGridItemClasses(item, index)"
                    @click="handleDailyGroupClick(item, $event)"
                    @dblclick.stop.prevent="openDailyGroupPreview(item, $event)"
                  >
                    <span class="daily-card-cover">
                      <img v-if="item.cover" :src="item.cover" alt="" loading="lazy" decoding="async" />
                      <img v-else class="daily-empty-cover-icon" :src="paperIcons.dailyEmptyCover" alt="" />
                    </span>
                    <span class="daily-card-title-row">
                      <strong :title="item.name">{{ item.name }}</strong>
                      <img v-if="item.favorite" class="daily-favorite-status daily-card-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                    </span>
                    <span class="daily-card-count">{{ item.count }}</span>
                  </button>
                </div>

                <div v-if="!dailyListLoading && !isDailyTodoTab && dailyViewMode == 'list' && dailyGroupedItems.length > 0" class="daily-table">
                  <div
                    v-for="item in dailyGroupedItems"
                    :key="item.id"
                    class="daily-row"
                    :class="{ selected: isDailyGroupSelected(item) }"
                    role="button"
                    tabindex="0"
                    @click="handleDailyGroupClick(item, $event)"
                    @dblclick.stop.prevent="openDailyGroupPreview(item, $event)"
                    @keyup.enter="toggleDailyGroupSelection(item)"
                  >
                    <span class="daily-row-cover">
                      <img v-if="item.cover" :src="item.cover" alt="" loading="lazy" decoding="async" />
                      <img v-else class="daily-empty-cover-icon" :src="paperIcons.dailyEmptyCover" alt="" />
                    </span>
                    <strong :title="item.name">{{ item.name }}</strong>
                    <span class="daily-row-count">{{ item.count }} {{ ui.page }}</span>
                    <span class="daily-row-date">{{ formatDate(item.date) }}</span>
                    <span class="daily-row-favorite-status">
                      <img v-if="item.favorite" class="daily-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                    </span>
                    <span class="paper-more-wrap" @click.stop>
                      <button class="paper-more-button" type="button" :title="ui.more" @click="toggleMoreMenu('daily-' + item.id)">
                        <svg class="paper-more-icon" viewBox="0 0 12 4" aria-hidden="true" focusable="false">
                          <circle cx="2" cy="2" r="1.25"></circle>
                          <circle cx="6" cy="2" r="1.25"></circle>
                          <circle cx="10" cy="2" r="1.25"></circle>
                        </svg>
                      </button>
                      <div v-if="moreMenuId == 'daily-' + item.id" class="paper-more-menu">
                        <button type="button" @click="downloadDailyGroupFromMenu(item)">
                          <img :src="paperIcons.download" alt="" />
                          <span>{{ ui.download }}</span>
                        </button>
                      </div>
                    </span>
                  </div>
                </div>
              </div>
            </a-spin>
          </section>
        </main>
      </section>

      <section v-else class="daily-preview-page" :class="{ 'pages-open': dailyPreviewGridOpen }">
        <header class="paper-detail-header">
          <div class="paper-detail-title">
            <button class="paper-back-button" type="button" :aria-label="ui.back" @click="handleDailyPreviewBack">
              <span></span>
            </button>
            <strong :title="dailyPreviewTitle">{{ dailyPreviewTitle }}</strong>
          </div>
          <div class="paper-detail-actions">
            <button class="paper-tool-button" type="button" :disabled="dailyPreviewLoading || !canDownloadCurrentDailyPreview" :title="ui.download" @click.stop="downloadCurrentDailyPreview">
              <img :src="paperIcons.download" alt="" />
            </button>
            <button v-if="!dailyPreviewGridOpen" class="paper-tool-button" type="button" :class="{ active: dailyPreviewGridOpen }" :disabled="dailyPreviewLoading || dailyPreviewGridOpen || dailyPreviewItems.length == 0" :title="ui.pageGrid" @click.stop="toggleDailyPreviewGrid">
              <img :src="paperIcons.pageGrid" alt="" />
            </button>
            <button v-if="!dailyPreviewGridOpen" class="paper-tool-button" type="button" :disabled="dailyPreviewLoading || dailyPreviewGridOpen || dailyPreviewZoomLevel >= 5" :title="ui.zoomIn" @click.stop="zoomInDailyPreview">
              <img :src="paperIcons.zoomIn" alt="" />
              <span v-if="isPreviewZoomTipVisible('daily', 'in')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
            </button>
            <button v-if="!dailyPreviewGridOpen" class="paper-tool-button" type="button" :disabled="dailyPreviewLoading || dailyPreviewGridOpen || dailyPreviewZoomLevel <= -3" :title="ui.zoomOut" @click.stop="zoomOutDailyPreview">
              <img :src="paperIcons.zoomOut" alt="" />
              <span v-if="isPreviewZoomTipVisible('daily', 'out')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
            </button>
          </div>
        </header>

        <section class="paper-detail-body" :class="{ 'pages-open': dailyPreviewGridOpen, 'is-resolving': dailyPreviewLoading }">
          <a-spin :spinning="dailyPreviewLoading" wrapper-class-name="daily-preview-spin">
            <div v-if="dailyPreviewGridOpen" ref="dailyPreviewGrid" class="daily-preview-grid">
              <button
                v-for="(item, index) in dailyPreviewItems"
                :key="item.id"
                type="button"
                :class="{ active: dailyPreviewCurrentItem && dailyPreviewCurrentItem.id == item.id, selected: isDailyPreviewSelected(item), 'grid-edge-top': isDailyPreviewGridTopEdge(index), 'grid-selected-row-start': isDailyPreviewGridSelectedRowStart(index), 'grid-selection-continues-below': isDailyPreviewGridSelectionContinuesBelow(index) }"
                @click="toggleDailyPreviewSelection(item, $event)"
                @dblclick="openDailyPreviewItem(item)"
              >
                <span>
                  <img v-if="item.imageUrl" :src="item.imageUrl" alt="" loading="lazy" decoding="async" />
                  <em v-else class="page-fallback" :class="{ pdf: item.pdfPreviewUrl }">{{ item.pdfPreviewUrl ? 'PDF' : '' }}</em>
                </span>
                <strong :title="'Page ' + item.pageNo">Page {{ item.pageNo }}</strong>
              </button>
            </div>
            <div
              v-else
              ref="dailyPreviewShell"
              class="paper-preview-shell"
              :class="{ dragging: dailyPreviewDragState }"
              :style="{ '--paper-preview-zoom': dailyPreviewZoomValue }"
              @mousedown="startDailyPreviewDrag"
            >
              <div class="paper-preview-zoom">
                <div class="paper-preview-stage">
                  <img v-if="dailyPreviewCurrentItem && dailyPreviewCurrentItem.imageUrl" :src="dailyPreviewCurrentItem.imageUrl" alt="" decoding="async" @dragstart.prevent />
                  <div v-else class="paper-preview-empty"></div>
                </div>
              </div>
            </div>
          </a-spin>
          <div v-if="!dailyPreviewGridOpen && dailyPreviewItems.length > 0" class="paper-page-controls">
            <div class="paper-page-count">
              <button type="button" :disabled="dailyPreviewIndex <= 0" @click="goPrevDailyPreview"></button>
              <span>{{ dailyPreviewIndex + 1 }}/{{ dailyPreviewItems.length }}</span>
              <button type="button" :disabled="dailyPreviewIndex >= dailyPreviewItems.length - 1" @click="goNextDailyPreview"></button>
            </div>
          </div>
        </section>
      </section>

      <div v-if="paperDownloadFormatModalOpen" class="paper-download-mask" @click="closePaperDownloadFormatModal"></div>
      <section v-if="paperDownloadFormatModalOpen" class="paper-download-modal" @click.stop>
        <header>
          <strong>{{ ui.chooseFormat }}</strong>
          <button type="button" :aria-label="ui.close" @click="closePaperDownloadFormatModal">
            <span></span>
          </button>
        </header>
        <div class="paper-download-options">
          <label>
            <input v-model="paperDownloadFormat" type="radio" value="pdf" />
            <span>PDF</span>
          </label>
          <label>
            <input v-model="paperDownloadFormat" type="radio" value="png" />
            <span>PNG</span>
          </label>
        </div>
        <footer>
          <button type="button" @click="closePaperDownloadFormatModal">{{ ui.cancel }}</button>
          <button type="button" @click="confirmPaperDownloadFormat">{{ ui.ok }}</button>
        </footer>
      </section>
    </template>
    <template v-else>
    <header v-if="!detailMode || isLearningMode" class="paper-list-header">
      <div class="paper-breadcrumb">
        <button class="paper-breadcrumb-root" type="button" @click="$emit('back-root')">{{ ui.myFiles }}</button>
        <span
          v-for="(crumb, index) in paperBreadcrumbItems"
          :key="(crumb.resourceId || 'paper-root') + '-' + index"
          class="paper-breadcrumb-segment"
        >
          <img class="paper-breadcrumb-separator" :src="paperIcons.breadcrumbSeparator" alt="" />
          <button
            v-if="index < paperBreadcrumbItems.length - 1"
            class="paper-breadcrumb-link"
            type="button"
            :title="crumb.name"
            @click="openPaperBreadcrumb(index)"
          >{{ crumb.name }}</button>
          <strong v-else :title="crumb.name">{{ crumb.name }}</strong>
        </span>
      </div>
      <div class="paper-header-actions">
        <button v-if="false" class="paper-icon-button" type="button" :title="ui.createFolder" @click.stop="fakeAction(ui.createFolder)">
          <img :src="paperIcons.createFolder" alt="" />
        </button>
        <button class="paper-icon-button" type="button" :title="ui.search" @click.stop="$emit('global-search')">
          <img :src="paperIcons.search" alt="" />
        </button>
        <button v-if="false" class="paper-import-button" type="button" @click.stop="fakeAction(ui.importPdf)">
          <img :src="paperIcons.import" alt="" />
          <span>{{ ui.importPdf }}</span>
        </button>
      </div>
    </header>

    <header v-else class="paper-detail-header">
      <div class="paper-detail-title">
        <button class="paper-back-button" type="button" :aria-label="ui.back" @click="handleDetailBack">
          <span></span>
        </button>
        <strong :title="paperDetailTitle">{{ paperDetailTitle }}</strong>
      </div>
      <div class="paper-detail-actions">
        <button
          v-if="!pageGridOpen"
          class="paper-tool-button"
          type="button"
          :class="{ active: recordPanelOpen && !recordTextOpen }"
          :disabled="paperDetailLoading"
          :title="ui.recordings"
          @click.stop="toggleRecordPanel"
        >
          <img :src="paperIcons.record" alt="" />
        </button>
        <button class="paper-tool-button" type="button" :disabled="paperDetailLoading || detailPages.length == 0 || (pageGridOpen && selectedDetailPages.length == 0)" :title="ui.download" @click.stop="downloadCurrentPage">
          <img :src="paperIcons.download" alt="" />
        </button>
        <button
          v-if="!pageGridOpen"
          class="paper-tool-button"
          type="button"
          :class="{ active: pageGridOpen }"
          :disabled="paperDetailLoading || pageGridOpen || detailPages.length == 0"
          :title="ui.pageGrid"
          @click.stop="togglePageGrid"
        >
          <img :src="paperIcons.pageGrid" alt="" />
        </button>
        <button v-if="!pageGridOpen" class="paper-tool-button" type="button" :disabled="paperDetailLoading || pageGridOpen || zoomLevel >= 5" :title="ui.zoomIn" @click.stop="zoomIn">
          <img :src="paperIcons.zoomIn" alt="" />
          <span v-if="isPreviewZoomTipVisible('paper', 'in')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
        </button>
        <button v-if="!pageGridOpen" class="paper-tool-button" type="button" :disabled="paperDetailLoading || pageGridOpen || zoomLevel <= -3" :title="ui.zoomOut" @click.stop="zoomOut">
          <img :src="paperIcons.zoomOut" alt="" />
          <span v-if="isPreviewZoomTipVisible('paper', 'out')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
        </button>
      </div>
    </header>

    <template v-if="!detailMode || isLearningMode">
      <div
        v-if="hasPaperSelection"
        ref="paperSelectionLayer"
        class="paper-selection-layer"
        :class="{ dragging: selectionBarDragging }"
        :style="selectionBarLayerStyle"
        @mousedown.stop="startSelectionBarDrag"
        @click.stop
      >
        <div class="paper-selection-bar">
          <button class="paper-selection-close" type="button" :aria-label="ui.cancel" @click="clearSelection">
            <span></span>
          </button>
          <span>{{ ui.selectedPrefix }} {{ selectedCount }} {{ ui.selectedSuffix }}</span>
          <div class="paper-selection-actions">
            <button type="button" :disabled="!canBatchSelectionAction" @click="handleSelectionAction('download')">
              <img :src="paperIcons.download" alt="" />
              <span>{{ ui.download }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="paper-toolbar">
        <div v-if="false" class="paper-filter" @click.stop>
          <button class="paper-filter-button" type="button" @click="filterMenuOpen = !filterMenuOpen">
            <img class="paper-filter-icon" :src="paperIcons.filterAll" alt="" />
            <span>{{ activeFilterLabel }}</span>
            <span class="filter-arrow" :class="{ open: filterMenuOpen }"></span>
          </button>
          <div v-if="filterMenuOpen" class="paper-filter-menu">
            <button
              v-for="item in filterOptions"
              :key="item.key"
              type="button"
              :class="{ active: paperFilter == item.key }"
              @click="selectFilter(item.key)"
            >
              <img class="paper-menu-icon" :src="item.icon" alt="" />
              <span>{{ item.label }}</span>
              <img v-if="paperFilter == item.key" class="filter-check" :src="paperIcons.check" alt="" />
            </button>
          </div>
        </div>

        <div class="paper-toolbar-right">
          <div class="paper-sort" @click.stop>
            <button class="paper-sort-button" type="button" @click="sortMenuOpen = !sortMenuOpen">{{ activeSortLabel }}</button>
            <div v-if="sortMenuOpen" class="paper-sort-menu">
              <button
                v-for="item in sortOptions"
                :key="item.key"
                type="button"
                :class="{ active: sortField == item.key }"
                @click="selectSort(item.key)"
              >
                <span>{{ item.label }}</span>
                <img v-if="sortField == item.key" class="filter-check" :src="paperIcons.check" alt="" />
              </button>
            </div>
          </div>
          <button class="paper-order-button" type="button" :title="sortDesc ? ui.desc : ui.asc" @click.stop="toggleSortOrder">
            <img :src="sortDesc ? paperIcons.sortDesc : paperIcons.sortAsc" alt="" />
          </button>
          <div class="paper-view-switch" role="group">
            <img :src="viewSwitchIcon" alt="" />
            <button
              type="button"
              class="paper-view-hotspot paper-view-list"
              :aria-label="ui.listView"
              @click.stop="setViewMode('list')"
            ></button>
            <button
              type="button"
              class="paper-view-hotspot paper-view-grid"
              :aria-label="ui.gridView"
              @click.stop="setViewMode('grid')"
            ></button>
          </div>
        </div>
      </div>

      <div v-if="searchOpen && !hasPaperSelection" class="paper-search-line" @click.stop>
        <img :src="paperIcons.search" alt="" />
        <input ref="paperSearchInput" v-model.trim="searchKeyword" type="text" :placeholder="ui.search" maxlength="125" />
        <button v-if="searchKeyword" type="button" @click="searchKeyword = ''"></button>
      </div>

      <section ref="paperListArea" class="paper-list-area" :class="{ 'is-grid': viewMode == 'grid' }" @scroll.passive="handlePaperListScroll">
        <a-spin :spinning="paperListLoading" wrapper-class-name="paper-list-spin">
          <div class="paper-list-content">
            <div v-if="!paperListLoading && visiblePaperItems.length == 0" class="paper-empty">
              <img :src="paperIcons.recordEmpty" alt="" />
              <p>{{ appEmptyText }}</p>
            </div>

            <div v-if="!paperListLoading && viewMode == 'grid' && visiblePaperItems.length > 0" ref="paperGrid" class="paper-grid">
              <div
                v-for="(item, index) in visiblePaperItems"
                :key="item.id"
                class="paper-card"
                :class="getPaperGridItemClasses(item, index)"
                role="button"
                tabindex="0"
                @click="handlePaperItemClick(item, $event)"
                @dblclick.stop.prevent="handlePaperItemDblClick(item, $event)"
                @keyup.enter="handlePaperItemKeyup(item)"
              >
                <span class="paper-cover" :class="{ empty: !shouldDisplayGridCover(item), locked: item.encrypted, template: item.template }">
                  <img v-if="shouldDisplayGridCover(item)" class="paper-cover-image" :src="getPaperGridCoverImageUrl(item)" alt="" loading="lazy" decoding="async" @error="markPaperGridCoverFailed(item)" />
                  <img v-else class="paper-cover-default" :class="{ 'folder-icon': item.isFolder && !item.encrypted, 'encrypt-icon': item.isFolder && item.encrypted }" :src="getFallbackCover(item, 'grid')" alt="" />
                </span>
                <span class="paper-card-title-row">
                  <span class="paper-card-name" :title="item.name">{{ item.name }}</span>
                  <img v-if="item.favorite" class="paper-favorite-status paper-card-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                </span>
                <span class="paper-card-meta">
                  <span>{{ formatDate(getDisplayDate(item)) }}</span>
                  <span>{{ formatPageCount(item) }}</span>
                </span>
              </div>
            </div>

            <div v-if="!paperListLoading && viewMode == 'list' && visiblePaperItems.length > 0" class="paper-table">
              <div
                v-for="item in visiblePaperItems"
                :key="item.id"
                class="paper-row"
                :class="{ selected: isPaperSelected(item) }"
                role="button"
                tabindex="0"
                :title="item.name"
                @click="handlePaperItemClick(item, $event)"
                @dblclick.stop.prevent="handlePaperItemDblClick(item, $event)"
                @keyup.enter="handlePaperItemKeyup(item)"
              >
                <span class="paper-row-cover" :class="{ empty: !shouldDisplayCover(item), locked: item.encrypted }">
                  <img v-if="shouldDisplayCover(item)" class="paper-row-cover-image" :src="item.cover" alt="" loading="lazy" decoding="async" @error="markCoverFailed(item)" />
                  <img v-else class="paper-row-cover-default" :class="{ 'folder-icon': item.isFolder && !item.encrypted, 'encrypt-icon': item.encrypted }" :src="getFallbackCover(item, 'list')" alt="" />
                </span>
                <strong>{{ item.name }}</strong>
                <span class="paper-row-count">{{ formatPageCount(item) }}</span>
                <span class="paper-row-date">{{ formatDate(getDisplayDate(item)) }}</span>
                <span class="paper-row-favorite-status">
                  <img v-if="item.favorite" class="paper-favorite-status" :src="paperIcons.favoriteFilled" :alt="ui.favorite" />
                </span>
                <span class="paper-more-wrap" @click.stop>
                  <button class="paper-more-button" type="button" :title="ui.more" @click="toggleMoreMenu(item.id)">
                    <svg class="paper-more-icon" viewBox="0 0 12 4" aria-hidden="true" focusable="false">
                      <circle cx="2" cy="2" r="1.25"></circle>
                      <circle cx="6" cy="2" r="1.25"></circle>
                      <circle cx="10" cy="2" r="1.25"></circle>
                    </svg>
                  </button>
                  <div v-if="moreMenuId == item.id" class="paper-more-menu">
                    <button type="button" :disabled="!isPaperDownloadableItem(item)" @click="handleItemMenuAction('download', item)">
                      <img :src="paperIcons.download" alt="" />
                      <span>{{ ui.download }}</span>
                    </button>
                  </div>
                </span>
              </div>
            </div>
            <div v-if="paperListAppendLoading" class="list-bottom-loading">
              <a-spin size="small" :spinning="true" />
              <span>{{ ui.loading }}</span>
            </div>
          </div>
        </a-spin>
      </section>
    </template>

    <section v-else class="paper-detail-body" :class="{ 'pages-open': pageGridOpen, 'is-resolving': paperDetailLoading }">
      <div v-if="paperDetailLoading || paperDetailError" class="paper-detail-resolving">
        <div class="paper-detail-progress-box" :class="{ error: paperDetailError }">
          <strong>{{ paperDetailError ? ui.loadFailed : ui.loading }}</strong>
          <span v-if="!paperDetailError">{{ paperDetailProgress }}%</span>
          <div v-if="!paperDetailError" class="paper-detail-progress-track">
            <i :style="{ width: paperDetailProgress + '%' }"></i>
          </div>
          <p v-if="paperDetailError">{{ paperDetailError }}</p>
          <button v-if="paperDetailError" type="button" @click.stop="retryPaperDetailLoad">{{ ui.retry }}</button>
        </div>
      </div>
      <template v-else>
        <div v-if="pageGridOpen" ref="paperPagesGrid" class="paper-pages-grid">
          <button
            v-for="(page, index) in detailPages"
            :key="page.pageNo"
            type="button"
            :class="{ active: page.pageNo == currentPageNo, selected: isDetailPageSelected(page.pageNo), 'grid-edge-top': isPaperPagesGridTopEdge(index), 'grid-selected-row-start': isPaperPagesGridSelectedRowStart(index), 'grid-selection-continues-below': isPaperPagesGridSelectionContinuesBelow(index) }"
            @click="toggleDetailPageSelection(page.pageNo, $event)"
            @dblclick="openDetailPageFromGrid(page.pageNo)"
          >
            <span>
              <img v-if="page.imageUrl" :src="page.imageUrl" alt="" loading="lazy" decoding="async" />
              <em v-else class="page-fallback" :class="{ pdf: page.pdfPreviewUrl }">{{ page.pdfPreviewUrl ? 'PDF' : '' }}</em>
            </span>
            <strong :title="'Page ' + page.pageNo">Page {{ page.pageNo }}</strong>
          </button>
        </div>
        <div
          v-else
          ref="paperPreviewShell"
          class="paper-preview-shell"
          :class="{ dragging: paperPreviewDragState }"
          :style="{ '--paper-preview-zoom': zoomValue }"
          @mousedown="startPaperPreviewDrag"
        >
          <div class="paper-preview-zoom">
            <div class="paper-preview-stage">
              <iframe v-if="currentPage && currentPage.pdfPreviewUrl" class="paper-preview-pdf" :src="currentPage.pdfPreviewUrl" title="PDF" @mousedown.stop></iframe>
              <img v-else-if="currentPage && currentPage.imageUrl" :key="currentPage.imageUrl" class="paper-preview-page-image" :class="{ 'is-loading': isPaperPreviewPageImageLoading(currentPage) }" :src="currentPage.imageUrl" alt="" decoding="async" fetchpriority="high" @load="handlePaperPreviewPageImageLoad($event, currentPage)" @error="handlePaperPreviewPageImageError($event, currentPage)" @dragstart.prevent />
              <div v-else class="paper-preview-empty"></div>
              <div v-if="isPaperPreviewPageImageLoading(currentPage)" class="paper-preview-page-loading" aria-hidden="true">
                <span class="paper-preview-page-loading-ring"></span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!pageGridOpen && detailPages.length > 0" class="paper-page-controls">
        <div class="paper-page-count">
          <button type="button" :disabled="currentPageIndex <= 0" @click="goPrevPage"></button>
          <span>{{ currentPageIndex + 1 }}/{{ detailPages.length }}</span>
          <button type="button" :disabled="currentPageIndex >= detailPages.length - 1" @click="goNextPage"></button>
        </div>
      </div>

      <div v-if="recordPanelOpen" class="paper-record-mask" @click="closeRecordPanel"></div>
      <section v-if="recordPanelOpen" class="paper-record-panel" @click.stop>
        <button class="paper-record-close" type="button" :aria-label="ui.close" @click="closeRecordPanel">
          <span></span>
        </button>
        <template v-if="!recordTextOpen">
          <header>{{ ui.allRecordings }}</header>
          <div v-if="detailRecordings.length > 0" class="record-list">
            <div v-for="recording in detailRecordings" :key="recording.id" class="record-row">
              <img class="record-icon" :src="paperIcons.record" alt="" />
              <div class="record-main">
                <strong>{{ recording.recordingName }}</strong>
                <span>{{ formatDuration(recording.duration) }} <em></em> {{ formatDateTime(recording.recordedAtMs) }}</span>
              </div>
              <button
                class="record-action"
                type="button"
                :disabled="!hasTranscript(recording)"
                :title="ui.transcript"
                @click="openRecordingText(recording)"
              >
                <img :src="paperIcons.transcript" alt="" />
              </button>
              <button class="record-action" type="button" :title="ui.download" @click="enqueueRecordingDownload(recording)">
                <img :src="paperIcons.download" alt="" />
              </button>
            </div>
          </div>
          <div v-else class="record-empty">
            <img :src="paperIcons.recordEmpty" alt="" />
          </div>
        </template>

        <template v-else>
          <header class="record-text-head">
            <button type="button" @click="closeRecordingText">
              <span></span>
            </button>
            <strong>{{ activeRecordingName }}</strong>
          </header>
          <div class="record-text-body">
            {{ activeRecordingText }}
          </div>
        </template>
      </section>
    </section>

    <div v-if="isLearningMode && detailMode" class="learning-detail-mask" @click="closeLearningDetailModal"></div>
    <section v-if="isLearningMode && detailMode" class="learning-detail-modal" @click.stop>
      <div v-if="paperDetailLoading || paperDetailError" class="learning-detail-resolving">
        <div class="paper-detail-progress-box" :class="{ error: paperDetailError }">
          <strong>{{ paperDetailError ? ui.loadFailed : ui.loading }}</strong>
          <span v-if="!paperDetailError">{{ paperDetailProgress }}%</span>
          <div v-if="!paperDetailError" class="paper-detail-progress-track">
            <i :style="{ width: paperDetailProgress + '%' }"></i>
          </div>
          <p v-if="paperDetailError">{{ paperDetailError }}</p>
          <button v-if="paperDetailError" type="button" @click.stop="retryPaperDetailLoad">{{ ui.retry }}</button>
        </div>
      </div>

      <template v-else>
        <section v-if="activeLearningTab == 'summary'" class="learning-summary-panel">
          <button class="learning-panel-close" type="button" :aria-label="ui.close" @click="closeLearningDetailModal">
            <span></span>
          </button>
          <header class="learning-summary-head">
            <div class="learning-summary-main">
              <strong :title="paperDetailTitle">{{ paperDetailTitle }}</strong>
              <p class="learning-summary-meta">
                <span class="learning-meta-size">{{ learningFileSizeText }}</span>
                <span class="learning-meta-chip">{{ learningFileFormatText }}</span>
                <span class="learning-meta-chip is-sync" :class="learningSyncStatusClass">
                  <i></i>
                  <span>{{ learningSyncStatusText }}</span>
                </span>
              </p>
            </div>
          </header>

          <div class="learning-summary-list">
            <div class="learning-summary-row is-progress">
              <strong>{{ ui.readingProgress }}</strong>
              <span class="learning-summary-progress">
                <i :style="{ width: learningProgressPercent + '%' }"></i>
              </span>
              <em>{{ learningProgressText }}</em>
            </div>
            <button
              v-for="row in learningSummaryRows"
              :key="row.key"
              class="learning-summary-row"
              type="button"
              @click="setLearningTab(row.key)"
            >
              <strong>{{ row.label }}</strong>
              <em>{{ row.count }}</em>
            </button>
          </div>
        </section>

        <nav v-if="activeLearningTab != 'summary' && !learningHighlightDetailOpen" class="learning-tabs" role="tablist">
          <div class="learning-tabs-main">
            <button class="learning-tabs-back" type="button" :aria-label="ui.back" @click="returnToLearningSummary">
              <span></span>
            </button>
            <button
              v-for="tab in learningTabs"
              :key="tab.key"
              type="button"
              :class="['learning-tab-button', { active: activeLearningTab == tab.key }]"
              @click="setLearningTab(tab.key)"
            >
              <span>{{ tab.label }}</span>
              <em>{{ tab.count }}</em>
            </button>
          </div>
          <div class="learning-tabs-right">
            <div v-if="activeLearningTab == 'annotations'" class="learning-tabs-tools" @click.stop>
              <div class="learning-view-switch" role="group">
                <img :src="learningAnnotationViewSwitchIcon" alt="" />
                <button
                  type="button"
                  class="learning-view-hotspot learning-view-list"
                  :aria-label="ui.listView"
                  @click="setLearningAnnotationViewMode('list')"
                ></button>
                <button
                  type="button"
                  class="learning-view-hotspot learning-view-grid"
                  :aria-label="ui.gridView"
                  @click="setLearningAnnotationViewMode('grid')"
                ></button>
              </div>
            </div>
            <button class="learning-tabs-close" type="button" :aria-label="ui.close" @click="closeLearningDetailModal">
              <span></span>
            </button>
          </div>
        </nav>

        <div v-if="activeLearningTab == 'bookmarks'" class="learning-detail-content">
          <div v-if="learningBookmarks.length > 0" class="learning-list-box learning-bookmark-list">
            <button
              v-for="bookmark in learningBookmarks"
              :key="bookmark.id"
              class="learning-info-row learning-bookmark-row"
              type="button"
            >
              <span class="learning-bookmark-main">
                <strong>{{ bookmark.title }}</strong>
                <em>{{ formatDate(bookmark.createdAt) }}</em>
              </span>
              <span class="learning-row-page">{{ formatLearningPageBadge(bookmark) }}</span>
            </button>
          </div>
          <div v-else class="learning-empty-box">{{ ui.noBookmarks }}</div>
        </div>

        <div v-if="activeLearningTab == 'highlights' && learningHighlightDetailOpen && activeLearningHighlight" class="learning-highlight-page">
          <header>
            <button class="learning-highlight-back" type="button" :aria-label="ui.back" @click="closeLearningHighlightDetail">
              <span></span>
            </button>
            <strong :title="activeLearningHighlight.title">{{ activeLearningHighlight.title }}</strong>
            <div class="learning-highlight-head-actions">
              <button class="learning-tabs-close" type="button" :aria-label="ui.close" @click="closeLearningDetailModal">
                <span></span>
              </button>
            </div>
          </header>
          <article>
            <section class="learning-highlight-body">
              <p class="learning-highlight-meta">
                <span>{{ formatDate(activeLearningHighlight.createdAt) || '--' }}</span>
              </p>
              <p class="learning-highlight-text">{{ activeLearningHighlight.content }}</p>
            </section>
          </article>
        </div>

        <div v-if="activeLearningTab == 'highlights' && !learningHighlightDetailOpen" class="learning-detail-content">
          <div v-if="learningHighlights.length > 0" class="learning-list-box learning-highlight-list">
            <button
              v-for="highlight in learningHighlights"
              :key="highlight.id"
              class="learning-info-row learning-highlight-row"
              :class="{ active: activeLearningHighlight && activeLearningHighlight.id == highlight.id }"
              type="button"
              @click="openLearningHighlight(highlight)"
            >
              <span class="learning-highlight-main">
                <strong :title="highlight.title">{{ highlight.title }}</strong>
                <em>{{ formatDate(highlight.createdAt) || '--' }}</em>
              </span>
              <i></i>
            </button>
          </div>
          <div v-else class="learning-empty-box">{{ ui.noHighlights }}</div>
        </div>

        <div v-if="activeLearningTab == 'annotations'" class="learning-detail-content is-annotations">
          <div v-if="learningAnnotations.length > 0 && learningAnnotationViewMode == 'grid'" ref="learningAnnotationGrid" class="learning-annotation-grid">
            <button
              v-for="(annotation, index) in learningAnnotations"
              :key="annotation.id"
              class="learning-annotation-card"
              :class="getLearningAnnotationGridItemClasses(annotation, index)"
              type="button"
              @click="selectLearningAnnotation(annotation)"
              @dblclick="openLearningAnnotationPreview(annotation)"
            >
              <span class="learning-annotation-cover">
                <img v-if="annotation.pageImageUrl" class="learning-annotation-page" :src="annotation.pageImageUrl" alt="" loading="lazy" decoding="async" />
                <img v-if="annotation.imageUrl" class="learning-annotation-ink" :src="annotation.imageUrl" alt="" loading="lazy" decoding="async" />
              </span>
              <strong :title="annotation.title">{{ annotation.title }}</strong>
              <em>{{ formatDate(annotation.createdAt) }}</em>
            </button>
          </div>

          <div v-if="learningAnnotations.length > 0 && learningAnnotationViewMode == 'list'" class="learning-list-box learning-annotation-list">
            <button
              v-for="annotation in learningAnnotations"
              :key="annotation.id"
              class="learning-annotation-row"
              :class="{ active: activeLearningAnnotationId && activeLearningAnnotationId == annotation.id }"
              type="button"
              @click="selectLearningAnnotation(annotation)"
              @dblclick="openLearningAnnotationPreview(annotation)"
            >
              <span class="learning-annotation-cover small">
                <img v-if="annotation.pageImageUrl" class="learning-annotation-page" :src="annotation.pageImageUrl" alt="" loading="lazy" decoding="async" />
                <img v-if="annotation.imageUrl" class="learning-annotation-ink" :src="annotation.imageUrl" alt="" loading="lazy" decoding="async" />
              </span>
              <strong :title="annotation.title">{{ annotation.title }}</strong>
              <em>{{ formatDate(annotation.createdAt) }}</em>
            </button>
          </div>

          <div v-if="learningAnnotations.length == 0" class="learning-empty-box">{{ ui.noAnnotations }}</div>
        </div>
      </template>
    </section>

    <section v-if="learningAnnotationPreviewOpen" ref="learningAnnotationPreviewModal" class="learning-preview-modal" @click.stop>
      <header class="learning-annotation-preview-head">
        <div class="learning-annotation-preview-title">
          <button class="learning-annotation-preview-back" type="button" :aria-label="ui.back" @click="handleLearningAnnotationPreviewBack">
            <span></span>
          </button>
          <strong>{{ learningAnnotationPreviewHeaderTitle }}</strong>
        </div>
        <div class="learning-annotation-preview-actions">
          <button
            class="learning-annotation-preview-tool"
            type="button"
            :disabled="!canDownloadCurrentLearningAnnotationPreview"
            :title="ui.download"
            @click.stop="downloadCurrentLearningAnnotationPreview"
          >
            <img :src="paperIcons.download" alt="" />
          </button>
          <button
            v-if="!learningAnnotationPreviewGridOpen"
            class="learning-annotation-preview-tool"
            type="button"
            :class="{ active: learningAnnotationPreviewGridOpen }"
            :disabled="learningAnnotationPreviewGridOpen || learningAnnotations.length == 0"
            :title="ui.pageGrid"
            @click.stop="toggleLearningAnnotationPreviewGrid"
          >
            <img :src="paperIcons.pageGrid" alt="" />
          </button>
          <button
            v-if="!learningAnnotationPreviewGridOpen"
            class="learning-annotation-preview-tool"
            type="button"
            :disabled="learningAnnotationPreviewGridOpen || learningAnnotationPreviewZoomLevel >= 5"
            :title="ui.zoomIn"
            @click.stop="zoomInLearningAnnotationPreview"
          >
            <img :src="paperIcons.zoomIn" alt="" />
            <span v-if="isPreviewZoomTipVisible('learning-annotation', 'in')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
          </button>
          <button
            v-if="!learningAnnotationPreviewGridOpen"
            class="learning-annotation-preview-tool"
            type="button"
            :disabled="learningAnnotationPreviewGridOpen || learningAnnotationPreviewZoomLevel <= -3"
            :title="ui.zoomOut"
            @click.stop="zoomOutLearningAnnotationPreview"
          >
            <img :src="paperIcons.zoomOut" alt="" />
            <span v-if="isPreviewZoomTipVisible('learning-annotation', 'out')" class="preview-zoom-tip">{{ previewZoomTip.text }}</span>
          </button>
        </div>
      </header>

      <section class="learning-annotation-preview-body" :class="{ 'pages-open': learningAnnotationPreviewGridOpen }">
        <div v-if="learningAnnotationPreviewGridOpen" ref="learningAnnotationPreviewGrid" class="learning-annotation-preview-pages-grid">
          <button
            v-for="(annotation, index) in learningAnnotations"
            :key="annotation.id"
            type="button"
            :class="{ active: activeLearningAnnotation && activeLearningAnnotation.id == annotation.id, selected: isLearningAnnotationPreviewSelected(annotation.id), 'grid-edge-top': isLearningAnnotationPreviewGridTopEdge(index), 'grid-selected-row-start': isLearningAnnotationPreviewGridSelectedRowStart(index), 'grid-selection-continues-below': isLearningAnnotationPreviewGridSelectionContinuesBelow(index) }"
            @click="toggleLearningAnnotationPreviewSelection(annotation, $event)"
            @dblclick="openLearningAnnotationFromPreviewGrid(annotation)"
          >
            <span>
              <i class="learning-annotation-preview-page-thumb">
                <img v-if="annotation.pageImageUrl" class="learning-annotation-page" :src="annotation.pageImageUrl" alt="" loading="lazy" decoding="async" />
                <img v-if="annotation.imageUrl" class="learning-annotation-ink" :src="annotation.imageUrl" alt="" loading="lazy" decoding="async" />
                <em v-if="!annotation.pageImageUrl && !annotation.imageUrl" class="page-fallback"></em>
              </i>
            </span>
            <strong :title="annotation.pageLabel || annotation.title">{{ annotation.pageLabel || annotation.title }}</strong>
          </button>
        </div>
        <div
          v-else
          ref="learningAnnotationPreviewShell"
          class="learning-annotation-preview-shell"
          :class="{ dragging: learningAnnotationPreviewDragState }"
          :style="{ '--learning-annotation-preview-zoom': learningAnnotationPreviewZoomValue }"
          @mousedown="startLearningAnnotationPreviewDrag"
        >
          <div class="learning-annotation-preview-zoom">
            <div class="learning-annotation-preview-stage">
              <div v-if="activeLearningAnnotation" class="learning-annotation-preview-page">
                <img v-if="activeLearningAnnotation.pageImageUrl" class="learning-annotation-page" :src="activeLearningAnnotation.pageImageUrl" alt="" decoding="async" @dragstart.prevent />
                <img v-if="activeLearningAnnotation.imageUrl" class="learning-annotation-ink" :src="activeLearningAnnotation.imageUrl" alt="" decoding="async" @dragstart.prevent />
              </div>
              <div v-else class="learning-annotation-preview-empty"></div>
            </div>
          </div>
        </div>
      </section>

      <div v-if="!learningAnnotationPreviewGridOpen && learningAnnotations.length > 0" class="learning-annotation-preview-controls">
        <div class="learning-annotation-preview-count">
          <button type="button" :disabled="learningAnnotationPreviewIndex <= 0" @click="goPrevLearningAnnotationPreview"></button>
          <span>{{ learningAnnotationPreviewIndex + 1 }}/{{ learningAnnotations.length }}</span>
          <button type="button" :disabled="learningAnnotationPreviewIndex >= learningAnnotations.length - 1" @click="goNextLearningAnnotationPreview"></button>
        </div>
      </div>
    </section>

    <div v-if="paperPasswordModalOpen" class="paper-password-mask" @click="closePaperPasswordModal"></div>
    <section v-if="paperPasswordModalOpen" class="paper-password-modal" @click.stop>
      <header>{{ ui.passwordTitle }}</header>
      <div class="paper-password-field" :class="{ error: paperPasswordError }">
        <input
          ref="paperPasswordInput"
          v-model="paperPasswordInput"
          :type="paperPasswordVisible ? 'text' : 'password'"
          :placeholder="ui.passwordPlaceholder"
          maxlength="125"
          :disabled="paperPasswordSubmitting"
          @keyup.enter="confirmPaperPassword"
        />
        <button type="button" :disabled="paperPasswordSubmitting" :aria-label="paperPasswordVisible ? ui.passwordHide : ui.passwordShow" @click="togglePaperPasswordVisible">
          <span :class="{ visible: paperPasswordVisible }"></span>
        </button>
      </div>
      <p v-if="paperPasswordError" class="paper-password-error">{{ paperPasswordError }}</p>
      <footer>
        <div>
          <button type="button" :disabled="paperPasswordSubmitting" @click="closePaperPasswordModal">{{ ui.cancel }}</button>
          <button type="button" :disabled="paperPasswordSubmitting" @click="confirmPaperPassword">{{ paperPasswordSubmitting ? ui.passwordConfirming : ui.ok }}</button>
        </div>
      </footer>
    </section>

    <div v-if="paperDownloadFormatModalOpen" class="paper-download-mask" @click="closePaperDownloadFormatModal"></div>
    <section v-if="paperDownloadFormatModalOpen" class="paper-download-modal" @click.stop>
      <header>
        <strong>{{ ui.chooseFormat }}</strong>
        <button type="button" :aria-label="ui.close" @click="closePaperDownloadFormatModal">
          <span></span>
        </button>
      </header>
      <div class="paper-download-options">
        <label>
          <input v-model="paperDownloadFormat" type="radio" value="pdf" />
          <span>PDF</span>
        </label>
        <label>
          <input v-model="paperDownloadFormat" type="radio" value="png" />
          <span>PNG</span>
        </label>
      </div>
      <footer>
        <button type="button" @click="closePaperDownloadFormatModal">{{ ui.cancel }}</button>
        <button type="button" @click="confirmPaperDownloadFormat">{{ ui.ok }}</button>
      </footer>
    </section>
    </template>
  </div>
</template>

<script>
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import pako from 'pako'
import pdfjsLib from '@/utils/pdfjs'
import { getLearningSyncList, getLearningSyncDetail, getResourceDailySyncList, getResourceDailySyncDateList , checkCloudPassword } from '@/api/api'
import downloadCenterMixin from '@/mixins/downloadCenterMixin'
import paperListPaginationMixin from '@/mixins/paperListPaginationMixin'
import cloudListStateMixin from '@/mixins/cloudListStateMixin'
import directFilePreviewMixin from '@/mixins/directFilePreviewMixin'
import previewPerformanceMixin from '@/mixins/previewPerformanceMixin'
import { getAppUiText, normalizeLanguage } from '@/utils/language/common'
import { selectItemWithWindowsRange } from '@/utils/rangeSelection'

const MOCK_PAGE_IMAGE = require('@/assets/pc/learning/mock-page.png')
const MOCK_SMALL_PAGE_IMAGE = require('@/assets/pc/learning/mock-small-page.png')
const MOCK_TEMPLATE_IMAGE = require('@/assets/pc/learning/template-placeholder.png')
const ICON_CREATE_FOLDER = require('@/assets/pc/learning/create-folder.png')
const ICON_SEARCH = require('@/assets/pc/daily/search.png')
const ICON_CALENDAR_DAY = require('@/assets/pc/daily/calendar-day.png')
const ICON_DAILY_MARKER_TODO = require('@/assets/pc/daily/11.svg')
const ICON_DAILY_MARKER_TASK_NOTE = require('@/assets/pc/daily/22.svg')
const ICON_DAILY_MARKER_TODAY = require('@/assets/pc/daily/33.svg')
const ICON_DAILY_MARKER_TASK_NOTE_TODAY = require('@/assets/pc/daily/task-note-today.svg')
const ICON_PERIOD_FILTER = require('@/assets/pc/daily/period-filter.png')
const ICON_PERIOD_WEEK = require('@/assets/pc/daily/period-week.png')
const ICON_PERIOD_DAY = require('@/assets/pc/daily/period-day.png')
const ICON_TODO_IMPORTANT_URGENT = require('@/assets/pc/daily/todo-important-urgent.png')
const ICON_TODO_IMPORTANT = require('@/assets/pc/daily/todo-important.png')
const ICON_TODO_URGENT = require('@/assets/pc/daily/todo-urgent.png')
const ICON_TODO_EMPTY = require('@/assets/pc/daily/todo-empty.png')
const ICON_IMPORT = require('@/assets/pc/learning/import-pdf.png')
const ICON_VIEW_GRID = require('@/assets/pc/daily/view-grid.png')
const ICON_VIEW_LIST = require('@/assets/pc/daily/view-list.png')
const ICON_SORT_DESC = require('@/assets/pc/learning/sort-desc.png')
const ICON_SORT_ASC = require('@/assets/pc/learning/sort-asc.png')
const ICON_BREADCRUMB_SEPARATOR = require('@/assets/pc/learning/more.png')
const ICON_FILTER_ALL = require('@/assets/pc/learning/filter-all.png')
const ICON_FILTER_FAVORITE = require('@/assets/pc/learning/favorite.png')
const ICON_FILTER_DELETED = require('@/assets/pc/learning/delete.png')
const ICON_CHECK = require('@/assets/pc/learning/check.png')
const ICON_DOWNLOAD = require('@/assets/pc/daily/download.png')
const ICON_RENAME = require('@/assets/pc/learning/rename.png')
const ICON_MOVE = require('@/assets/pc/learning/move.png')
const ICON_ENCRYPT_MENU = require('@/assets/pc/learning/encrypt-file.png')
const ICON_DELETE = require('@/assets/pc/learning/delete.png')
const ICON_FAVORITE = require('@/assets/pc/learning/favorite.png')
const ICON_FAVORITE_FILLED = require('@/assets/pc/learning/favorite-filled.png')
const ICON_FOLDER_LIST_FILLED = require('@/assets/pc/learning/list-folder-filled.png')
const ICON_FOLDER_LIST_EMPTY = require('@/assets/pc/learning/list-folder-empty.png')
const ICON_FOLDER_GRID_FILLED = require('@/assets/pc/learning/grid-folder-filled.png')
const ICON_FOLDER_GRID_EMPTY = require('@/assets/pc/learning/grid-folder-empty.png')
const ICON_TEMPLATE_LIST_FILLED = require('@/assets/pc/learning/list-template-filled.png')
const ICON_TEMPLATE_LIST_EMPTY = require('@/assets/pc/learning/list-template-empty.png')
const ICON_TEMPLATE_GRID_FILLED = require('@/assets/pc/learning/grid-template-filled.png')
const ICON_TEMPLATE_GRID_EMPTY = require('@/assets/pc/learning/grid-template-empty.png')
const ICON_FILE_LIST_PDF = require('@/assets/pc/learning/list-pdf.png')
const ICON_FILE_LIST_PAPER = require('@/assets/pc/learning/list-paper.png')
const ICON_FILE_LIST_ENCRYPT = require('@/assets/pc/learning/list-encrypt.png')
const ICON_FILE_GRID_PDF = require('@/assets/pc/learning/grid-pdf.png')
const ICON_FILE_GRID_PAPER = require('@/assets/pc/learning/grid-paper.png')
const ICON_FILE_GRID_ENCRYPT = require('@/assets/pc/learning/grid-encrypt.png')
const ICON_PAGE_GRID = require('@/assets/pc/daily/page-grid.png')
const ICON_ZOOM_IN = require('@/assets/pc/daily/zoom-in.png')
const ICON_ZOOM_OUT = require('@/assets/pc/daily/zoom-out.png')
const ICON_CLOSE = require('@/assets/pc/learning/close-plain.png')
const RECORD_ICON = require('@/assets/pc/learning/record-file.png')
const TRANSCRIPT_ICON = require('@/assets/pc/learning/record-transcript.png')
const RECORD_EMPTY_ICON = require('@/assets/pc/daily/record-empty.png')
const ICON_DAILY_EMPTY_COVER = require('@/assets/pc/daily/daily-empty-cover.png')
const LEARNING_APP_TYPE = 3
const DAILY_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAILY_MONTH_SHORT_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export default {
  name: 'DailySync',
  downloadPdfPageSize: 'a4',
  restoreDailyFiltersWithoutPageState: true,
  paperPreviewPagePreload: true,
  mixins: [downloadCenterMixin, cloudListStateMixin, paperListPaginationMixin, directFilePreviewMixin, previewPerformanceMixin],
  props: {
    language: {
      type: String,
      default: 'us'
    },
    appType: {
      type: [Number, String],
      default: 5
    },
    folderType: {
      type: String,
      default: 'daily'
    },
    initialActiveTab: {
      type: String,
      default: 'todo'
    },
    detailBackRoot: {
      type: Boolean,
      default: false
    },
    skipInitialDashboardLoad: {
      type: Boolean,
      default: false
    },
    initialRefreshState: {
      type: Object,
      default: null
    }
  },
  data() {
    const initialActiveTab = ['todo', 'tasks', 'notes'].includes(this.initialActiveTab)
      ? this.initialActiveTab
      : 'todo'
    const today = this.formatDailyDateValue(new Date())
    return {
      paperListLoading: false,
      paperDetailLoading: false,
      paperDetailProgress: 0,
      paperDetailError: '',
      paperItems: [],
      paperFilter: 'all',
      filterMenuOpen: false,
      sortMenuOpen: false,
      sortField: 'createTime',
      sortDesc: true,
      viewMode: 'list',
      searchOpen: false,
      searchKeyword: '',
      paperPasswordModalOpen: false,
      paperPasswordTarget: null,
      paperPasswordInput: '',
      paperPasswordVisible: false,
      paperPasswordError: '',
      paperPasswordSubmitting: false,
      paperPasswordRequestSeq: 0,
      paperPasswordAction: 'open',
      paperPasswordItems: [],
paperDownloadFormatModalOpen: false,
      paperDownloadFormat: 'pdf',
      paperDownloadItems: [],
      paperDownloadPassword: '',
paperDownloadDetailPages: [],
      paperDownloadFormatContext: 'items',
      learningAnnotationDownloadItems: [],
      dailyDownloadItems: [],
      dailyDownloadClearSelectionOnStart: false,
      dailyDownloadSkipSelectionLimit: false,
      dailyDownloadBaseName: '',
      Visible: true,
      selectedPaperId: '',
      selectedPaperIds: [],
      selectedPaperAnchorId: '',
      paperItemClickTimer: null,
      moreMenuId: '',
      detailMode: false,
      currentPaper: null,
      currentPaperPassword: '',
      paperDetail: null,
      paperDetailRequestSeq: 0,
      paperPreviewObjectUrls: [],
      paperDetailCache: {},
      currentPageNo: 1,
      pageGridOpen: false,
      pageGridReturnPageNo: 1,
      paperPagesGridColumnCount: 1,
      selectedDetailPageNos: [],
      selectedDetailPageAnchorNo: '',
      recordPanelOpen: false,
      recordTextOpen: false,
      recordTextLoading: false,
      recordTextError: '',
      recordTextRequestSeq: 0,
      activeRecording: null,
      zoomLevel: 0,
      zoomValue: 1,
      paperPreviewDragState: null,
      paperListPendingLoad: false,
      paperListRequestSeq: 0,
      paperListResourceId: '',
      paperFolderPath: [],
      paperGridColumnCount: 1,
      learningAnnotationGridColumnCount: 1,
      selectionBarOffsetY: 0,
      selectionBarScrollbarOffset: 0,
      selectionBarDragging: false,
      selectionBarDragState: null,
      activeLearningTab: 'summary',
      activeLearningHighlightId: '',
      learningHighlightDetailOpen: false,
      activeLearningAnnotationId: '',
      learningAnnotationViewMode: 'grid',
      learningAnnotationPreviewOpen: false,
      learningAnnotationPreviewGridOpen: false,
      learningAnnotationPreviewGridColumnCount: 1,
      learningAnnotationPreviewSelectedIds: [],
      learningAnnotationPreviewSelectionAnchorId: '',
      learningAnnotationPreviewZoomLevel: 0,
      learningAnnotationPreviewZoomValue: 1,
      learningAnnotationPreviewDragState: null,
      dailyCalendarLoading: false,
      dailyListLoading: false,
      dailyPreviewLoading: false,
      dailyCalendarCursorDate: new Date(),
      dailyCalendarMarkedDates: [],
      dailyCalendarMarkerCache: {},
      dailySelectedDate: today,
      dailyActiveTab: initialActiveTab,
      dailyPeriod: 'day',
      dailyTodoFinishFilter: -1,
      dailyPeriodMenuOpen: false,
      dailyFinishMenuOpen: false,
      dailyPeriodMenuDropLeft: false,
      dailyFinishMenuDropLeft: false,
      dailyViewMode: 'list',
      dailyRawItems: [],
      dailySelectedGroupIds: [],
      dailySelectionAnchorId: '',
      dailyItemClickTimer: null,
      dailyPendingSelectionSnapshot: null,
      dailyPendingSelectionSnapshotTimer: null,
      dailySelectionBarOffsetY: 0,
      dailySelectionBarDragging: false,
      dailySelectionBarDragState: null,
      dailyGridColumnCount: 1,
      dailyCalendarRequestSeq: 0,
      dailyListRequestSeq: 0,
      dailyPreviewRequestSeq: 0,
      dailyPreviewOpen: false,
      dailyPreviewGridOpen: false,
      dailyPreviewGridColumnCount: 1,
      dailyPreviewItems: [],
      dailyPreviewCurrentId: '',
      dailyPreviewGridReturnId: '',
      dailyPreviewTitle: '',
      dailyPreviewSelectedIds: [],
      dailyPreviewSelectionAnchorId: '',
      dailyPreviewZoomLevel: 0,
      dailyPreviewZoomValue: 1,
      dailyPreviewDragState: null,
      paperIcons: {
        createFolder: ICON_CREATE_FOLDER,
        search: ICON_SEARCH,
        calendarDay: ICON_CALENDAR_DAY,
        periodFilter: ICON_PERIOD_FILTER,
        periodWeek: ICON_PERIOD_WEEK,
        periodDay: ICON_PERIOD_DAY,
        todoImportantUrgent: ICON_TODO_IMPORTANT_URGENT,
        todoUrgent: ICON_TODO_URGENT,
        todoImportant: ICON_TODO_IMPORTANT,
        todoEmpty: ICON_TODO_EMPTY,
        import: ICON_IMPORT,
        viewList: ICON_VIEW_LIST,
        viewGrid: ICON_VIEW_GRID,
        sortDesc: ICON_SORT_DESC,
        sortAsc: ICON_SORT_ASC,
        breadcrumbSeparator: ICON_BREADCRUMB_SEPARATOR,
        filterAll: ICON_FILTER_ALL,
        filterFavorite: ICON_FILTER_FAVORITE,
        filterDeleted: ICON_FILTER_DELETED,
        check: ICON_CHECK,
        download: ICON_DOWNLOAD,
        rename: ICON_RENAME,
        move: ICON_MOVE,
        encryptMenu: ICON_ENCRYPT_MENU,
        delete: ICON_DELETE,
        favorite: ICON_FAVORITE,
        favoriteFilled: ICON_FAVORITE_FILLED,
        folderListFilled: ICON_FOLDER_LIST_FILLED,
        folderListEmpty: ICON_FOLDER_LIST_EMPTY,
        folderGridFilled: ICON_FOLDER_GRID_FILLED,
        folderGridEmpty: ICON_FOLDER_GRID_EMPTY,
        templateListFilled: ICON_TEMPLATE_LIST_FILLED,
        templateListEmpty: ICON_TEMPLATE_LIST_EMPTY,
        templateGridFilled: ICON_TEMPLATE_GRID_FILLED,
        templateGridEmpty: ICON_TEMPLATE_GRID_EMPTY,
        fileListPdf: ICON_FILE_LIST_PDF,
        fileListPaper: ICON_FILE_LIST_PAPER,
        fileListEncrypt: ICON_FILE_LIST_ENCRYPT,
        fileGridPdf: ICON_FILE_GRID_PDF,
        fileGridPaper: ICON_FILE_GRID_PAPER,
        fileGridEncrypt: ICON_FILE_GRID_ENCRYPT,
        pageGrid: ICON_PAGE_GRID,
        zoomIn: ICON_ZOOM_IN,
        zoomOut: ICON_ZOOM_OUT,
        close: ICON_CLOSE,
        record: RECORD_ICON,
        transcript: TRANSCRIPT_ICON,
        recordEmpty: RECORD_EMPTY_ICON,
        dailyEmptyCover: ICON_DAILY_EMPTY_COVER
      },
      ui: {
        all: '\u5168\u90e8',
        favorite: '\u6536\u85cf',
        recentDeleted: '\u6700\u8fd1\u5220\u9664',
        search: '\u641c\u7d22',
        createFolder: '\u65b0\u5efa\u6587\u4ef6\u5939',
        importPdf: '\u5bfc\u5165 PDF',
        modifyTime: '\u4fee\u6539\u65f6\u95f4',
        createTime: '\u521b\u5efa\u65f6\u95f4',
        name: '\u540d\u79f0',
        desc: '\u5012\u5e8f',
        asc: '\u6b63\u5e8f',
        listView: '\u5217\u8868',
        gridView: '\u7f51\u683c',
        empty: '\u6682\u65e0\u6570\u636e',
        item: '\u9879',
        page: '\u9875',
        selectedPrefix: '\u5df2\u9009\u62e9',
        selectedSuffix: '\u9879',
        cancel: '\u53d6\u6d88',
        more: '\u66f4\u591a',
        download: '\u4e0b\u8f7d',
        rename: '\u91cd\u547d\u540d',
        move: '\u79fb\u52a8',
        encrypt: '\u52a0\u5bc6',
        decrypt: '\u53d6\u6d88\u52a0\u5bc6',
        encrypted: '\u5df2\u52a0\u5bc6',
        unfavorite: '\u53d6\u6d88\u6536\u85cf',
        delete: '\u5220\u9664',
        back: '\u8fd4\u56de',
        recordings: '\u5f55\u97f3',
        allRecordings: '\u5168\u90e8\u5f55\u97f3',
        transcript: '\u5f55\u97f3\u8f6c\u6587\u672c',
        pageGrid: '\u9875\u9762\u5217\u8868',
        zoomIn: '\u653e\u5927',
        zoomOut: '\u7f29\u5c0f',
        close: '\u5173\u95ed',
        chooseFormat: '\u9009\u62e9\u4e0b\u8f7d\u683c\u5f0f',
        ok: '\u786e\u5b9a',
        bookmarks: '\u4e66\u7b7e',
        highlights: '\u9ad8\u4eae',
        annotations: '\u624b\u5199\u6279\u6ce8',
        fakeTip: '\u63a5\u53e3\u6682\u672a\u5f00\u653e\uff0c\u5df2\u7528\u5047\u63a5\u53e3\u5360\u4f4d'
      }
    }
  },
  computed: {
    isDailyMode() {
      return true
    },
    isLearningMode() {
      return false
    },
    appLabel() {
      return this.isDailyMode ? 'Daily' : 'Learning'
    },
    appEmptyText() {
      return this.ui.empty
    },
    normalizedLanguage() {
      return normalizeLanguage(this.language)
    },
    isCjkDailyLanguage() {
      return ['zh', 'tw'].includes(this.normalizedLanguage)
    },
    dailyTabs() {
      return [
        { key: 'todo', label: 'To-Do', type: 2 },
        { key: 'tasks', label: 'Tasks', type: 1 },
        { key: 'notes', label: 'Notes', type: 0 }
      ]
    },
    dailyPeriodOptions() {
      return [
        { key: 'month', label: this.ui.month, icon: this.paperIcons.periodFilter },
        { key: 'week', label: this.ui.week, icon: this.paperIcons.periodWeek },
        { key: 'day', label: this.ui.day, icon: this.paperIcons.periodDay }
      ]
    },
    dailyFinishOptions() {
      return [
        { key: -1, label: this.ui.allStatus },
        { key: 0, label: this.ui.unfinished },
        { key: 1, label: this.ui.finished }
      ]
    },
    isDailyTodoTab() {
      return this.dailyActiveTab == 'todo'
    },
    dailyActiveType() {
      const tab = this.dailyTabs.find(item => item.key == this.dailyActiveTab)
      return tab ? tab.type : 2
    },
    dailyBaseDate() {
      return this.parseDailyDate(this.dailySelectedDate) || this.startOfDayDate(new Date())
    },
    dailyListDateRange() {
      return this.getDailyRangeByPeriod(this.dailyBaseDate, this.dailyPeriod)
    },
    dailyTodayHeaderText() {
      return this.dailyTodayHeaderParts.map(part => (part.type == 'line' ? '-' : part.value)).join('')
    },
    dailyTodayHeaderParts() {
      const date = this.dailyBaseDate
      if (this.dailyPeriod == 'month') {
        return this.buildDailyHeaderMonthParts(date)
      }
      if (this.dailyPeriod == 'week') {
        const range = this.dailyListDateRange
        return this.buildDailyHeaderDateRangeParts(range.start, range.end)
      }
      return this.buildDailyHeaderDateParts(date)
    },
    dailyCalendarIconDay() {
      return this.startOfDayDate(new Date()).getDate()
    },
    dailyCalendarYearText() {
      const date = this.toValidDate(this.dailyCalendarCursorDate) || new Date()
      return this.isCjkDailyLanguage ? `${date.getFullYear()}\u5e74` : `${date.getFullYear()}`
    },
    dailyCalendarMonthText() {
      const date = this.toValidDate(this.dailyCalendarCursorDate) || new Date()
      return this.isCjkDailyLanguage
        ? `${date.getMonth() + 1}\u6708`
        : this.getDailyEnglishMonthName(date.getMonth(), true)
    },
    dailyActivePeriodLabel() {
      const option = this.dailyPeriodOptions.find(item => item.key == this.dailyPeriod)
      return option ? option.label : this.ui.month
    },
    dailyActivePeriodIcon() {
      const option = this.dailyPeriodOptions.find(item => item.key == this.dailyPeriod)
      return option && option.icon ? option.icon : this.paperIcons.periodFilter
    },
    dailyActiveFinishLabel() {
      const option = this.dailyFinishOptions.find(item => item.key == this.dailyTodoFinishFilter)
      return option ? option.label : this.ui.allStatus
    },
    dailyWeekdayLabels() {
      const labels = this.ui.weekdays || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      if (this.isCjkDailyLanguage || labels.length < 7) {
        return labels
      }
      return labels.slice(1).concat(labels[0])
    },
    dailyCalendarMarkerMap() {
      const map = {}
      this.dailyCalendarMarkedDates.forEach(item => {
        if (!item || !item.date) {
          return
        }
        map[item.date] = {
          date: item.date,
          hasTodo: !!item.hasTodo,
          hasTaskNote: !!item.hasTaskNote
        }
      })
      return map
    },
    dailyCalendarCells() {
      return this.buildDailyCalendarCells(this.dailyCalendarCursorDate)
    },
    showDailyCalendarSpinner() {
      return this.dailyCalendarLoading && !this.dailyListLoading
    },
    dailyNormalizedItems() {
      return this.dailyRawItems.map((item, index) => this.normalizeDailyItem(item, index))
    },
    dailyTypedItems() {
      const activeType = Number(this.dailyActiveType)
      return this.dailyNormalizedItems.filter(item => {
        const itemType = Number(item && item.type)
        return !Number.isFinite(itemType) || itemType == activeType
      })
    },
    dailyTodoItems() {
      return this.dailyTypedItems
    },
    dailyGroupedItems() {
      return this.groupDailyItemsByName(this.dailyTypedItems)
    },
    dailyGridAllCoversEmpty() {
      return this.dailyGroupedItems.length > 0 && this.dailyGroupedItems.every(item => !item || !item.cover)
    },
    dailyDisplayItems() {
      return this.isDailyTodoTab ? this.dailyTodoItems : this.dailyGroupedItems
    },
    dailySelectedGroups() {
      const selectedIds = new Set(this.dailySelectedGroupIds)
      return this.dailyGroupedItems.filter(item => selectedIds.has(item.id))
    },
    dailySelectedCount() {
      return this.dailySelectedGroups.length
    },
    hasDailySelection() {
      return !this.isDailyTodoTab && this.dailySelectedCount > 0
    },
    dailySelectionLayerStyle() {
      return {
        transform: `translateY(${this.dailySelectionBarOffsetY}px)`
      }
    },
    dailyViewSwitchIcon() {
      return this.dailyViewMode == 'list'
        ? this.paperIcons.viewList
        : this.paperIcons.viewGrid
    },
    dailyPreviewCurrentItem() {
      return this.dailyPreviewItems.find(item => item.id == this.dailyPreviewCurrentId) || this.dailyPreviewItems[0] || null
    },
    dailyPreviewIndex() {
      const index = this.dailyPreviewItems.findIndex(item => item.id == this.dailyPreviewCurrentId)
      return index >= 0 ? index : 0
    },
    dailyPreviewSelectedItems() {
      const selectedIds = new Set(this.dailyPreviewSelectedIds)
      return this.dailyPreviewItems.filter(item => selectedIds.has(item.id))
    },
    dailyPreviewSelectedCount() {
      return this.dailyPreviewSelectedItems.length
    },
    canDownloadCurrentDailyPreview() {
      if (this.dailyPreviewGridOpen) {
        return this.dailyPreviewSelectedCount > 0
      }
      return !!this.dailyPreviewCurrentItem || this.dailyPreviewItems.length > 0
    },
    interactionLocked() {
      if (this.isDailyMode) {
        return this.dailyCalendarLoading || this.dailyListLoading || this.dailyPreviewLoading
      }
      return this.paperListLoading || this.paperDetailLoading || this.recordTextLoading || this.paperPasswordSubmitting
    },
    paperListAppType() {
      const value = Number(this.appType)
      return Number.isFinite(value) && value > 0 ? value : LEARNING_APP_TYPE
    },
    filterOptions() {
      return [
        { key: 'all', label: this.ui.all, icon: this.paperIcons.filterAll },
        { key: 'favorite', label: this.ui.favorite, icon: this.paperIcons.filterFavorite },
        { key: 'deleted', label: this.ui.recentDeleted, icon: this.paperIcons.filterDeleted }
      ]
    },
    sortOptions() {
      return [
        { key: 'createTime', label: this.ui.createTime },
        { key: 'modifyTime', label: this.ui.modifyTime },
        { key: 'name', label: this.ui.name }
      ]
    },
    activeFilterLabel() {
      const item = this.filterOptions.find(option => option.key == this.paperFilter)
      return item ? item.label : this.ui.all
    },
    activeSortLabel() {
      const item = this.sortOptions.find(option => option.key == this.sortField)
      return item ? item.label : this.ui.createTime
    },
    viewSwitchIcon() {
      return this.viewMode == 'list'
        ? this.paperIcons.viewList
        : this.paperIcons.viewGrid
    },
    learningAnnotationViewSwitchIcon() {
      return this.learningAnnotationViewMode == 'list'
        ? this.paperIcons.viewList
        : this.paperIcons.viewGrid
    },
    selectionBarLayerStyle() {
      return {
        transform: `translateY(${this.selectionBarOffsetY}px)`,
        right: `${this.selectionBarScrollbarOffset}px`
      }
    },
    paperBreadcrumbItems() {
      return [{ name: this.appLabel, resourceId: '', pathKey: 'root' }].concat(this.normalizeCloudPaperFolderPath(this.paperFolderPath))
    },
    learningTabs() {
      return [
        { key: 'bookmarks', label: this.ui.bookmarks, count: this.learningBookmarks.length },
        { key: 'highlights', label: this.ui.highlights, count: this.learningHighlights.length },
        { key: 'annotations', label: this.ui.annotations, count: this.learningAnnotations.length }
      ]
    },
    learningSummaryRows() {
      return this.learningTabs
    },
    learningSummaryCover() {
      return this.pickString(this.paperDetail || {}, ['cover', 'coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
        || this.pickString(this.currentPaper || {}, ['cover', 'coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
    },
    learningBookmarks() {
      return this.paperDetail && Array.isArray(this.paperDetail.bookmarks) ? this.paperDetail.bookmarks : []
    },
    learningHighlights() {
      return this.paperDetail && Array.isArray(this.paperDetail.highlights) ? this.paperDetail.highlights : []
    },
    learningAnnotations() {
      return this.paperDetail && Array.isArray(this.paperDetail.annotations) ? this.paperDetail.annotations : []
    },
    learningProgressPercent() {
      const value = this.paperDetail && this.paperDetail.readingProgress ? this.paperDetail.readingProgress.progressPercent : 0
      const percent = Number(value)
      return Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0
    },
    learningProgressText() {
      return `${this.formatLearningPercent(this.learningProgressPercent)}%`
    },
    learningProgressPageText() {
      const progress = this.paperDetail && this.paperDetail.readingProgress ? this.paperDetail.readingProgress : {}
      const currentPage = progress.currentPage || 0
      const totalPages = progress.totalPages || 0
      if (currentPage || totalPages) {
        return `${currentPage || 0}/${totalPages || 0} \u9875`
      }
      return this.ui.noReadingProgress
    },
    learningFileFormatText() {
      return this.formatLearningFileFormat(this.paperDetail && this.paperDetail.fileFormat)
    },
    learningFileSizeText() {
      const value = this.paperDetail ? this.paperDetail.fileSizeBytes : 0
      return this.formatByteSize(value)
    },
    learningSyncStatusCode() {
      return this.normalizeLearningSyncStatus(this.paperDetail && this.paperDetail.syncStatus)
    },
    learningSyncStatusClass() {
      if (this.learningSyncStatusCode === 1) {
        return 'is-synced'
      }
      if (this.learningSyncStatusCode === 0) {
        return 'is-unsynced'
      }
      return 'is-unknown'
    },
    learningSyncStatusText() {
      const value = this.learningSyncStatusCode
      if (value === 1) {
        return '\u5df2\u540c\u6b65\u6e90\u6587\u4ef6'
      }
      if (value === 0) {
        return '\u672a\u540c\u6b65\u6e90\u6587\u4ef6'
      }
      return '\u540c\u6b65\u72b6\u6001 -'
    },
    activeLearningHighlight() {
      return this.learningHighlights.find(item => item.id == this.activeLearningHighlightId) || this.learningHighlights[0] || null
    },
    activeLearningAnnotation() {
      return this.learningAnnotations.find(item => item.id == this.activeLearningAnnotationId) || null
    },
    learningAnnotationPreviewHeaderTitle() {
      if (this.learningAnnotationPreviewGridOpen) {
        return this.paperDetailTitle || this.ui.annotations
      }
      if (this.activeLearningAnnotation && this.activeLearningAnnotation.title) {
        return this.activeLearningAnnotation.title
      }
      return this.paperDetailTitle || this.ui.annotations
    },
    learningAnnotationPreviewIndex() {
      const index = this.learningAnnotations.findIndex(item => item.id == this.activeLearningAnnotationId)
      return index >= 0 ? index : 0
    },
    learningAnnotationPreviewSelectedAnnotations() {
      const selectedIds = new Set(this.learningAnnotationPreviewSelectedIds)
      return this.learningAnnotations.filter(item => selectedIds.has(item.id))
    },
    learningAnnotationPreviewSelectedCount() {
      return this.learningAnnotationPreviewSelectedAnnotations.length
    },
    canDownloadLearningAnnotationPreviewSelection() {
      return this.learningAnnotationPreviewSelectedCount > 0
    },
    canDownloadCurrentLearningAnnotationPreview() {
      if (this.learningAnnotationPreviewGridOpen) {
        return this.canDownloadLearningAnnotationPreviewSelection
      }
      return !!(this.activeLearningAnnotation && (this.activeLearningAnnotation.imageUrl || this.activeLearningAnnotation.pageImageUrl))
    },
    paperDownloadTaskSummary() {
      const total = this.paperDownloadTasks.length
      const running = this.paperDownloadTasks.filter(task => task.state == 'running').length
      const failed = this.paperDownloadTasks.filter(task => task.state == 'error').length
      if (running > 0) {
        return `${running}/${total} ${this.ui.downloading}`
      }
      if (failed > 0) {
        return `${failed}/${total} ${this.ui.downloadFailed}`
      }
      return `${total}/${total} ${this.ui.downloadDone}`
    },
    visiblePaperItems() {
      let items = this.paperItems.slice()
      if (this.paperFilter == 'favorite') {
        items = items.filter(item => item.favorite)
      } else if (this.paperFilter == 'deleted') {
        items = items.filter(item => item.deleted)
      } else {
        items = items.filter(item => !item.deleted)
      }
      const keyword = String(this.searchKeyword || '').toLowerCase()
      if (keyword) {
        items = items.filter(item => String(item.name || '').toLowerCase().includes(keyword))
      }
      return items
    },
    selectedPaperItems() {
      const selectedIds = new Set(this.selectedPaperIds)
      return this.paperItems.filter(item => selectedIds.has(item.id))
    },
    selectedPaper() {
      return this.selectedPaperItems[0] || null
    },
    selectedCount() {
      return this.selectedPaperItems.length
    },
    hasPaperSelection() {
      return this.selectedCount > 0
    },
    canBatchSelectionAction() {
      return this.selectedCount > 0 && this.selectedPaperItems.every(item => this.isPaperDownloadableItem(item))
    },
    canRenameSelected() {
      return this.selectedCount == 1
    },
    canEncryptSelected() {
      return this.selectedCount == 1 && !!this.selectedPaper
    },
    canUnfavoriteSelected() {
      return this.selectedCount == 1 && this.selectedPaper && this.selectedPaper.favorite
    },
    selectionEncryptLabel() {
      return this.selectedPaper && this.selectedPaper.encrypted ? this.ui.decrypt : this.ui.encrypt
    },
    paperDetailTitle() {
      if (this.paperDetail && this.paperDetail.name) {
        return this.paperDetail.name
      }
      return this.currentPaper && this.currentPaper.name ? this.currentPaper.name : this.appLabel
    },
    detailPages() {
      return this.paperDetail && Array.isArray(this.paperDetail.imagePages) ? this.paperDetail.imagePages : []
    },
    selectedDetailPages() {
      const selectedNos = new Set(this.selectedDetailPageNos)
      return this.detailPages.filter(page => selectedNos.has(page.pageNo))
    },
    detailRecordings() {
      return this.paperDetail && Array.isArray(this.paperDetail.recordings) ? this.paperDetail.recordings : []
    },
    currentPageIndex() {
      const index = this.detailPages.findIndex(page => page.pageNo == this.currentPageNo)
      return index >= 0 ? index : 0
    },
    currentPage() {
      return this.detailPages[this.currentPageIndex] || null
    },
    activeRecordingName() {
      return this.activeRecording && this.activeRecording.recordingName ? this.activeRecording.recordingName : ''
    },
    activeRecordingText() {
      if (!this.activeRecording) {
        return ''
      }
      if (this.recordTextLoading) {
        return this.ui.transcriptLoading
      }
      if (this.recordTextError) {
        return this.recordTextError
      }
      if (this.activeRecording.transcriptText) {
        return this.activeRecording.transcriptText
      }
      if (this.activeRecording.transcriptTextUrl) {
        return this.ui.transcriptPending
      }
      return this.ui.noTranscript
    }
  },
  watch: {
    language: {
      immediate: true,
      handler(value) {
        this.ui = getAppUiText(value)
      }
    },
    viewMode() {
      this.updatePaperGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
    },
    visiblePaperItems() {
      this.updatePaperGridColumnCount()
      this.syncSelectionBarScrollbarOffset()
    },
    pageGridOpen() {
      this.updatePaperPagesGridColumnCount()
    },
    detailPages() {
      this.updatePaperPagesGridColumnCount()
    },
    activeLearningTab() {
      this.updateLearningAnnotationGridColumnCount()
    },
    learningAnnotationViewMode() {
      this.updateLearningAnnotationGridColumnCount()
    },
    learningAnnotations() {
      this.updateLearningAnnotationGridColumnCount()
      this.updateLearningAnnotationPreviewGridColumnCount()
      this.syncLearningAnnotationPreviewState()
    },
    learningAnnotationPreviewGridOpen() {
      this.updateLearningAnnotationPreviewGridColumnCount()
    },
    dailyViewMode() {
      this.updateDailyGridColumnCount()
    },
    dailyDisplayItems() {
      this.updateDailyGridColumnCount()
    },
    dailyPreviewGridOpen() {
      this.updateDailyPreviewGridColumnCount()
    },
    dailyPreviewItems() {
      this.updateDailyPreviewGridColumnCount()
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handlePaperWindowResize)
    }
    this.applyInitialRefreshState()
    if (this.isDailyMode) {
      if (!this.skipInitialDashboardLoad) {
        this.loadDailyDashboard()
      }
    } else {
      this.loadPaperList()
    }
    this.updatePaperGridColumnCount()
    this.syncSelectionBarScrollbarOffset()
    this.updateLearningAnnotationGridColumnCount()
    this.updateDailyGridColumnCount()
  },
  beforeDestroy() {
    this.paperListRequestSeq += 1
    this.paperDetailRequestSeq += 1
    this.paperPasswordRequestSeq += 1
    this.dailyCalendarRequestSeq += 1
    this.dailyListRequestSeq += 1
    this.dailyPreviewRequestSeq += 1
    this.paperListPendingLoad = false
    if (!this.$hasRunningDownloadTasks()) {
      this.clearPaperDetailCache()
      this.clearPaperPreviewObjectUrls()
    }
    this.clearPaperItemClickTimer()
    this.clearDailyItemClickTimer()
    this.stopSelectionBarDrag()
    this.stopDailySelectionBarDrag()
    this.stopPaperPreviewDrag()
    this.stopLearningAnnotationPreviewDrag()
    this.stopDailyPreviewDrag()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handlePaperWindowResize)
    }
  },
  methods: {
    applyInitialRefreshState() {
      const state = this.initialRefreshState && typeof this.initialRefreshState == 'object' ? this.initialRefreshState : null
      if (!state) {
        return
      }
      if (['todo', 'tasks', 'notes'].includes(state.dailyActiveTab)) {
        this.dailyActiveTab = state.dailyActiveTab
      }
      const selectedDate = this.normalizeDailyDateValue(state.dailySelectedDate)
      if (selectedDate) {
        this.dailySelectedDate = selectedDate
      }
      if (['month', 'week', 'day'].includes(state.dailyPeriod)) {
        this.dailyPeriod = state.dailyPeriod
      }
      const finishFilter = Number(state.dailyTodoFinishFilter)
      if (Number.isFinite(finishFilter) && [-1, 0, 1].includes(finishFilter)) {
        this.dailyTodoFinishFilter = finishFilter
      }
      if (state.dailyViewMode == 'grid' && this.dailyActiveTab != 'todo') {
        this.dailyViewMode = 'grid'
      } else {
        this.dailyViewMode = 'list'
      }
    },
    getRefreshState() {
      return {
        dailyActiveTab: this.dailyActiveTab,
        dailySelectedDate: this.dailySelectedDate || '',
        dailyPeriod: this.dailyPeriod,
        dailyTodoFinishFilter: this.dailyTodoFinishFilter,
        dailyViewMode: this.dailyViewMode
      }
    },
    loadDailyDashboard() {
      this.loadDailyCalendarMarkers()
      this.loadDailyList()
    },
    loadDailyCalendarMarkers() {
      const requestId = this.dailyCalendarRequestSeq + 1
      this.dailyCalendarRequestSeq = requestId
      const month = this.getDailyCalendarMonthValue(this.dailyCalendarCursorDate)
      if (!month) {
        this.dailyCalendarMarkedDates = []
        this.dailyCalendarLoading = false
        return
      }
      const cachedMarkers = this.dailyCalendarMarkerCache[month]
      if (Array.isArray(cachedMarkers)) {
        this.dailyCalendarMarkedDates = cachedMarkers.map(item => Object.assign({}, item))
        this.dailyCalendarLoading = false
        return
      }
      this.dailyCalendarLoading = true
      getResourceDailySyncDateList(this.buildDailyCalendarDateListParams()).then(ret => {
        if (requestId != this.dailyCalendarRequestSeq) {
          return
        }
        const payload = this.resolveApiPayload(ret)
        if (payload === null) {
          this.dailyCalendarMarkedDates = []
          return
        }
        const markers = this.normalizeDailyCalendarMarkers(payload)
        this.$set(this.dailyCalendarMarkerCache, month, markers)
        this.dailyCalendarMarkedDates = markers.map(item => Object.assign({}, item))
      }).catch(() => {
        if (requestId == this.dailyCalendarRequestSeq) {
          this.dailyCalendarMarkedDates = []
        }
      }).finally(() => {
        if (requestId == this.dailyCalendarRequestSeq) {
          this.dailyCalendarLoading = false
        }
      })
    },
    buildDailyCalendarDateListParams() {
      return {
        month: this.getDailyCalendarMonthValue(this.dailyCalendarCursorDate)
      }
    },
    loadDailyList() {
      const requestId = this.dailyListRequestSeq + 1
      this.dailyListRequestSeq = requestId
      this.dailyListLoading = true
      this.clearDailySelection()
      getResourceDailySyncList(this.buildDailyListParams()).then(ret => {
        if (requestId != this.dailyListRequestSeq) {
          return
        }
        const payload = this.resolveApiPayload(ret)
        this.dailyRawItems = payload === null ? [] : this.extractArray(payload)
      }).catch(error => {
        if (requestId != this.dailyListRequestSeq) {
          return
        }
        this.dailyRawItems = []
        if (this.$message && typeof this.$message.error == 'function') {
          this.$message.error(this.getPaperApiErrorMessage(error) || this.ui.dataLoadFailed.replace('{app}', 'Daily'))
        }
      }).finally(() => {
        if (requestId == this.dailyListRequestSeq) {
          this.dailyListLoading = false
          this.updateDailyGridColumnCount()
        }
      })
    },
    buildDailyListParams() {
      const range = this.dailyListDateRange
      return {
        startDate: range.start,
        endDate: range.end,
        type: this.dailyActiveType,
        isFinish: this.isDailyTodoTab ? this.dailyTodoFinishFilter : -1
      }
    },
    normalizeDailyCalendarMarkers(payload) {
      const sourceList = Array.isArray(payload) ? payload : this.extractArray(payload)
      const map = {}
      ;(Array.isArray(sourceList) ? sourceList : []).forEach(item => {
        const source = item && typeof item == 'object' ? item : {}
        const date = this.normalizeDailyDateValue(this.pickValue(source, ['date', 'day', 'syncDate', 'dailyDate', 'createdAt', 'createTime', 'time']))
        if (!date) {
          return
        }
        const current = map[date] || {
          date,
          hasTodo: false,
          hasTaskNote: false
        }
        current.hasTodo = current.hasTodo || this.pickBoolean(source, ['hasTodo', 'todo', 'has_todo'])
        current.hasTaskNote = current.hasTaskNote || this.pickBoolean(source, ['hasTaskNote', 'taskNote', 'task_note', 'has_task_note'])
        map[date] = current
      })
      return Object.keys(map)
        .sort()
        .map(key => map[key])
    },
    pickDailySourceImageUrl(source) {
      return this.pickFirstNonEmptyString(source, [
        'annotationImageUrl', 'annotation_image_url',
        'inkImageUrl', 'ink_image_url',
        'handwritingImageUrl', 'handwriting_image_url',
        'noteImageUrl', 'note_image_url',
        'sourceImageUrl', 'source_image_url',
        'originImageUrl', 'origin_image_url',
        'originalImageUrl', 'original_image_url',
        'fullImageUrl', 'full_image_url',
        'rawImageUrl', 'raw_image_url',
        'downloadUrl', 'download_url', 'ossUrl', 'oss_url',
        'resourceUrl', 'resource_url', 'fileUrl', 'file_url',
        'imageUrl', 'image_url', 'url', 'src',
        'cover', 'coverUrl', 'cover_url',
        'thumbnail', 'thumbnailUrl', 'thumbnail_url'
      ])
    },
    pickDailyPageImageUrl(source) {
      return this.pickFirstNonEmptyString(source, [
        'pageImageUrl', 'page_image_url',
        'backgroundImageUrl', 'background_image_url',
        'baseImageUrl', 'base_image_url',
        'pageUrl', 'page_url', 'pageSrc', 'page_src',
        'bgImageUrl', 'bg_image_url',
        'backgroundUrl', 'background_url',
        'baseUrl', 'base_url',
        'templateImageUrl', 'template_image_url',
        'templateUrl', 'template_url',
        'paperImageUrl', 'paper_image_url',
        'sourcePageImageUrl', 'source_page_image_url',
        'originPageImageUrl', 'origin_page_image_url',
        'originalPageImageUrl', 'original_page_image_url',
        'fullPageImageUrl', 'full_page_image_url',
        'rawPageImageUrl', 'raw_page_image_url'
      ])
    },
    normalizeDailyItem(raw, index) {
      const item = raw && typeof raw == 'object' ? raw : { content: raw }
      const meta = this.parseMeta(this.pickValue(item, ['meta']))
      const source = Object.assign({}, meta, item)
      const date = this.normalizeDailyDateValue(this.pickValue(source, ['date', 'day', 'syncDate', 'dailyDate', 'createdAt', 'createTime', 'time', 'updatedAt', 'updateTime']))
      const name = this.pickString(source, ['name', 'title', 'fileName', 'resourceName']) || `Daily ${index + 1}`
      const sourceImageUrl = this.pickDailySourceImageUrl(source)
      const pageImageUrl = this.pickDailyPageImageUrl(source)
      const imageUrl = sourceImageUrl || pageImageUrl
      const content = this.pickString(source, ['content', 'text', 'description', 'summary']) || name
      const typeValue = this.pickValue(source, ['type', 'dailyType', 'daily_type', 'tabType', 'tab_type'])
      const classificationValue = this.pickValue(source, ['classification', 'classify'])
      const isFinishValue = this.pickValue(source, ['isFinish', 'finish', 'finished', 'done'])
      const levelValue = this.pickValue(source, ['level', 'priority'])
      const type = Number(typeValue === undefined || typeValue === null || typeValue === '' ? this.dailyActiveType : typeValue)
      return {
        id: this.pickString(source, ['id', 'uuid', 'resourceId', 'dailyId']) || `daily-${this.dailyActiveTab}-${index}`,
        name,
        content,
        imageUrl,
        url: imageUrl,
        sourceImageUrl,
        pageImageUrl,
        date,
        type,
        classification: Number(classificationValue === undefined || classificationValue === null || classificationValue === '' ? 0 : classificationValue),
        isFinish: Number(isFinishValue === undefined || isFinishValue === null || isFinishValue === '' ? 0 : isFinishValue),
        level: Number(levelValue === undefined || levelValue === null || levelValue === '' ? 100 : levelValue),
        favorite: this.pickBoolean(source, ['favorite', 'isFavorite', 'starred', 'isStar', 'star', 'start']),
        raw: source
      }
    },
    groupDailyItemsByName(items) {
      const groups = []
      const groupMap = {}
      ;(Array.isArray(items) ? items : []).forEach(item => {
        const name = item && item.name ? item.name : '\u672a\u547d\u540d'
        const itemDate = item && item.date ? item.date : ''
        const itemType = item && item.type !== undefined && item.type !== null && item.type !== ''
          ? item.type
          : this.dailyActiveType
        const key = `${name}::${itemDate}::${itemType}`
        if (!groupMap[key]) {
          const group = {
            id: `daily-group-${this.dailyActiveTab}-${groups.length}`,
            name,
            cover: item && item.imageUrl ? item.imageUrl : '',
            date: itemDate,
            type: itemType,
            count: 0,
            favorite: false,
            items: [],
            previewRequest: this.buildDailyPreviewRequestFromItem(item)
          }
          groupMap[key] = group
          groups.push(group)
        }
        groupMap[key].items.push(item)
        groupMap[key].count = groupMap[key].items.length
        if (!groupMap[key].cover && item && item.imageUrl) {
          groupMap[key].cover = item.imageUrl
        }
        if (!groupMap[key].date && item && item.date) {
          groupMap[key].date = item.date
        }
        if (!groupMap[key].previewRequest) {
          groupMap[key].previewRequest = this.buildDailyPreviewRequestFromItem(item)
        }
        if (item && item.favorite) {
          groupMap[key].favorite = true
        }
      })
      return groups
    },
    buildDailyPreviewRequestFromItem(item) {
      if (!item || this.isDailyTodoTab) {
        return null
      }
      const startDate = item.date || ''
      if (!startDate) {
        return null
      }
      const typeValue = item.type === undefined || item.type === null || item.type === ''
        ? this.dailyActiveType
        : item.type
      const type = Number(typeValue)
      return {
        startDate,
        endDate: startDate,
        type: Number.isFinite(type) ? type : this.dailyActiveType,
        isFinish: -1
      }
    },
    setDailyTab(tab) {
      if (this.dailyActiveTab == tab) {
        return
      }
      this.dailyActiveTab = tab
      if (tab != 'todo') {
        this.dailyViewMode = this.viewMode == 'grid' ? 'grid' : 'list'
      }
      this.dailyPeriodMenuOpen = false
      this.dailyFinishMenuOpen = false
      this.dailyPeriodMenuDropLeft = false
      this.dailyFinishMenuDropLeft = false
      this.clearDailySelection()
      this.loadDailyList()
    },
    setDailyPeriod(period) {
      if (!['month', 'week', 'day'].includes(period) || this.dailyPeriod == period) {
        this.dailyPeriodMenuOpen = false
        this.dailyPeriodMenuDropLeft = false
        return
      }
      this.dailyPeriod = period
      this.dailyPeriodMenuOpen = false
      this.dailyPeriodMenuDropLeft = false
      this.loadDailyList()
    },
    toggleDailyPeriodMenu() {
      this.dailyPeriodMenuOpen = !this.dailyPeriodMenuOpen
      if (this.dailyPeriodMenuOpen) {
        this.dailyFinishMenuOpen = false
        this.dailyFinishMenuDropLeft = false
        this.resolveDailyFilterMenuPlacement('period')
      } else {
        this.dailyPeriodMenuDropLeft = false
      }
    },
    toggleDailyFinishMenu() {
      this.dailyFinishMenuOpen = !this.dailyFinishMenuOpen
      if (this.dailyFinishMenuOpen) {
        this.dailyPeriodMenuOpen = false
        this.dailyPeriodMenuDropLeft = false
        this.resolveDailyFilterMenuPlacement('finish')
      } else {
        this.dailyFinishMenuDropLeft = false
      }
    },
    resolveDailyFilterMenuPlacement(menuType) {
      this.$nextTick(() => {
        if (menuType == 'period' && !this.dailyPeriodMenuOpen) {
          return
        }
        if (menuType == 'finish' && !this.dailyFinishMenuOpen) {
          return
        }
        const root = this.$refs.dailyMainPanel || this.$el
        const selector = menuType == 'finish' ? '.daily-finish-wrap' : '.daily-filter-wrap:not(.daily-finish-wrap)'
        const wrap = root && root.querySelector ? root.querySelector(selector) : null
        const menu = wrap && wrap.querySelector ? wrap.querySelector('.daily-filter-menu') : null
        if (!wrap || !menu || typeof window == 'undefined') {
          return
        }
        const rect = wrap.getBoundingClientRect()
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || rect.right
        const viewportGap = 12
        const menuWidth = Math.ceil(menu.scrollWidth || menu.offsetWidth || rect.width)
        const rightSpace = viewportWidth - rect.left - viewportGap
        const leftSpace = rect.right - viewportGap
        const shouldDropLeft = menuWidth > rightSpace && leftSpace >= rightSpace
        if (menuType == 'finish') {
          this.dailyFinishMenuDropLeft = shouldDropLeft
        } else {
          this.dailyPeriodMenuDropLeft = shouldDropLeft
        }
      })
    },
    resolveOpenDailyFilterMenuPlacement() {
      if (this.dailyPeriodMenuOpen) {
        this.resolveDailyFilterMenuPlacement('period')
      }
      if (this.dailyFinishMenuOpen) {
        this.resolveDailyFilterMenuPlacement('finish')
      }
    },
    setDailyFinishFilter(value) {
      const nextValue = Number(value)
      this.dailyFinishMenuOpen = false
      this.dailyFinishMenuDropLeft = false
      if (this.dailyTodoFinishFilter == nextValue) {
        return
      }
      this.dailyTodoFinishFilter = nextValue
      this.loadDailyList()
    },
    setDailyViewMode(mode) {
      if (this.isDailyTodoTab) {
        this.dailyViewMode = 'list'
        return
      }
      this.dailyViewMode = mode == 'grid' ? 'grid' : 'list'
      this.updateDailyGridColumnCount()
    },
    changeDailyCalendarMonth(offset) {
      const current = this.toValidDate(this.dailyCalendarCursorDate) || new Date()
      this.dailyCalendarCursorDate = new Date(current.getFullYear(), current.getMonth() + offset, 1)
      this.loadDailyCalendarMarkers()
    },
    goDailyToday() {
      const today = new Date()
      this.dailyCalendarCursorDate = today
      this.dailySelectedDate = this.formatDailyDateValue(today)
      this.loadDailyCalendarMarkers()
      this.loadDailyList()
    },
    selectDailyCalendarDate(value) {
      const date = this.normalizeDailyDateValue(value)
      if (!date) {
        return
      }
      const previousCursor = this.toValidDate(this.dailyCalendarCursorDate) || new Date()
      const nextCursor = this.parseDailyDate(date) || previousCursor
      this.dailySelectedDate = date
      this.dailyCalendarCursorDate = nextCursor
      if (previousCursor.getFullYear() != nextCursor.getFullYear() || previousCursor.getMonth() != nextCursor.getMonth()) {
        this.loadDailyCalendarMarkers()
      }
      this.loadDailyList()
    },
    clearDailySelection() {
      this.clearDailyItemClickTimer()
      this.clearDailyPendingSelectionSnapshot()
      this.dailySelectedGroupIds = []
      this.dailySelectionAnchorId = ''
      this.resetDailySelectionBarOffset()
    },
    isDailyGroupSelected(item) {
      return !!(item && this.dailySelectedGroupIds.includes(item.id))
    },
    handleDailyGroupClick(item, event) {
      if (!item) {
        return
      }
      if (event && event.detail > 1) {
        this.clearDailyItemClickTimer()
        return
      }
      this.clearDailyItemClickTimer()
      this.rememberDailySelectionSnapshot(item)
      const clickEvent = event
        ? { ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey }
        : null
      this.dailyItemClickTimer = setTimeout(() => {
        this.dailyItemClickTimer = null
        this.toggleDailyGroupSelection(item, clickEvent)
      }, 220)
    },
    toggleDailyGroupSelection(item, event) {
      if (!item || this.isDailyTodoTab) {
        return
      }
      const result = selectItemWithWindowsRange(
        this.dailyGroupedItems,
        this.dailySelectedGroupIds,
        item,
        this.dailySelectionAnchorId,
        event
      )
      this.dailySelectedGroupIds = result.selectedIds
      this.dailySelectionAnchorId = result.anchorId
    },
    clearDailyItemClickTimer() {
      if (this.dailyItemClickTimer) {
        clearTimeout(this.dailyItemClickTimer)
        this.dailyItemClickTimer = null
      }
    },
    rememberDailySelectionSnapshot(item) {
      this.clearDailyPendingSelectionSnapshot()
      if (!item) {
        return
      }
      // Keep the pre-click selection briefly so a slower double-click does not leave the opened group selected.
      this.dailyPendingSelectionSnapshot = {
        itemId: item.id,
        selectedIds: this.dailySelectedGroupIds.slice()
      }
      this.dailyPendingSelectionSnapshotTimer = setTimeout(() => {
        this.dailyPendingSelectionSnapshot = null
        this.dailyPendingSelectionSnapshotTimer = null
      }, 1000)
    },
    restoreDailySelectionSnapshot(item) {
      const snapshot = this.dailyPendingSelectionSnapshot
      this.clearDailyPendingSelectionSnapshot()
      if (!item || !snapshot || snapshot.itemId != item.id) {
        return
      }
      this.dailySelectedGroupIds = snapshot.selectedIds.slice()
      this.dailySelectionAnchorId = this.dailySelectedGroupIds[0] || ''
      if (this.dailySelectedGroupIds.length == 0) {
        this.resetDailySelectionBarOffset()
      }
    },
    clearDailyPendingSelectionSnapshot() {
      if (this.dailyPendingSelectionSnapshotTimer) {
        clearTimeout(this.dailyPendingSelectionSnapshotTimer)
        this.dailyPendingSelectionSnapshotTimer = null
      }
      this.dailyPendingSelectionSnapshot = null
    },
    getDailyTodoLevelClass(level) {
      const value = Number(level)
      if (value === 0) {
        return 'important-urgent'
      }
      if (value === 1) {
        return 'urgent'
      }
      if (value === 2) {
        return 'important'
      }
      return 'empty'
    },
    getDailyGridItemClasses(item, index) {
      return {
        selected: this.isDailyGroupSelected(item),
        'grid-edge-left': this.isDailyGridLeftEdge(index),
        'grid-edge-right': this.isDailyGridRightEdge(index),
        'grid-edge-top': this.isDailyGridTopEdge(index),
        'grid-edge-bottom': this.isDailyGridBottomEdge(index)
      }
    },
    isDailyGridLeftEdge(index) {
      const columnCount = Math.max(1, Number(this.dailyGridColumnCount) || 1)
      if (this.isDailyGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || index % columnCount == 0
    },
    isDailyGridRightEdge(index) {
      const columnCount = Math.max(1, Number(this.dailyGridColumnCount) || 1)
      if (this.isDailyGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || (index + 1) % columnCount == 0
    },
    isDailyGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.dailyGridColumnCount) || 1)
      return index < columnCount
    },
    isDailyGridBottomEdge() {
      return false
    },
    isDailyGridLastIncompleteRowLastItem(index) {
      const columnCount = Math.max(1, Number(this.dailyGridColumnCount) || 1)
      const itemCount = this.dailyGroupedItems.length
      return itemCount > 0 && itemCount % columnCount != 0 && index == itemCount - 1
    },
    updateDailyGridColumnCount() {
      if (!this.isDailyMode || this.isDailyTodoTab || this.dailyViewMode != 'grid') {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.dailyGrid
        if (!grid || typeof window === 'undefined') {
          this.dailyGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.dailyGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    isDailyPreviewGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.dailyPreviewGridColumnCount) || 1)
      return index < columnCount
    },
    isDailyPreviewGridSelectedRowStart(index) {
      const columnCount = Math.max(1, Number(this.dailyPreviewGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      if (rowStart == 0) {
        return true
      }
      return !this.hasSelectedDailyPreviewGridRow(rowStart - columnCount, columnCount)
    },
    isDailyPreviewGridSelectionContinuesBelow(index) {
      const columnCount = Math.max(1, Number(this.dailyPreviewGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      return this.hasSelectedDailyPreviewGridRow(rowStart + columnCount, columnCount)
    },
    hasSelectedDailyPreviewGridRow(rowStart, columnCount) {
      if (rowStart < 0 || rowStart >= this.dailyPreviewItems.length) {
        return false
      }
      const rowEnd = Math.min(rowStart + columnCount, this.dailyPreviewItems.length)
      for (let index = rowStart; index < rowEnd; index += 1) {
        const item = this.dailyPreviewItems[index]
        if (item && this.isDailyPreviewSelected(item)) {
          return true
        }
      }
      return false
    },
    updateDailyPreviewGridColumnCount() {
      if (!this.dailyPreviewGridOpen) {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.dailyPreviewGrid
        if (!grid || typeof window === 'undefined') {
          this.dailyPreviewGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.dailyPreviewGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    async openDailyGroupPreview(group, event = null) {
      this.clearDailyItemClickTimer()
      if (event && event.detail > 1) {
        this.restoreDailySelectionSnapshot(group)
      } else {
        this.clearDailyPendingSelectionSnapshot()
      }
      if (!group || this.isDailyTodoTab) {
        return
      }
      const requestId = this.dailyPreviewRequestSeq + 1
      this.dailyPreviewRequestSeq = requestId
      this.dailyPreviewOpen = true
      this.dailyPreviewGridOpen = false
      this.dailyPreviewLoading = true
      this.dailyPreviewTitle = group.name || 'Daily'
      this.dailyPreviewItems = []
      this.dailyPreviewSelectedIds = []
      this.dailyPreviewSelectionAnchorId = ''
      this.dailyPreviewCurrentId = ''
      this.resetDailyPreviewZoom()
      this.clearPreviewZoomTip()
      const previewRequest = this.buildDailyPreviewRequestParams(group)
      const cacheKey = this.getDailyPreviewCacheKey(group, previewRequest)
      const cachedPreview = this.getCachedDailyPreview(cacheKey)
      if (cachedPreview) {
        this.applyDailyPreviewItems(cachedPreview.items, cachedPreview.title || group.name)
        if (requestId == this.dailyPreviewRequestSeq) {
          this.dailyPreviewLoading = false
          this.centerDailyPreviewScroll()
        }
        return
      }
      if (!previewRequest) {
        this.applyDailyPreviewItems(group.items, group.name)
        this.setDailyPreviewCache(cacheKey, this.dailyPreviewItems, group.name)
        if (requestId == this.dailyPreviewRequestSeq) {
          this.dailyPreviewLoading = false
          this.centerDailyPreviewScroll()
        }
        return
      }
      try {
        const ret = await getResourceDailySyncList(previewRequest)
        if (requestId != this.dailyPreviewRequestSeq) {
          return
        }
        const payload = this.resolveApiPayload(ret)
        const rawItems = payload === null ? [] : this.extractArray(payload)
        const normalized = rawItems.map((item, index) => this.normalizeDailyItem(item, index))
        this.applyDailyPreviewItems(normalized.length > 0 ? normalized : group.items, group.name)
        this.setDailyPreviewCache(cacheKey, this.dailyPreviewItems, group.name)
      } catch (error) {
        if (requestId != this.dailyPreviewRequestSeq) {
          return
        }
        this.applyDailyPreviewItems(group.items, group.name)
        if (this.$message && typeof this.$message.error == 'function') {
          this.$message.error(this.getPaperApiErrorMessage(error) || this.ui.loadFailed)
        }
      } finally {
        if (requestId == this.dailyPreviewRequestSeq) {
          this.dailyPreviewLoading = false
          this.centerDailyPreviewScroll()
        }
      }
    },
    buildDailyPreviewRequestParams(group) {
      const request = group && group.previewRequest && typeof group.previewRequest == 'object'
        ? group.previewRequest
        : this.buildDailyPreviewRequestFromItem(group)
      if (!request) {
        return null
      }
      const startDate = this.pickValue(request, ['startDate'])
      const endDate = this.pickValue(request, ['endDate'])
      if (startDate === undefined || startDate === null || startDate === '') {
        return null
      }
      const typeValue = this.pickValue(request, ['type'])
      const type = Number(typeValue)
      const isFinishValue = this.pickValue(request, ['isFinish'])
      return {
        startDate,
        endDate: endDate === undefined || endDate === null || endDate === '' ? startDate : endDate,
        type: Number.isFinite(type) ? type : this.dailyActiveType,
        isFinish: isFinishValue === undefined ? (this.isDailyTodoTab ? this.dailyTodoFinishFilter : -1) : isFinishValue
      }
    },
    getDailyPreviewSharedCacheScope() {
      const name = this.$options && this.$options.name ? this.$options.name : 'DailySync'
      return `${name}:daily-preview:api-order-v2`
    },
    getDailyPreviewCacheKey(group, previewRequest = null) {
      const request = previewRequest || (group && group.previewRequest)
      if (request) {
        return [
          'request',
          request.startDate || '',
          request.endDate || '',
          request.type === undefined ? '' : request.type,
          request.isFinish === undefined ? '' : request.isFinish
        ].join(':')
      }
      const source = group && typeof group == 'object' ? group : {}
      const id = this.pickString(source, ['id', 'resourceId', 'dailyId']) || ''
      const date = source.date || ''
      const name = source.name || ''
      return ['group', id, date, name, this.dailyActiveTab, this.dailyActiveType].join(':')
    },
    getCachedDailyPreview(cacheKey) {
      const cached = typeof this.getSharedPaperPreviewDetailCache == 'function'
        ? this.getSharedPaperPreviewDetailCache(this.getDailyPreviewSharedCacheScope(), cacheKey)
        : null
      const detail = cached && cached.detail ? cached.detail : null
      return detail && Array.isArray(detail.items) ? detail : null
    },
    setDailyPreviewCache(cacheKey, items, title) {
      const list = Array.isArray(items) ? items.slice() : []
      if (!cacheKey || list.length == 0 || typeof this.setSharedPaperPreviewDetailCache != 'function') {
        return
      }
      this.setSharedPaperPreviewDetailCache(this.getDailyPreviewSharedCacheScope(), cacheKey, {
        items: list,
        title: title || this.dailyPreviewTitle || 'Daily'
      }, [])
    },
    applyDailyPreviewItems(items, title) {
      const list = (Array.isArray(items) ? items : [])
        .map((item, index) => this.normalizeDailyPreviewItem(item, index, title))
      this.dailyPreviewItems = list
      this.dailyPreviewCurrentId = list[0] ? list[0].id : ''
      this.dailyPreviewSelectedIds = this.dailyPreviewCurrentId ? [this.dailyPreviewCurrentId] : []
      this.dailyPreviewSelectionAnchorId = this.dailyPreviewCurrentId
    },
    normalizeDailyPreviewItem(item, index, title) {
      const source = item && item.raw && typeof item.raw == 'object' ? Object.assign({}, item.raw, item) : (item || {})
      const rawSource = item && item.raw && typeof item.raw == 'object' ? Object.assign({}, item.raw, item) : (item || {})
      const blankPageDataUrl = this.getDailyBlankPageDataUrl()
      const sourceImageUrl = this.pickDailySourceImageUrl(rawSource)
        || (item && item.sourceImageUrl ? item.sourceImageUrl : '')
        || (item && item.imageUrl && !item.isPlaceholder && item.imageUrl != blankPageDataUrl ? item.imageUrl : '')
      const pageImageUrl = this.pickDailyPageImageUrl(rawSource)
        || (item && item.pageImageUrl ? item.pageImageUrl : '')
      const isPlaceholder = !sourceImageUrl && !pageImageUrl
      const imageUrl = sourceImageUrl || pageImageUrl || blankPageDataUrl
      const pageNo = Number(this.pickValue(source, ['pageNo', 'page', 'index', 'no'])) || index + 1
      const baseId = this.pickString(source, ['id', 'uuid', 'resourceId', 'dailyId'])
      return {
        id: `${baseId || 'daily-preview'}-${pageNo}-${index}`,
        name: this.pickString(source, ['name', 'title', 'fileName']) || title || 'Daily',
        imageUrl,
        sourceImageUrl,
        pageImageUrl,
        isPlaceholder,
        date: this.normalizeDailyDateValue(this.pickValue(source, ['date', 'day', 'syncDate', 'dailyDate', 'createdAt', 'createTime', 'time'])) || (item && item.date ? item.date : ''),
        pageNo,
        pageLabel: `Page ${pageNo}`,
        raw: source
      }
    },
    handleDailyPreviewBack() {
      if (this.dailyPreviewGridOpen) {
        const returnZoomLevel = this.takePreviewGridZoomLevel('daily')
        this.dailyPreviewGridOpen = false
        this.setDailyPreviewZoomLevel(returnZoomLevel)
        return
      }
      this.closeDailyPreview()
      if (this.detailBackRoot) {
        this.$emit('back-root')
      }
    },
    closeDailyPreview() {
      this.dailyPreviewRequestSeq += 1
      this.dailyPreviewOpen = false
      this.dailyPreviewGridOpen = false
      this.dailyPreviewItems = []
      this.dailyPreviewCurrentId = ''
      this.dailyPreviewTitle = ''
      this.dailyPreviewSelectedIds = []
      this.dailyPreviewSelectionAnchorId = ''
      this.dailyPreviewLoading = false
      this.resetDailyPreviewZoom()
      this.clearPreviewZoomTip()
      this.stopDailyPreviewDrag()
    },
    toggleDailyPreviewGrid() {
      if (this.dailyPreviewGridOpen || this.dailyPreviewItems.length == 0) {
        return
      }
      this.dailyPreviewSelectedIds = []
      this.dailyPreviewSelectionAnchorId = this.dailyPreviewCurrentId
      this.rememberPreviewGridZoomLevel('daily', this.dailyPreviewZoomLevel)
      this.dailyPreviewGridOpen = true
      this.clearPreviewZoomTip()
      this.stopDailyPreviewDrag()
    },
    isDailyPreviewSelected(item) {
      return !!(item && this.dailyPreviewSelectedIds.includes(item.id))
    },
    toggleDailyPreviewSelection(item, event) {
      if (!item || !item.id) {
        return
      }
      const result = selectItemWithWindowsRange(
        this.dailyPreviewItems,
        this.dailyPreviewSelectedIds,
        item,
        this.dailyPreviewSelectionAnchorId || this.dailyPreviewCurrentId,
        event
      )
      this.dailyPreviewSelectedIds = result.selectedIds
      this.dailyPreviewSelectionAnchorId = result.anchorId
    },
    openDailyPreviewItem(item) {
      if (!item || !item.id) {
        return
      }
      const isSameItem = item.id == this.dailyPreviewCurrentId
      const returnZoomLevel = this.takePreviewGridZoomLevel('daily')
      this.dailyPreviewCurrentId = item.id
      this.dailyPreviewGridOpen = false
      if (isSameItem) {
        this.setDailyPreviewZoomLevel(returnZoomLevel)
        return
      }
      this.resetDailyPreviewZoom()
      this.clearPreviewZoomTip()
    },
    goPrevDailyPreview() {
      if (this.dailyPreviewGridOpen || this.dailyPreviewIndex <= 0) {
        return
      }
      this.dailyPreviewCurrentId = this.dailyPreviewItems[this.dailyPreviewIndex - 1].id
      this.clearPreviewZoomTip()
      this.centerDailyPreviewScroll()
    },
    goNextDailyPreview() {
      if (this.dailyPreviewGridOpen || this.dailyPreviewIndex >= this.dailyPreviewItems.length - 1) {
        return
      }
      this.dailyPreviewCurrentId = this.dailyPreviewItems[this.dailyPreviewIndex + 1].id
      this.clearPreviewZoomTip()
      this.centerDailyPreviewScroll()
    },
    zoomInDailyPreview() {
      if (this.dailyPreviewGridOpen) {
        return
      }
      const nextLevel = this.setDailyPreviewZoomLevel(this.dailyPreviewZoomLevel + 1)
      this.showPreviewZoomTip('daily', 'in', nextLevel, this.ui)
    },
    zoomOutDailyPreview() {
      if (this.dailyPreviewGridOpen) {
        return
      }
      const nextLevel = this.setDailyPreviewZoomLevel(this.dailyPreviewZoomLevel - 1)
      this.showPreviewZoomTip('daily', 'out', nextLevel, this.ui)
    },
    resetDailyPreviewZoom() {
      this.setDailyPreviewZoomLevel(0)
    },
    setDailyPreviewZoomLevel(level) {
      const nextLevel = this.clampPreviewZoomLevel(level)
      this.dailyPreviewZoomLevel = nextLevel
      this.dailyPreviewZoomValue = this.getPreviewZoomValue(nextLevel)
      this.centerDailyPreviewScroll()
      return nextLevel
    },
    centerDailyPreviewScroll() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame == 'function') {
          window.requestAnimationFrame(this.applyDailyPreviewCenterScroll)
          return
        }
        this.applyDailyPreviewCenterScroll()
      })
    },
    applyDailyPreviewCenterScroll() {
      const shell = this.$refs.dailyPreviewShell
      if (!shell) {
        return
      }
      shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2)
      shell.scrollTop = 0
    },
    startDailyPreviewDrag(event) {
      const shell = this.$refs.dailyPreviewShell
      if (this.dailyPreviewGridOpen || !event || event.button !== 0 || !shell) {
        return
      }
      if (shell.scrollWidth <= shell.clientWidth && shell.scrollHeight <= shell.clientHeight) {
        return
      }
      event.preventDefault()
      this.dailyPreviewDragState = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: shell.scrollLeft,
        scrollTop: shell.scrollTop
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handleDailyPreviewDrag)
        document.addEventListener('mouseup', this.stopDailyPreviewDrag)
      }
    },
    handleDailyPreviewDrag(event) {
      const shell = this.$refs.dailyPreviewShell
      const state = this.dailyPreviewDragState
      if (!shell || !state || !event) {
        return
      }
      shell.scrollLeft = state.scrollLeft - (event.clientX - state.x)
      shell.scrollTop = state.scrollTop - (event.clientY - state.y)
    },
    stopDailyPreviewDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handleDailyPreviewDrag)
        document.removeEventListener('mouseup', this.stopDailyPreviewDrag)
      }
      this.dailyPreviewDragState = null
    },
    async downloadSelectedDailyGroups() {
      const groups = this.dailySelectedGroups.slice()
      if (groups.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartFileDownloadBatch(groups.length)) {
        return
      }
      this.openDailyGroupDownloadFormatModal(groups, { clearDailySelectionOnStart: true })
    },
    async downloadDailyGroupFromMenu(group) {
      this.moreMenuId = ''
      if (!group || !Array.isArray(group.items) || group.items.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      this.openDailyGroupDownloadFormatModal([group])
    },
    async resolveDailyGroupDownloadItems(group) {
      const fallbackItems = group && Array.isArray(group.items) ? group.items : []
      const previewRequest = this.buildDailyPreviewRequestParams(group)
      if (!previewRequest) {
        return fallbackItems
      }
      try {
        const ret = await getResourceDailySyncList(previewRequest)
        const payload = this.resolveApiPayload(ret)
        const rawItems = payload === null ? [] : this.extractArray(payload)
        const normalized = rawItems.map((item, index) => this.normalizeDailyItem(item, index))
        return normalized.length > 0 ? normalized : fallbackItems
      } catch (error) {
        if (this.$message && typeof this.$message.error == 'function') {
          this.$message.error(this.getPaperApiErrorMessage(error) || this.ui.loadFailed)
        }
        return fallbackItems
      }
    },
    downloadCurrentDailyPreview() {
      if (this.dailyPreviewLoading) {
        return
      }
      if (this.dailyPreviewGridOpen) {
        if (this.dailyPreviewSelectedItems.length > 0) {
          this.openDailyDownloadFormatModal(this.dailyPreviewSelectedItems, this.dailyPreviewTitle || 'Daily', { skipSelectionLimit: true })
          return
        }
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (this.dailyPreviewCurrentItem) {
        this.openDailyDownloadFormatModal([this.dailyPreviewCurrentItem], this.dailyPreviewTitle || 'Daily')
        return
      }
      if (this.dailyPreviewItems.length > 0) {
        this.openDailyDownloadFormatModal([this.dailyPreviewItems[0]], this.dailyPreviewTitle || 'Daily')
        return
      }
      this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
    },
    openDailyGroupDownloadFormatModal(groups, options = {}) {
      const list = (Array.isArray(groups) ? groups : []).filter(group => group && Array.isArray(group.items) && group.items.length > 0)
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartFileDownloadBatch(list.length, options && options.skipSelectionLimit ? { skipSelectionLimit: true } : {})) {
        return
      }
      this.paperDownloadItems = []
      this.paperDownloadDetailPages = []
      this.learningAnnotationDownloadItems = []
      this.dailyDownloadItems = list
      this.dailyDownloadClearSelectionOnStart = !!(options && options.clearDailySelectionOnStart)
      this.dailyDownloadSkipSelectionLimit = !!(options && options.skipSelectionLimit)
      this.dailyDownloadBaseName = list.length == 1 && list[0] && list[0].name ? list[0].name : 'Daily'
      this.paperDownloadFormatContext = 'daily-groups'
      this.paperDownloadFormat = 'pdf'
      this.paperDownloadFormatModalOpen = true
    },
    openDailyDownloadFormatModal(items, baseName, options = {}) {
      const safeBaseName = baseName || this.dailyPreviewTitle || 'Daily'
      const list = (Array.isArray(items) ? items : [])
        .map((item, index) => this.normalizeDailyDownloadPageForPaper(item, index, safeBaseName))
        .filter(page => page && (page.imageUrl || page.pageImageUrl || page.isPlaceholder))
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, options && options.skipSelectionLimit ? { skipSelectionLimit: true } : {})) {
        return
      }
      this.paperDownloadItems = []
      this.paperDownloadDetailPages = list
      this.learningAnnotationDownloadItems = []
      this.dailyDownloadItems = []
      this.dailyDownloadClearSelectionOnStart = !!(options && options.clearDailySelectionOnStart)
      this.dailyDownloadSkipSelectionLimit = !!(options && options.skipSelectionLimit)
      this.dailyDownloadBaseName = safeBaseName
      this.paperDownloadFormatContext = 'daily-detail-pages'
      this.paperDownloadFormat = 'png'
      this.paperDownloadFormatModalOpen = true
    },
    normalizeDailyDownloadPageForPaper(item, index, baseName) {
      if (item === undefined || item === null) {
        return null
      }
      const normalized = this.normalizeDailyPreviewItem(item, index, baseName)
      const pageNo = Number(normalized.pageNo) || index + 1
      const sourceImageUrl = normalized.sourceImageUrl ? String(normalized.sourceImageUrl).trim() : ''
      const pageImageUrl = normalized.pageImageUrl ? String(normalized.pageImageUrl).trim() : ''
      const displayImageUrl = !normalized.isPlaceholder && normalized.imageUrl ? String(normalized.imageUrl).trim() : ''
      let imageUrl = sourceImageUrl || displayImageUrl
      if (pageImageUrl && imageUrl == pageImageUrl) {
        imageUrl = ''
      }
      return Object.assign({}, normalized, {
        id: normalized.id || `daily-preview-${pageNo}-${index}`,
        name: normalized.name || baseName || 'Daily',
        imageUrl,
        sourceImageUrl,
        pageImageUrl,
        pageNo,
        pageLabel: normalized.pageLabel || `Page ${pageNo}`
      })
    },
    startDailyDetailPagesDownload(pages, baseName, format = 'png', options = {}) {
      const safeBaseName = baseName || this.dailyPreviewTitle || 'Daily'
      const list = Array.isArray(pages)
        ? pages.map((page, index) => this.normalizeDailyDownloadPageForPaper(page, index, safeBaseName))
          .filter(page => page && (page.imageUrl || page.pageImageUrl || page.isPlaceholder))
        : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, options)) {
        return
      }
      const task = this.createDailyDetailPagesDownloadTask(list, safeBaseName, format)
      this.$enqueuePaperDownloadTask(task, () => this.runDailyDetailPagesDownloadTask(task))
      if (this.dailyPreviewGridOpen) {
        this.dailyPreviewSelectedIds = []
        this.dailyPreviewSelectionAnchorId = ''
      }
    },
    createDailyDetailPagesDownloadTask(pages, baseName, format = 'png') {
      const safeName = this.$getDownloadBaseName(baseName, 'Daily', this.sanitizeFileName)
      const safeFormat = format == 'png' ? 'png' : 'pdf'
      const isSingle = pages.length == 1
      const extension = safeFormat == 'pdf' ? 'pdf' : (isSingle ? 'png' : 'zip')
      return {
        id: `daily-page-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'daily-detail-pages',
        name: safeName,
        fileName: isSingle ? `${safeName}-\u7b2c${pages[0].pageNo || 1}\u9875.${extension}` : `${safeName}.${extension}`,
        format: safeFormat,
        pages: pages.slice(),
        pngFiles: [],
        pdfPages: [],
        failedPageIndex: 0,
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runDailyDetailPagesDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const pages = Array.isArray(task.pages) ? task.pages : []
        if (pages.length == 0) {
          throw new Error('No pages')
        }
        this.updatePaperDownloadTask(task, {
          state: 'running',
          progress: Math.max(task.progress || 0, 5),
          statusText: this.ui.downloading
        })
        if (task.format == 'pdf') {
          await this.downloadDailyItemsAsPdf(task, pages)
        } else {
          await this.downloadDailyItemsAsPng(task, pages)
        }
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    startDailyItemsDownload(items, baseName, format = 'png', options = {}) {
      return this.startDailyDetailPagesDownload(items, baseName, format, options)
    },
    startDailyGroupDownloads(groups, format = 'png', options = {}) {
      const list = (Array.isArray(groups) ? groups : []).filter(group => group && Array.isArray(group.items) && group.items.length > 0)
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartFileDownloadBatch(list.length, options)) {
        return
      }
      if (this.$shouldBundleDownloadItems(list)) {
        const task = this.$createDownloadBundleTask(list, format, '', {
          idPrefix: 'daily-group-bundle-download',
          kind: 'daily-group-bundle',
          fallbackName: 'Daily',
          sanitizeFileName: this.sanitizeFileName,
          getItemName: group => group && group.name
        })
        this.$enqueuePaperDownloadTask(task, () => this.runDailyGroupDownloadBundleTask(task))
        return
      }
      list.forEach(group => {
        const task = this.createDailyGroupDownloadTask(group, format)
        this.$enqueuePaperDownloadTask(task, () => this.runDailyGroupDownloadTask(task, group, format))
      })
    },
    runDailyGroupDownloadBundleTask(task) {
      this.$runDownloadBundleTask(task, {
        items: task.sourceItems,
        format: task.format,
        kind: 'daily-group-bundle',
        fallbackName: 'Daily',
        sanitizeFileName: this.sanitizeFileName,
        getItemName: group => group && group.name,
        createItemTask: (group, format) => this.createDailyGroupDownloadTask(group, format),
        runItemTask: (bundleTask, group, format) => this.runDailyGroupDownloadTask(bundleTask, group, format)
      })
    },
    createDailyGroupDownloadTask(group, format = 'png') {
      const safeFormat = format == 'pdf' ? 'pdf' : 'png'
      const baseName = this.$getDownloadBaseName(group && group.name, 'Daily', this.sanitizeFileName)
      const pageCount = group && Array.isArray(group.items) ? group.items.length : 0
      const extension = safeFormat == 'pdf' ? 'pdf' : this.$getPaperDownloadPngExtension(pageCount)
      return {
        id: `daily-group-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'daily-group',
        name: baseName,
        fileName: `${baseName}.${extension}`,
        format: safeFormat,
        sourceDailyGroup: group,
        pages: [],
        pngFiles: [],
        pdfPages: [],
        failedPageIndex: 0,
        openUrl: '',
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runDailyGroupDownloadTask(task, group, format = 'png') {
      const sourceGroup = group || task && task.sourceDailyGroup
      const safeFormat = format == 'pdf' ? 'pdf' : 'png'
      const baseName = this.$getDownloadBaseName(sourceGroup && sourceGroup.name || task && task.name, 'Daily', this.sanitizeFileName)
      try {
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, {
          state: 'running',
          progress: Math.max(task.progress || 0, 5),
          statusText: this.ui.downloading
        })
        const items = await this.resolveDailyGroupDownloadItems(sourceGroup)
        const pages = (Array.isArray(items) ? items : [])
          .map((item, index) => this.normalizeDailyDownloadPageForPaper(item, index, baseName))
          .filter(page => page && (page.imageUrl || page.pageImageUrl || page.isPlaceholder))
        if (pages.length == 0) {
          throw new Error('No pages')
        }
        const extension = safeFormat == 'pdf' ? 'pdf' : this.$getPaperDownloadPngExtension(pages.length)
        this.updatePaperDownloadTask(task, {
          name: baseName,
          fileName: `${baseName}.${extension}`,
          format: safeFormat,
          pages
        })
        await this.runDailyDetailPagesDownloadTask(task)
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    createDailyItemsDownloadTask(items, baseName, format) {
      const safeName = this.$getDownloadBaseName(baseName, 'Daily', this.sanitizeFileName)
      const isSingle = items.length == 1
      const normalizedFormat = format == 'pdf' ? 'pdf' : 'png'
      return {
        id: `daily-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'daily-pages',
        name: safeName,
        fileName: normalizedFormat == 'pdf' ? `${safeName}.pdf` : (isSingle ? `${safeName}.png` : `${safeName}.zip`),
        format: normalizedFormat,
        dailyItems: items.slice(),
        pngFiles: [],
        pdfPages: [],
        failedPageIndex: 0,
        openUrl: '',
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runDailyItemsDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const items = Array.isArray(task.dailyItems) ? task.dailyItems : []
        if (items.length == 0) {
          throw new Error('No pages')
        }
        this.updatePaperDownloadTask(task, {
          state: 'running',
          progress: Math.max(task.progress || 0, 5),
          statusText: this.ui.downloading
        })
        if (task.format == 'pdf') {
          await this.downloadDailyItemsAsPdf(task, items)
        } else {
          await this.downloadDailyItemsAsPng(task, items)
        }
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    async downloadDailyItemsAsPng(task, items) {
      const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(items, task)
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      for (let i = 0; i < items.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const page = items[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, items.length, 5, 85, 0),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchDailyPageImageBlob(page, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(i, items.length, 5, 85, percent),
            statusText: this.ui.downloading
          })
        }, task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        const framedBlob = await this.canvasToPngBlob(canvas)
        this.releaseCanvas(canvas)
        pngFiles[i] = {
          fileName: this.getDailyPageFileName(page, i),
          buffer: await this.blobToArrayBuffer(framedBlob)
        }
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      if (items.length == 1 && pngFiles[0]) {
        this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
        this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
        return
      }
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getDailyPageFileName(items[index], index), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    async downloadDailyItemsAsPdf(task, items) {
      const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(items, task)
      const imagePages = Array.isArray(task.pdfPages) ? task.pdfPages.slice() : []
      this.updatePaperDownloadTask(task, { pdfPages: imagePages })
      for (let i = 0; i < items.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (imagePages[i]) {
          continue
        }
        const page = items[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, items.length, 5, 80, 0),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchDailyPageImageBlob(page, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(i, items.length, 5, 80, percent),
            statusText: this.ui.downloading
          })
        }, task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        imagePages[i] = await this.canvasToJpegPage(canvas)
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pdfPages: imagePages.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u751f\u6210 PDF' })
      const pdfBlob = this.buildPdfBlob(imagePages)
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    getDailyBlankPageDataUrl() {
      const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 4" width="3" height="4"><rect width="3" height="4" fill="#fff"/></svg>'
      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
    },
    async createWhiteBackgroundImageBlob(width, height) {
      const targetWidth = Math.max(1, Math.round(Number(width) || 900))
      const targetHeight = Math.max(1, Math.round(Number(height) || Math.ceil(targetWidth * 4 / 3)))
      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight
      const context = canvas.getContext('2d')
      context.fillStyle = '#fff'
      context.fillRect(0, 0, targetWidth, targetHeight)
      const blob = await this.canvasToPngBlob(canvas)
      this.releaseCanvas(canvas)
      return blob
    },
    async flattenImageBlobOnWhiteBackground(blob, width, height) {
      if (!blob) {
        return this.createWhiteBackgroundImageBlob(width, height)
      }
      const image = await this.loadImageFromBlob(blob)
      const imageWidth = Math.max(1, Math.round(image.naturalWidth || image.width || 900))
      const imageHeight = Math.max(1, Math.round(image.naturalHeight || image.height || Math.ceil(imageWidth * 4 / 3)))
      const explicitWidth = Number(width)
      const targetWidth = Math.max(
        1,
        Math.round(Number.isFinite(explicitWidth) && explicitWidth > 0 ? explicitWidth : Math.max(imageWidth, Math.ceil(imageHeight * 3 / 4), 900))
      )
      const explicitHeight = Number(height)
      const targetHeight = Math.max(
        1,
        Math.round(Number.isFinite(explicitHeight) && explicitHeight > 0 ? explicitHeight : Math.ceil(targetWidth * 4 / 3))
      )
      const drawWidth = targetWidth
      const drawHeight = Math.max(1, Math.round(imageHeight * targetWidth / imageWidth))
      const finalTargetHeight = Math.max(targetHeight, drawHeight)
      const drawX = 0
      const drawY = Math.max(0, Math.round((finalTargetHeight - drawHeight) / 2))
      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = finalTargetHeight
      const context = canvas.getContext('2d')
      if (context && 'imageSmoothingEnabled' in context) {
        context.imageSmoothingEnabled = true
        if ('imageSmoothingQuality' in context) {
          context.imageSmoothingQuality = 'high'
        }
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, drawX, drawY, drawWidth, drawHeight)
      const flattenedBlob = await this.canvasToPngBlob(canvas)
      this.releaseCanvas(canvas)
      return flattenedBlob
    },
    async fetchDailyPageImageBlob(page, onProgress, task) {
      const pageInfo = page || {}
      if (pageInfo.isPlaceholder || (!pageInfo.sourceImageUrl && !pageInfo.pageImageUrl && !pageInfo.imageUrl)) {
        if (typeof onProgress == 'function') {
          onProgress(100)
        }
        return this.createWhiteBackgroundImageBlob()
      }
      const sourceImageUrl = pageInfo.sourceImageUrl ? String(pageInfo.sourceImageUrl).trim() : ''
      const displayImageUrl = !pageInfo.isPlaceholder && pageInfo.imageUrl ? String(pageInfo.imageUrl).trim() : ''
      const pageImageUrl = pageInfo.pageImageUrl ? String(pageInfo.pageImageUrl).trim() : ''
      const imageUrl = sourceImageUrl || displayImageUrl
      const baseImageUrl = pageImageUrl && pageImageUrl != imageUrl ? pageImageUrl : ''
      if (!imageUrl && !baseImageUrl) {
        if (typeof onProgress == 'function') {
          onProgress(100)
        }
        return this.createWhiteBackgroundImageBlob()
      }
      const frameKey = task
        ? (task.compositeFrameSize ? `${task.compositeFrameSize.width}x${task.compositeFrameSize.height}` : 'download-source')
        : 'preview-source'
      const cacheKey = `daily-page:${baseImageUrl}|${imageUrl}|${frameKey}`
      return this.fetchCachedPreviewPerformanceBlob(cacheKey, async () => {
        const blob = await this.fetchPaperPageImageBlob({
          imageUrl,
          pageImageUrl: baseImageUrl
        }, onProgress, task)
        const flattenedBlob = await this.flattenImageBlobOnWhiteBackground(blob)
        if (typeof onProgress == 'function') {
          onProgress(100)
        }
        return flattenedBlob
      }, { cacheKey, onProgress, task })
    },
    getDailyPageFileName(page, index) {
      const pageNo = page && page.pageNo ? page.pageNo : index + 1
      const name = this.$getDownloadBaseName(page && page.name ? page.name : this.dailyPreviewTitle, 'Daily', this.sanitizeFileName)
      return `${name}-${pageNo}.png`
    },
    buildDailyCalendarCells(cursor) {
      const current = this.toValidDate(cursor) || new Date()
      const firstDay = new Date(current.getFullYear(), current.getMonth(), 1)
      const start = new Date(firstDay)
      start.setDate(firstDay.getDate() - this.getDailyWeekStartOffset(firstDay))
      const today = this.formatDailyDateValue(new Date())
      const selected = this.dailySelectedDate
      const weekRange = this.dailyPeriod == 'week' ? this.dailyListDateRange : null
      const weekStart = weekRange && weekRange.start ? weekRange.start : ''
      const weekEnd = weekRange && weekRange.end ? weekRange.end : ''
      const markerMap = this.dailyCalendarMarkerMap
      const cells = []
      for (let i = 0; i < 42; i++) {
        const date = new Date(start)
        date.setDate(start.getDate() + i)
        const value = this.formatDailyDateValue(date)
        const isToday = value == today
        const markers = this.getDailyCalendarCellMarkers(markerMap[value], isToday)
        const weekSelected = !!(weekStart && weekEnd && value >= weekStart && value <= weekEnd)
        cells.push({
          key: value,
          dateValue: value,
          day: date.getDate(),
          currentMonth: date.getMonth() == current.getMonth(),
          today: isToday,
          selected: value == selected,
          weekSelected,
          weekStart: weekSelected && value == weekStart,
          weekEnd: weekSelected && value == weekEnd,
          hasData: markers.length > 0,
          markers
        })
      }
      return cells
    },
    getDailyCalendarCellMarkers(markerState, isToday = false) {
      if (!markerState) {
        return []
      }
      const todoMarker = markerState.hasTodo
        ? {
            key: 'todo',
            icon: isToday ? ICON_DAILY_MARKER_TODAY : ICON_DAILY_MARKER_TODO
          }
        : null
      const taskNoteMarker = markerState.hasTaskNote
        ? {
            key: 'task-note',
            icon: isToday ? ICON_DAILY_MARKER_TASK_NOTE_TODAY : ICON_DAILY_MARKER_TASK_NOTE
          }
        : null
      if (!isToday && todoMarker && taskNoteMarker) {
        return [taskNoteMarker, todoMarker]
      }
      return [todoMarker, taskNoteMarker].filter(Boolean)
    },
    getDailyCalendarMonthValue(value) {
      const date = this.toValidDate(value) || new Date()
      if (!date || Number.isNaN(date.getTime())) {
        return ''
      }
      const month = String(date.getMonth() + 1).padStart(2, '0')
      return `${date.getFullYear()}-${month}`
    },
    getDailyMonthRange(value) {
      const date = this.toValidDate(value) || new Date()
      const start = new Date(date.getFullYear(), date.getMonth(), 1)
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0)
      return {
        start: this.formatDailyDateValue(start),
        end: this.formatDailyDateValue(end)
      }
    },
    getDailyRangeByPeriod(baseDate, period) {
      const date = this.toValidDate(baseDate) || new Date()
      if (period == 'day') {
        const value = this.formatDailyDateValue(date)
        return { start: value, end: value }
      }
      if (period == 'week') {
        const start = new Date(date)
        start.setDate(date.getDate() - this.getDailyWeekStartOffset(date))
        const end = new Date(start)
        end.setDate(start.getDate() + 6)
        return {
          start: this.formatDailyDateValue(start),
          end: this.formatDailyDateValue(end)
        }
      }
      return this.getDailyMonthRange(date)
    },
    getDailyWeekStartOffset(date) {
      const day = date instanceof Date ? date.getDay() : 0
      return this.isCjkDailyLanguage ? day : (day + 6) % 7
    },
    normalizeDailyDateValue(value) {
      const date = this.parseDailyDate(value)
      return date ? this.formatDailyDateValue(date) : ''
    },
    parseDailyDate(value) {
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : this.startOfDayDate(value)
      }
      if (value === undefined || value === null || value === '') {
        return null
      }
      if (typeof value == 'number' || /^\d+$/.test(String(value))) {
        const number = Number(value)
        const date = new Date(number < 1000000000000 ? number * 1000 : number)
        return Number.isNaN(date.getTime()) ? null : this.startOfDayDate(date)
      }
      const text = String(value || '').trim()
      const matched = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
      if (matched) {
        const date = new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]))
        return Number.isNaN(date.getTime()) ? null : this.startOfDayDate(date)
      }
      const date = new Date(text.replace(/-/g, '/'))
      return Number.isNaN(date.getTime()) ? null : this.startOfDayDate(date)
    },
    toValidDate(value) {
      return this.parseDailyDate(value)
    },
    startOfDayDate(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    },
    formatDailyDateValue(value) {
      const date = value instanceof Date ? value : this.parseDailyDate(value)
      if (!date || Number.isNaN(date.getTime())) {
        return ''
      }
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    },
    formatDailyTodoDate(value) {
      const date = this.parseDailyDate(value)
      if (!date || Number.isNaN(date.getTime())) {
        return '-'
      }
      return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
    },
    buildDailyHeaderTextPart(value) {
      return { type: 'text', value: String(value) }
    },
    buildDailyHeaderLinePart() {
      return { type: 'line', value: '-' }
    },
    buildDailyHeaderSeparatorPart(value) {
      return { type: 'separator', value: String(value) }
    },
    buildDailyNumericDateParts(year, month, day) {
      const parts = [
        this.buildDailyHeaderTextPart(year),
        this.buildDailyHeaderLinePart(),
        this.buildDailyHeaderTextPart(month)
      ]
      if (day !== undefined && day !== null) {
        parts.push(this.buildDailyHeaderLinePart(), this.buildDailyHeaderTextPart(day))
      }
      return parts
    },
    getDailyEnglishMonthName(monthIndex, abbreviated = false) {
      const names = abbreviated ? DAILY_MONTH_SHORT_NAMES : DAILY_MONTH_NAMES
      return names[Number(monthIndex)] || ''
    },
    formatDailyEnglishDate(value, abbreviated = false) {
      const date = this.toValidDate(value) || new Date()
      return `${this.getDailyEnglishMonthName(date.getMonth(), abbreviated)} ${date.getDate()}, ${date.getFullYear()}`
    },
    buildDailyCjkMonthHeaderText(year, month) {
      return `${year}\u5e74${month}\u6708`
    },
    buildDailyCjkMonthText(year, month) {
      return `${year}年${month}月`
    },
    buildDailyCjkDateText(year, month, day) {
      return `${this.buildDailyCjkMonthText(year, month)}${day}日`
    },
    buildDailyHeaderMonthParts(value) {
      const date = this.toValidDate(value) || new Date()
      const text = this.isCjkDailyLanguage
        ? this.buildDailyCjkMonthHeaderText(date.getFullYear(), date.getMonth() + 1)
        : `${this.getDailyEnglishMonthName(date.getMonth(), true)} ${date.getFullYear()}`
      return [this.buildDailyHeaderTextPart(text)]
    },
    buildDailyHeaderDateParts(value) {
      const date = this.toValidDate(value) || new Date()
      const text = this.isCjkDailyLanguage
        ? this.buildDailyCjkDateText(date.getFullYear(), date.getMonth() + 1, date.getDate())
        : this.formatDailyEnglishDate(date, true)
      return [this.buildDailyHeaderTextPart(text)]
    },
    buildDailyHeaderDateRangeParts(startValue, endValue) {
      const start = this.toValidDate(startValue) || new Date()
      const end = this.toValidDate(endValue) || start
      const sameYear = start.getFullYear() == end.getFullYear()
      const sameMonth = sameYear && start.getMonth() == end.getMonth()
      if (this.isCjkDailyLanguage) {
        if (sameMonth) {
          return [this.buildDailyHeaderTextPart(`${this.buildDailyCjkDateText(start.getFullYear(), start.getMonth() + 1, start.getDate())} ~ ${end.getDate()}\u65e5`)]
        }
        if (sameYear) {
          return [this.buildDailyHeaderTextPart(`${this.buildDailyCjkDateText(start.getFullYear(), start.getMonth() + 1, start.getDate())} ~ ${end.getMonth() + 1}\u6708${end.getDate()}\u65e5`)]
        }
        return [this.buildDailyHeaderTextPart(`${this.buildDailyCjkDateText(start.getFullYear(), start.getMonth() + 1, start.getDate())} ~ ${this.buildDailyCjkDateText(end.getFullYear(), end.getMonth() + 1, end.getDate())}`)]
      }
      return [this.buildDailyHeaderTextPart(`${this.formatDailyEnglishDate(start, true)} - ${this.formatDailyEnglishDate(end, true)}`)]
    },
    formatDailyHeaderDate(value) {
      return this.buildDailyHeaderDateParts(value).map(part => part.value).join('')
    },
    formatDailyHeaderMonth(value) {
      return this.buildDailyHeaderMonthParts(value).map(part => part.value).join('')
    },
    formatDailyHeaderDateRange(startValue, endValue) {
      return this.buildDailyHeaderDateRangeParts(startValue, endValue).map(part => part.value).join('')
    },
    usesCjkDailyDateFormat() {
      return this.isCjkDailyLanguage
    },
    loadPaperList(options = {}) {
      const pageOptions = this.normalizePaperListLoadOptions(options)
      if (this.paperListLoading || this.paperListAppendLoading) {
        this.paperListPendingLoad = pageOptions
        return
      }
      const requestId = this.paperListRequestSeq + 1
      this.paperListRequestSeq = requestId
      this.paperListLoading = !pageOptions.append
      this.paperListAppendLoading = pageOptions.append
      getLearningSyncList(this.buildPaperListParams(pageOptions.pageIndex)).then(ret => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        const payload = this.resolveApiPayload(ret)
        if (payload === null) {
          this.handlePaperListLoadFailed(ret)
          return
        }
        this.applyPaperListPage(payload, this.normalizePaperList(payload), pageOptions)
      }).catch(error => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        this.handlePaperListLoadFailed(error)
      }).finally(() => {
        if (!this.isActivePaperListRequest(requestId)) {
          return
        }
        this.syncSelectionWithItems()
        this.paperListLoading = false
        this.paperListAppendLoading = false
        this.updatePaperGridColumnCount()
        if (this.paperListPendingLoad) {
          const pendingOptions = this.paperListPendingLoad
          this.paperListPendingLoad = false
          this.loadPaperList(pendingOptions)
        }
      })
    },
    isActivePaperListRequest(requestId) {
      return requestId == this.paperListRequestSeq
    },
    buildPaperListParams(pageIndex = this.paperListPageIndex) {
      return {
        pageIndex,
        pageSize: this.paperListPageSize,
        appType: this.paperListAppType,
        resourceId: this.paperListResourceId,
        isDelete: this.paperFilter == 'deleted' ? 1 : -1,
        subTab: -1,
        star: this.paperFilter == 'favorite' ? 1 : -1,
        sortField: this.toResourceSortField(this.sortField),
        sortOrder: this.sortDesc ? 'desc' : 'asc'
      }
    },
    toResourceSortField(field) {
      if (field == 'createTime') {
        return 'creation_time'
      }
      if (field == 'modifyTime') {
        return 'last_modified_time'
      }
      if (field == 'name') {
        return 'name'
      }
      return ''
    },
    async loadPaperDetail(item, options = {}) {
      const password = options.password || ''
      const requestId = this.paperDetailRequestSeq + 1
      const cacheKey = this.getPaperDetailCacheKey(item, password)
      this.paperDetailRequestSeq = requestId
      this.paperDetailLoading = true
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.paperDetail = null
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetLearningDetailState()
      this.resetPaperZoom()
      this.clearPreviewZoomTip()

      const cachedDetail = !options.force ? this.getCachedPaperDetail(cacheKey) : null
      if (cachedDetail) {
        this.paperPreviewObjectUrls = cachedDetail.objectUrls || []
        this.applyPaperDetail(cachedDetail.detail)
        this.setPaperDetailProgress(100, requestId)
        this.paperDetailLoading = false
        return true
      }

      try {
        this.setPaperDetailProgress(5, requestId)
        const ret = options.prefetchedRet || await this.fetchPaperDetailResponse(item, password)
        if (requestId != this.paperDetailRequestSeq) {
          return false
        }
        this.setPaperDetailProgress(18, requestId)
        if (password && this.isPaperPasswordCodeError(ret)) {
          throw new Error(this.getPaperPasswordErrorMessage())
        }
        if (this.isPaperApiCodeFailed(ret)) {
          throw new Error(this.getPaperApiErrorMessage(ret) || `${this.appLabel} detail failed`)
        }
        const payload = this.resolveApiPayload(ret)
        if (payload === null) {
          throw new Error(`${this.appLabel} detail failed`)
        }
        const rawDetail = await this.normalizePaperDetail(payload, item)
        this.setPaperDetailProgress(25, requestId)
        const prepared = await this.preparePaperPreviewDetail(rawDetail, progress => {
          this.setPaperDetailProgress(progress, requestId)
        })
        if (requestId != this.paperDetailRequestSeq) {
          this.revokePaperPreviewObjectUrls(prepared.objectUrls)
          return false
        }
        this.paperPreviewObjectUrls = prepared.objectUrls || []
        this.applyPaperDetail(prepared.detail)
        this.setPaperDetailCache(cacheKey, prepared.detail, prepared.objectUrls)
        this.setPaperDetailProgress(100, requestId)
        return true
      } catch (error) {
        if (requestId != this.paperDetailRequestSeq) {
          return false
        }
        this.paperDetail = null
        this.clearPaperPreviewObjectUrls({ revoke: false })
        this.paperDetailError = password && this.isPaperPasswordCodeError(error)
          ? this.getPaperPasswordErrorMessage()
          : this.getPaperDetailErrorMessage(error)
        return false
      } finally {
        if (requestId == this.paperDetailRequestSeq) {
          this.paperDetailLoading = false
        }
      }
    },
    retryPaperDetailLoad() {
      if (this.paperDetailLoading || !this.currentPaper) {
        return
      }
      this.loadPaperDetail(this.currentPaper, { force: true, password: this.currentPaperPassword || '' })
    },
    applyPaperDetail(detail) {
      this.paperDetail = detail
      if (this.isLearningMode) {
        this.applyLearningDetailState(detail)
        return
      }
      if (detail && detail.imagePages && detail.imagePages.length > 0) {
        this.currentPageNo = detail.imagePages[0].pageNo
      }
      this.centerPaperPreviewScroll()
    },
    resetLearningDetailState() {
      this.activeLearningTab = 'summary'
      this.activeLearningHighlightId = ''
      this.learningHighlightDetailOpen = false
      this.activeLearningAnnotationId = ''
      this.learningAnnotationViewMode = 'grid'
      this.learningAnnotationPreviewOpen = false
      this.learningAnnotationPreviewGridOpen = false
      this.learningAnnotationPreviewSelectedIds = []
      this.learningAnnotationPreviewSelectionAnchorId = ''
      this.stopLearningAnnotationPreviewDrag()
      this.resetLearningAnnotationPreviewZoom()
      this.clearPreviewZoomTip()
      this.learningAnnotationDownloadItems = []
    },
    applyLearningDetailState(detail) {
      const highlights = detail && Array.isArray(detail.highlights) ? detail.highlights : []
      const annotations = detail && Array.isArray(detail.annotations) ? detail.annotations : []
      this.activeLearningTab = 'summary'
      this.learningHighlightDetailOpen = false
      this.activeLearningHighlightId = highlights[0] ? highlights[0].id : ''
      this.activeLearningAnnotationId = annotations[0] ? annotations[0].id : ''
      this.learningAnnotationViewMode = annotations.length > 0 ? 'grid' : this.learningAnnotationViewMode
      this.learningAnnotationPreviewGridOpen = false
      this.learningAnnotationPreviewSelectedIds = []
      this.learningAnnotationPreviewSelectionAnchorId = this.activeLearningAnnotationId
    },
    closeLearningDetailModal() {
      this.closePaperDetail()
      if (this.detailBackRoot) {
        this.$emit('back-root')
      }
    },
    returnToLearningSummary() {
      this.activeLearningTab = 'summary'
      this.learningHighlightDetailOpen = false
    },
    setLearningTab(tab) {
      this.activeLearningTab = tab
      this.learningHighlightDetailOpen = false
      if (tab == 'annotations' && !this.learningAnnotationViewMode) {
        this.learningAnnotationViewMode = 'grid'
      }
      this.updateLearningAnnotationGridColumnCount()
    },
    selectLearningHighlight(highlight) {
      if (highlight && highlight.id) {
        this.activeLearningHighlightId = highlight.id
      }
    },
    openLearningHighlight(highlight) {
      this.selectLearningHighlight(highlight)
      this.learningHighlightDetailOpen = !!highlight
    },
    closeLearningHighlightDetail() {
      this.learningHighlightDetailOpen = false
    },
    setLearningAnnotationViewMode(mode) {
      this.learningAnnotationViewMode = mode == 'list' ? 'list' : 'grid'
      this.updateLearningAnnotationGridColumnCount()
    },
    selectLearningAnnotation(annotation, options = {}) {
      if (annotation && annotation.id) {
        if (!options.force && this.activeLearningTab == 'annotations' && this.learningAnnotationViewMode == 'grid') {
          return
        }
        this.activeLearningAnnotationId = annotation.id
      }
    },
    openLearningAnnotationPreview(annotation) {
      if (!annotation) {
        return
      }
      this.selectLearningAnnotation(annotation, { force: true })
      this.learningAnnotationPreviewGridOpen = false
      this.clearLearningAnnotationPreviewSelection({ preserveCurrent: true })
      this.resetLearningAnnotationPreviewZoom()
      this.clearPreviewZoomTip()
      this.learningAnnotationPreviewOpen = true
      this.stopLearningAnnotationPreviewDrag()
      this.centerLearningAnnotationPreviewScroll()
    },
    closeLearningAnnotationPreview() {
      this.learningAnnotationPreviewOpen = false
      this.learningAnnotationPreviewGridOpen = false
      this.clearLearningAnnotationPreviewSelection({ preserveCurrent: false })
      this.resetLearningAnnotationPreviewZoom()
      this.clearPreviewZoomTip()
      this.stopLearningAnnotationPreviewDrag()
    },
    handleLearningAnnotationPreviewBack() {
      if (this.learningAnnotationPreviewGridOpen) {
        this.learningAnnotationPreviewGridOpen = false
        this.setLearningAnnotationPreviewZoomLevel(this.takePreviewGridZoomLevel('learning-annotation'))
        return
      }
      this.closeLearningAnnotationPreview()
      if (this.detailBackRoot) {
        this.$emit('back-root')
      }
    },
    toggleLearningAnnotationPreviewGrid() {
      if (!this.learningAnnotationPreviewOpen || this.learningAnnotations.length == 0 || this.learningAnnotationPreviewGridOpen) {
        return
      }
      this.activeLearningTab = 'annotations'
      this.learningAnnotationViewMode = 'grid'
      this.selectCurrentLearningAnnotationPreviewInGrid()
      this.rememberPreviewGridZoomLevel('learning-annotation', this.learningAnnotationPreviewZoomLevel)
      this.learningAnnotationPreviewGridOpen = true
      this.clearPreviewZoomTip()
      this.updateLearningAnnotationGridColumnCount()
      this.stopLearningAnnotationPreviewDrag()
    },
    zoomInLearningAnnotationPreview() {
      if (this.learningAnnotationPreviewGridOpen) {
        return
      }
      const nextLevel = this.setLearningAnnotationPreviewZoomLevel(this.learningAnnotationPreviewZoomLevel + 1)
      this.showPreviewZoomTip('learning-annotation', 'in', nextLevel, this.ui)
    },
    zoomOutLearningAnnotationPreview() {
      if (this.learningAnnotationPreviewGridOpen) {
        return
      }
      const nextLevel = this.setLearningAnnotationPreviewZoomLevel(this.learningAnnotationPreviewZoomLevel - 1)
      this.showPreviewZoomTip('learning-annotation', 'out', nextLevel, this.ui)
    },
    resetLearningAnnotationPreviewZoom() {
      this.setLearningAnnotationPreviewZoomLevel(0)
    },
    setLearningAnnotationPreviewZoomLevel(level) {
      const nextLevel = this.clampPreviewZoomLevel(level)
      this.learningAnnotationPreviewZoomLevel = nextLevel
      this.learningAnnotationPreviewZoomValue = this.getLearningAnnotationPreviewZoomValue(nextLevel)
      this.centerLearningAnnotationPreviewScroll()
      return nextLevel
    },
    getLearningAnnotationPreviewZoomValue(level) {
      return this.getPreviewZoomValue(level)
    },
    selectCurrentLearningAnnotationPreviewInGrid() {
      if (!this.activeLearningAnnotationId && this.learningAnnotations[0]) {
        this.activeLearningAnnotationId = this.learningAnnotations[0].id
      }
      if (!this.activeLearningAnnotationId) {
        this.learningAnnotationPreviewSelectedIds = []
        this.learningAnnotationPreviewSelectionAnchorId = ''
        return
      }
      this.learningAnnotationPreviewSelectedIds = []
      this.learningAnnotationPreviewSelectionAnchorId = this.activeLearningAnnotationId
    },
    isLearningAnnotationPreviewSelected(annotationId) {
      return this.learningAnnotationPreviewSelectedIds.includes(annotationId)
    },
    toggleLearningAnnotationPreviewSelection(annotation, event) {
      if (!annotation || !annotation.id) {
        return
      }
      const result = selectItemWithWindowsRange(
        this.learningAnnotations,
        this.learningAnnotationPreviewSelectedIds,
        annotation,
        this.learningAnnotationPreviewSelectionAnchorId || this.activeLearningAnnotationId,
        event
      )
      this.learningAnnotationPreviewSelectedIds = result.selectedIds
      this.learningAnnotationPreviewSelectionAnchorId = result.anchorId
    },
    clearLearningAnnotationPreviewSelection(options = {}) {
      const preserveCurrent = !!options.preserveCurrent
      this.learningAnnotationPreviewSelectedIds = preserveCurrent && this.activeLearningAnnotationId
        ? [this.activeLearningAnnotationId]
        : []
      this.learningAnnotationPreviewSelectionAnchorId = preserveCurrent && this.activeLearningAnnotationId
        ? this.activeLearningAnnotationId
        : ''
    },
    openLearningAnnotationFromPreviewGrid(annotation) {
      if (!annotation || !annotation.id) {
        return
      }
      const isSameAnnotation = annotation.id == this.activeLearningAnnotationId
      const returnZoomLevel = this.takePreviewGridZoomLevel('learning-annotation')
      this.activeLearningAnnotationId = annotation.id
      this.learningAnnotationPreviewGridOpen = false
      if (isSameAnnotation) {
        this.setLearningAnnotationPreviewZoomLevel(returnZoomLevel)
        return
      }
      this.resetLearningAnnotationPreviewZoom()
      this.clearPreviewZoomTip()
    },
    goPrevLearningAnnotationPreview() {
      if (this.learningAnnotationPreviewGridOpen || this.learningAnnotationPreviewIndex <= 0) {
        return
      }
      this.activeLearningAnnotationId = this.learningAnnotations[this.learningAnnotationPreviewIndex - 1].id
      this.clearPreviewZoomTip()
      this.centerLearningAnnotationPreviewScroll()
    },
    goNextLearningAnnotationPreview() {
      if (this.learningAnnotationPreviewGridOpen || this.learningAnnotationPreviewIndex >= this.learningAnnotations.length - 1) {
        return
      }
      this.activeLearningAnnotationId = this.learningAnnotations[this.learningAnnotationPreviewIndex + 1].id
      this.clearPreviewZoomTip()
      this.centerLearningAnnotationPreviewScroll()
    },
    downloadCurrentLearningAnnotationPreview() {
      if (this.learningAnnotationPreviewGridOpen) {
        this.downloadSelectedLearningAnnotations()
        return
      }
      this.startLearningAnnotationPreviewDownload(this.activeLearningAnnotation ? [this.activeLearningAnnotation] : [])
    },
    downloadSelectedLearningAnnotations() {
      this.startLearningAnnotationPreviewDownload(this.learningAnnotationPreviewSelectedAnnotations)
    },
    startLearningAnnotationPreviewDownload(annotations) {
      const list = Array.isArray(annotations) ? annotations.filter(item => item && (item.imageUrl || item.pageImageUrl)) : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u6279\u6ce8')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, { skipSelectionLimit: true })) {
        return
      }
      const task = this.createLearningAnnotationPreviewDownloadTask(list)
      this.$enqueuePaperDownloadTask(task, () => this.runLearningAnnotationPreviewDownloadTask(task))
      if (this.learningAnnotationPreviewGridOpen) {
        this.learningAnnotationPreviewSelectedIds = []
        this.learningAnnotationPreviewSelectionAnchorId = ''
      }
    },
    createLearningAnnotationPreviewDownloadTask(annotations) {
      const list = Array.isArray(annotations) ? annotations.slice() : []
      const baseName = this.$getDownloadBaseName(this.paperDetailTitle, this.ui.annotations, this.sanitizeFileName)
      const isSingle = list.length == 1
      const pageNo = isSingle ? (list[0].pageNo || 1) : 0
      return {
        id: `learning-annotation-preview-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'learning-annotation-preview-pages',
        name: baseName,
        fileName: isSingle ? `${baseName}-\u7b2c${pageNo}\u9875.png` : `${baseName}.zip`,
        annotations: list,
        pngFiles: [],
        failedPageIndex: 0,
        openUrl: '',
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runLearningAnnotationPreviewDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const annotations = Array.isArray(task.annotations) ? task.annotations : []
        if (annotations.length == 0) {
          throw new Error('No annotations')
        }
        const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(annotations, task)
        const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
        for (let i = 0; i < annotations.length; i++) {
          this.ensurePaperDownloadTaskActive(task)
          if (pngFiles[i]) {
            continue
          }
          const annotation = annotations[i]
          this.updatePaperDownloadTask(task, {
            failedPageIndex: i,
            progress: this.getPaperDownloadImageProgress(i, annotations.length, 5, 85, 0),
            statusText: this.ui.downloading
          })
          const blob = await this.fetchPaperPageImageBlob(annotation, percent => {
            this.updatePaperDownloadTask(task, {
              progress: this.getPaperDownloadImageProgress(i, annotations.length, 5, 85, percent),
              statusText: this.ui.downloading
            })
          }, task)
          const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
          const framedBlob = await this.canvasToPngBlob(canvas)
          this.releaseCanvas(canvas)
          pngFiles[i] = {
            fileName: this.getPaperPageFileName(annotation, i, 'png'),
            buffer: await this.blobToArrayBuffer(framedBlob)
          }
          this.updatePaperDownloadTask(task, {
            pngFiles: pngFiles.slice(),
            failedPageIndex: i + 1
          })
        }
        this.ensurePaperDownloadTaskActive(task)
        if (annotations.length == 1 && pngFiles[0]) {
          this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
          this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
        } else {
          const zip = new JSZip()
          pngFiles.forEach((file, index) => {
            if (file) {
              zip.file(file.fileName || this.getPaperPageFileName(annotations[index], index, 'png'), file.buffer)
            }
          })
          this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
          const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
            this.updatePaperDownloadTask(task, {
              progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
              statusText: '\u6253\u5305 ZIP'
            })
          })
          this.ensurePaperDownloadTaskActive(task)
          this.savePaperDownloadBlob(task, zipBlob)
        }
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    openLearningAnnotationDownloadModalWithList(list) {
      const items = Array.isArray(list) ? list.filter(item => item && (item.imageUrl || item.pageImageUrl)) : []
      if (items.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u6279\u6ce8')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1)) {
        return
      }
      this.paperDownloadItems = []
      this.paperDownloadPassword = ''
      this.paperDownloadDetailPages = []
      this.learningAnnotationDownloadItems = items
      this.paperDownloadFormatContext = 'learning-annotations'
      this.paperDownloadFormat = 'png'
      this.paperDownloadFormatModalOpen = true
    },
    centerLearningAnnotationPreviewScroll() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame == 'function') {
          window.requestAnimationFrame(this.applyLearningAnnotationPreviewCenterScroll)
          return
        }
        this.applyLearningAnnotationPreviewCenterScroll()
      })
    },
    applyLearningAnnotationPreviewCenterScroll() {
      const shell = this.$refs.learningAnnotationPreviewShell
      if (!shell) {
        return
      }
      shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2)
      shell.scrollTop = 0
    },
    startLearningAnnotationPreviewDrag(event) {
      const shell = this.$refs.learningAnnotationPreviewShell
      if (this.learningAnnotationPreviewGridOpen || !event || event.button !== 0 || !shell) {
        return
      }
      if (shell.scrollWidth <= shell.clientWidth && shell.scrollHeight <= shell.clientHeight) {
        return
      }
      event.preventDefault()
      this.learningAnnotationPreviewDragState = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: shell.scrollLeft,
        scrollTop: shell.scrollTop
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handleLearningAnnotationPreviewDrag)
        document.addEventListener('mouseup', this.stopLearningAnnotationPreviewDrag)
      }
    },
    handleLearningAnnotationPreviewDrag(event) {
      const shell = this.$refs.learningAnnotationPreviewShell
      const state = this.learningAnnotationPreviewDragState
      if (!shell || !state || !event) {
        return
      }
      shell.scrollLeft = state.scrollLeft - (event.clientX - state.x)
      shell.scrollTop = state.scrollTop - (event.clientY - state.y)
    },
    stopLearningAnnotationPreviewDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handleLearningAnnotationPreviewDrag)
        document.removeEventListener('mouseup', this.stopLearningAnnotationPreviewDrag)
      }
      this.learningAnnotationPreviewDragState = null
    },
    formatLearningPageBadge(item) {
      const pageNo = item && item.pageNo
      const pageLabel = item && item.pageLabel ? String(item.pageLabel) : ''
      const number = Number(pageNo)
      if (Number.isFinite(number) && number > 0) {
        return `P${number}`
      }
      const matched = pageLabel.match(/\d+/)
      return matched ? `P${matched[0]}` : pageLabel
    },
    setPaperDetailProgress(progress, requestId) {
      if (requestId && requestId != this.paperDetailRequestSeq) {
        return
      }
      const value = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)))
      this.paperDetailProgress = Math.max(this.paperDetailProgress || 0, value)
    },
    getPaperDetailCacheKey(item, password = '') {
      const resourceId = item && item.resourceId ? String(item.resourceId) : ''
      if (!resourceId) {
        return ''
      }
      return this.isPaperEncryptedItem(item) ? `${resourceId}:${password || 'encrypted'}` : resourceId
    },
    getPaperDetailSharedCacheScope() {
      return this.$options && this.$options.name ? this.$options.name : 'DailySync'
    },
    getCachedPaperDetail(cacheKey) {
      if (!cacheKey) {
        return null
      }
      const cached = this.paperDetailCache && this.paperDetailCache[cacheKey]
      if (cached && cached.detail) {
        return cached
      }
      const shared = typeof this.getSharedPaperPreviewDetailCache == 'function'
        ? this.getSharedPaperPreviewDetailCache(this.getPaperDetailSharedCacheScope(), cacheKey)
        : null
      if (shared && shared.detail && this.paperDetailCache) {
        this.$set(this.paperDetailCache, cacheKey, shared)
      }
      return shared && shared.detail ? shared : null
    },
    setPaperDetailCache(cacheKey, detail, objectUrls) {
      if (!cacheKey || !detail) {
        return
      }
      const previous = this.paperDetailCache ? this.paperDetailCache[cacheKey] : null
      const entry = typeof this.setSharedPaperPreviewDetailCache == 'function'
        ? this.setSharedPaperPreviewDetailCache(this.getPaperDetailSharedCacheScope(), cacheKey, detail, objectUrls)
        : {
            detail,
            objectUrls: Array.isArray(objectUrls) ? objectUrls.slice() : [],
            cachedAt: Date.now()
          }
      if (previous && previous.objectUrls && previous.objectUrls !== objectUrls && previous !== entry) {
        this.revokePaperPreviewObjectUrls(previous.objectUrls)
      }
      this.$set(this.paperDetailCache, cacheKey, entry)
      this.trimPaperPreviewDetailCache(this.paperDetailCache, cacheKey)
    },
    getPaperDetailErrorMessage(error) {
      return this.getPaperApiErrorMessage(error) || this.ui.detailLoadFailed
    },
    getPaperPasswordErrorMessage() {
      return this.ui.passwordWrong
    },
    fetchPaperDetailResponse(item, password = '') {
      const directDetail = this.createDirectFilePreviewDetail(item)
      if (directDetail) {
        return Promise.resolve({ code: 200, data: directDetail })
      }
      const resourceId = item && item.resourceId ? item.resourceId : ''
      const params = {
        uuid: resourceId
      }
      if (password || this.isPaperEncryptedItem(item)) {
        params.password = password
      }
      return getLearningSyncDetail(params, { skipGlobalErrorMessage: true })
    },
    getPaperApiResponseData(source) {
      const responseData = source && source.response && source.response.data ? source.response.data : null
      return responseData || (source && source.data ? source.data : source)
    },
    getPaperApiCode(source) {
      const data = this.getPaperApiResponseData(source)
      if (!data || typeof data != 'object') {
        return undefined
      }
      return this.pickValue(data, ['code', 'statusCode'])
    },
    isPaperApiCodeFailed(source) {
      const code = this.getPaperApiCode(source)
      if (code === undefined || code === null || code === '') {
        return false
      }
      return code != 200 && code != '200'
    },
    isPaperPasswordCodeError(source) {
      const code = this.getPaperApiCode(source)
      return code == 10013 || code == '10013'
    },
    resolveApiPayload(ret) {
      const data = ret && ret.data ? ret.data : ret
      if (!data) {
        return null
      }
      if (data.code == 200 || data.code == '200') {
        return data.data === undefined ? {} : data.data
      }
      if (data.success === true) {
        return data.data === undefined ? {} : data.data
      }
      if (Array.isArray(data) || data.list || data.records || data.items || data.meta || data.paperMeeting) {
        return data
      }
      return null
    },
    handlePaperListLoadFailed(error) {
      this.resetPaperListPagination()
      this.paperItems = []
      this.clearSelection()
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.showPaperListLoadError(error)
    },
    showPaperListLoadError(error) {
      const message = this.getPaperApiErrorMessage(error) || this.ui.dataLoadFailed.replace('{app}', this.appLabel)
      if (typeof this.$error == 'function') {
        this.$error({
          title: this.ui.notice,
          content: message,
          okButtonProps: {
            style: {
              backgroundColor: '#1f3a2d',
              borderColor: '#1f3a2d'
            }
          }
        })
        return
      }
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    getPaperApiErrorMessage(error) {
      const responseData = error && error.response && error.response.data ? error.response.data : null
      const data = responseData || (error && error.data ? error.data : error)
      if (data && typeof data == 'object') {
        return this.pickString(data, ['msg', 'message', 'errorMessage', 'error'])
      }
      if (error && error.message) {
        return error.message
      }
      return ''
    },
    showPaperDetailRequestError(error) {
      const message = this.getPaperDetailErrorMessage(error)
      if (this.$message && typeof this.$message.error == 'function') {
        this.$message.error(message)
      }
    },
    normalizePaperList(payload) {
      const list = this.extractArray(payload)
      return list.map((item, index) => this.normalizePaperItem(item, index))
    },
    extractArray(payload, depth = 0) {
      if (Array.isArray(payload)) {
        return payload
      }
      if (!payload || typeof payload != 'object' || depth > 4) {
        return []
      }
      const keys = ['list', 'items', 'records', 'rows', 'data', 'resourceList', 'paperList']
      for (let i = 0; i < keys.length; i++) {
        if (Array.isArray(payload[keys[i]])) {
          return payload[keys[i]]
        }
      }
      const values = Object.keys(payload).map(key => payload[key])
      for (let i = 0; i < values.length; i++) {
        const nested = this.extractArray(values[i], depth + 1)
        if (nested.length > 0) {
          return nested
        }
      }
      return []
    },
    normalizePaperItem(raw, index) {
      const item = raw && typeof raw == 'object' ? raw : {}
      const meta = this.parseMeta(this.pickValue(item, ['meta']))
      const paperMeeting = this.resolvePaperMeetingMeta(meta)
      const learningMeta = this.isLearningMode ? this.resolveLearningMeta(meta) : {}
      const learningProgress = learningMeta && typeof learningMeta.readingProgress == 'object' ? learningMeta.readingProgress : {}
      const imagePages = Array.isArray(paperMeeting.imagePages) ? paperMeeting.imagePages : []
      const explicitResourceId = this.pickValue(item, ['resourceId'])
      const fallbackResourceId = this.pickString(item, ['uuid', 'id', 'resourceUuid', 'paperId'])
      const resourceId = explicitResourceId !== undefined && explicitResourceId !== null
        ? String(explicitResourceId).trim()
        : fallbackResourceId
      const resourceType = this.pickValue(item, ['resourceType'])
      const numericResourceType = Number(resourceType)
      const hasNumericResourceType = resourceType !== undefined && resourceType !== null && resourceType !== '' && Number.isFinite(numericResourceType)
      const isFolder = hasNumericResourceType
        ? (this.isLearningMode ? numericResourceType === 0 : (numericResourceType === 0 || numericResourceType === 5))
        : this.pickBoolean(item, ['isFolder', 'folder', 'isDir', 'directory'])
      const isTemplate = hasNumericResourceType
        ? (!this.isLearningMode && numericResourceType === 5)
        : this.pickBoolean(item, ['template', 'isTemplate', 'templateFolder'])
      const name = this.pickString(item, ['name', 'title', 'fileName', 'resourceName', 'paperName', 'bookName'])
        || this.pickString(learningMeta, ['name', 'title', 'fileName', 'resourceName', 'bookName'])
        || paperMeeting.name
        || `\u5f55\u97f3 ${index + 1}`
      const cover = this.pickString(item, ['cover'])
        || this.pickString(item, ['coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
        || this.pickString(learningMeta, ['cover', 'coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
      const gridCover = this.pickPaperCoverImageUrl(item)
        || this.pickPaperCoverImageUrl(learningMeta)
      const filePath = this.pickString(item, ['filePath'])
        || this.pickString(item, ['file_path', 'path', 'fileUrl', 'url', 'src'])
        || this.pickString(paperMeeting, ['filePath', 'file_path', 'path', 'fileUrl', 'url', 'src'])
      const pageCount = this.pickValue(item, ['pageCount', 'pages', 'pageNum', 'imagePageCount', 'totalPage', 'childSize'])
        || this.pickValue(learningProgress, ['totalPages', 'totalPage', 'pageCount'])
      const childSize = this.pickValue(item, ['childSize', 'child_size', 'childrenSize', 'childCount', 'childrenCount'])
      const pathKey = this.getPaperFolderPathKey({ resourceId, resourceType, name, filePath }, index)
      const id = this.getPaperItemKey({ resourceId, resourceType, name, filePath }, index) || resourceId || fallbackResourceId || `paper-${index}`
      const createdAt = this.pickValue(item, ['creationTime', 'createdAt', 'createAt', 'createTime', 'ctime', 'createDate', 'createdDate', 'createdTime', 'creationDate', 'gmtCreate'])
        || this.pickValue(paperMeeting, ['creationTime', 'createdAt', 'createAt', 'createTime', 'ctime', 'createDate', 'createdDate', 'createdTime', 'creationDate', 'gmtCreate'])
        || this.pickValue(learningMeta, ['date', 'creationTime', 'createdAt', 'createTime'])
      const updatedAt = this.pickValue(item, ['lastModifiedTime', 'updatedAt', 'updateAt', 'modifyAt', 'modifyTime', 'modifiedAt', 'modifiedTime', 'mtime', 'updateTime', 'updatedTime', 'updateDate', 'updatedDate', 'lastModified', 'lastUpdateTime', 'gmtModify', 'gmtModified'])
        || this.pickValue(paperMeeting, ['lastModifiedTime', 'updatedAt', 'updateAt', 'modifyAt', 'modifyTime', 'modifiedAt', 'modifiedTime', 'mtime', 'updateTime', 'updatedTime', 'updateDate', 'updatedDate', 'lastModified', 'lastUpdateTime', 'gmtModify', 'gmtModified'])
        || this.pickValue(learningMeta, ['date', 'lastModifiedTime', 'updatedAt', 'updateTime'])
      return {
        id,
        pathKey,
        resourceId,
        resourceType,
        name,
        cover,
        gridCover,
        filePath,
        fileFormat: this.pickString(learningMeta, ['fileFormat', 'format']),
        pageCount: pageCount === undefined || pageCount === null || pageCount === '' ? imagePages.length : pageCount,
        childSize: childSize === undefined || childSize === null || childSize === '' ? pageCount : childSize,
        updatedAt,
        createdAt,
        lastModifiedTime: updatedAt,
        creationTime: createdAt,
        favorite: this.pickBoolean(item, ['favorite', 'isFavorite', 'starred', 'isStar', 'star', 'start']),
        deleted: this.pickBoolean(item, ['deleted', 'isDeleted', 'inTrash', 'isDelete']),
        encrypted: this.pickBoolean(item, ['encrypted', 'isEncrypted', 'locked', 'isLock', 'encrypt', 'encryp']),
        isFolder,
        template: isTemplate,
        openFolder: this.pickBoolean(item, ['openFolder']),
        raw: item,
        meta
      }
    },
    async normalizePaperDetail(payload, sourceItem) {
      if (this.isLearningMode) {
        return await this.normalizeLearningDetail(payload, sourceItem)
      }
      const raw = payload && typeof payload == 'object' ? payload : {}
      const detail = raw.resource && typeof raw.resource == 'object' ? raw.resource : raw
      const sourceMeta = sourceItem && sourceItem.meta ? sourceItem.meta : {}
      const meta = Object.assign({}, sourceMeta, this.parseMeta(this.pickValue(detail, ['meta'])))
      if (detail.paperMeeting && typeof detail.paperMeeting == 'object') {
        meta.paperMeeting = detail.paperMeeting
      }
      const paperMeeting = this.resolvePaperMeetingMeta(meta)
      const fallbackName = sourceItem && sourceItem.name ? sourceItem.name : 'Paper'
      const resourceType = this.pickValue(paperMeeting, ['resourceType'])
        || this.pickValue(meta, ['resourceType'])
        || this.pickValue(detail, ['resourceType'])
      const resourceUrl = this.pickString(paperMeeting, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
        || this.pickString(meta, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
        || this.pickString(detail, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
      const metaPages = Array.isArray(paperMeeting.imagePages)
        ? paperMeeting.imagePages
        : this.extractArray(this.pickValue(paperMeeting, ['imagePages', 'pages', 'pageList']))
      const rawPages = metaPages.length > 0
        ? metaPages
        : this.extractArray(this.pickValue(detail, ['imagePages', 'pages', 'pageList']))
      const allowPageFallback = !(sourceItem && sourceItem.disableDownloadPageFallback) && !(Number(resourceType) === 5 && resourceUrl)
      const imagePages = this.normalizeImagePages(rawPages, sourceItem, allowPageFallback)
      const metaRecordings = Array.isArray(paperMeeting.recordings)
        ? paperMeeting.recordings
        : this.extractArray(this.pickValue(paperMeeting, ['recordings', 'recordingList', 'audios']))
      const rawRecordings = metaRecordings.length > 0
        ? metaRecordings
        : this.extractArray(this.pickValue(detail, ['recordings', 'recordingList', 'audios']))
      return {
        name: this.pickString(paperMeeting, ['name', 'title', 'resourceName', 'paperName']) || this.pickString(detail, ['name', 'title', 'resourceName']) || fallbackName,
        resourceType,
        resourceUrl,
        filePath: this.pickString(detail, ['filePath']) || this.pickString(sourceItem || {}, ['filePath']),
        isDirectFilePreview: !!(detail && detail.isDirectFilePreview) || this.isDirectFilePreviewItem(sourceItem),
        imagePages,
        recordings: this.normalizeRecordings(rawRecordings)
      }
    },
    normalizeImagePages(rawPages, sourceItem, allowFallback = true) {
      const pages = Array.isArray(rawPages) ? rawPages : []
      const normalized = pages.map((page, index) => {
        const item = page && typeof page == 'object' ? page : { imageUrl: page }
        const pageNo = Number(this.pickValue(item, ['pageNo', 'page', 'index', 'no'])) || index + 1
        return {
          pageNo,
          imageUrl: this.pickPaperBestImageUrl(item) || this.pickString(item, ['imageUrl', 'url', 'src', 'cover']),
          pageImageUrl: this.pickPaperPageImageUrl(item)
        }
      }).filter(page => page.imageUrl || page.pageImageUrl || page.pageNo)
        .sort((a, b) => a.pageNo - b.pageNo)
      if (normalized.length > 0 || !allowFallback) {
        return normalized
      }
      const cover = this.pickPaperBestImageUrl(sourceItem, { includeThumbnail: false }) || (sourceItem && sourceItem.cover ? sourceItem.cover : MOCK_PAGE_IMAGE)
      return [{ pageNo: 1, imageUrl: cover }]
    },
    normalizeRecordings(rawRecordings) {
      const recordings = Array.isArray(rawRecordings) ? rawRecordings : []
      return recordings.map((recording, index) => {
        const item = recording && typeof recording == 'object' ? recording : {}
        return {
          id: this.pickString(item, ['id', 'recordingId', 'uuid', 'fileUrl']) || `recording-${index}`,
          recordingName: this.pickString(item, ['recordingName', 'name', 'title', 'fileName']) || `\u5f55\u97f3 ${index + 1}`,
          duration: this.pickValue(item, ['duration', 'durationMs', 'durationMillis']),
          fileSizeBytes: this.pickValue(item, ['fileSizeBytes', 'size', 'fileSize']),
          recordedAtMs: this.pickValue(item, ['recordedAtMs', 'recordedAt', 'createTime', 'createdAt']),
          fileUrl: this.pickString(item, ['fileUrl', 'url', 'src']),
          transcriptTextUrl: this.pickString(item, ['transcriptTextUrl', 'textUrl']),
          transcriptText: this.pickString(item, ['transcriptText', 'text', 'content'])
        }
      })
    },
    async normalizeLearningDetail(payload, sourceItem) {
      const raw = payload && typeof payload == 'object' ? payload : {}
      const detail = raw.resource && typeof raw.resource == 'object' ? raw.resource : raw
      const sourceMeta = sourceItem && sourceItem.meta ? sourceItem.meta : {}
      const detailMeta = await this.parseMetaAsync(this.pickValue(detail, ['meta']), true)
      const meta = Object.assign({}, sourceMeta, detailMeta)
      const learningMeta = this.resolveLearningMeta(meta)
      const directLearningMeta = this.resolveLearningMeta(detail)
      const effectiveLearningMeta = this.isLearningMetaShape(directLearningMeta)
        ? Object.assign({}, learningMeta, directLearningMeta)
        : learningMeta
      const fallbackName = sourceItem && sourceItem.name ? sourceItem.name : 'Learning'
      const bookmarks = this.normalizeLearningBookmarks(this.extractLearningCategoryArray(effectiveLearningMeta, detail, [
        'bookmarks',
        'bookmarkList',
        'bookMarkList',
        'marks',
        'markList'
      ], 'bookmark'))
      const highlights = this.normalizeLearningHighlights(this.extractLearningCategoryArray(effectiveLearningMeta, detail, [
        'highlights',
        'highlightList',
        'highlightNotes',
        'readHighlights'
      ], 'highlight'))
      const annotations = this.normalizeLearningAnnotations(this.extractLearningCategoryArray(effectiveLearningMeta, detail, [
        'readNotes',
        'readNoteList',
        'annotations',
        'annotationList',
        'handwriting',
        'handwritings',
        'handwritten',
        'handwrittenList',
        'handwrite',
        'handwriteList',
        'penNotes',
        'scribbles'
      ], 'annotation'))
      return {
        name: this.pickString(effectiveLearningMeta, ['name', 'title', 'resourceName', 'bookName', 'fileName'])
          || this.pickString(detail, ['name', 'title', 'resourceName', 'bookName', 'fileName'])
          || fallbackName,
        cover: this.pickString(effectiveLearningMeta, ['cover', 'coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
          || this.pickString(detail, ['cover', 'coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl'])
          || this.pickString(sourceItem || {}, ['cover', 'coverUrl', 'imageUrl', 'thumbnail', 'thumbnailUrl']),
        fileSizeBytes: this.pickFirstPresent(
          this.pickValue(effectiveLearningMeta, ['fileSizeBytes', 'fileSize', 'size']),
          this.pickValue(detail, ['fileSizeBytes', 'fileSize', 'size'])
        ),
        fileFormat: this.pickString(effectiveLearningMeta, ['fileFormat', 'format', 'suffix'])
          || this.pickString(detail, ['fileFormat', 'format', 'suffix']),
        syncStatus: this.normalizeLearningSyncStatus(this.pickFirstPresent(
          this.pickValue(effectiveLearningMeta, ['syncStatus']),
          this.pickValue(detail, ['syncStatus'])
        )),
        readingProgress: this.normalizeLearningProgress(this.pickFirstPresent(
          this.pickValue(effectiveLearningMeta, ['readingProgress']),
          this.pickValue(detail, ['readingProgress'])
        )),
        resourceType: this.pickValue(effectiveLearningMeta, ['resourceType'])
          || this.pickValue(meta, ['resourceType'])
          || this.pickValue(detail, ['resourceType']),
        resourceUrl: this.pickString(effectiveLearningMeta, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
          || this.pickString(meta, ['resourceUrl', 'resource_url', 'fileUrl', 'url'])
          || this.pickString(detail, ['resourceUrl', 'resource_url', 'fileUrl', 'url']),
        bookmarks,
        highlights,
        annotations,
        imagePages: annotations
      }
    },
    normalizeLearningProgress(rawProgress) {
      const progress = rawProgress && typeof rawProgress == 'object' ? rawProgress : {}
      const currentPage = this.toFiniteNumber(this.pickValue(progress, ['currentPage', 'pageNo', 'page']))
      const totalPages = this.toFiniteNumber(this.pickValue(progress, ['totalPages', 'totalPage', 'pageCount']))
      const rawPercent = this.pickValue(progress, ['progressPercent', 'percent', 'progress'])
      const hasExplicitPercent = rawPercent !== undefined && rawPercent !== null && rawPercent !== ''
      const explicitPercent = this.toFiniteNumber(rawPercent)
      const derivedPercent = totalPages > 0 ? currentPage / totalPages * 100 : 0
      return {
        currentPage,
        totalPages,
        progressPercent: this.clampPercent(hasExplicitPercent ? explicitPercent : derivedPercent)
      }
    },
    pickFirstPresent(...values) {
      for (let i = 0; i < values.length; i++) {
        const value = values[i]
        if (value !== undefined && value !== null && value !== '') {
          return value
        }
      }
      return undefined
    },
    toFiniteNumber(value, fallback = 0) {
      const number = Number(value)
      return Number.isFinite(number) ? number : fallback
    },
    clampPercent(value) {
      const number = this.toFiniteNumber(value, 0)
      return Math.max(0, Math.min(100, number))
    },
    formatLearningPercent(value) {
      const percent = this.clampPercent(value)
      return Number.isInteger(percent) ? String(percent) : percent.toFixed(1)
    },
    formatLearningFileFormat(value) {
      const text = String(value || '').trim().replace(/^\./, '')
      return text ? text.toUpperCase() : '-'
    },
    normalizeLearningSyncStatus(value) {
      const number = Number(value)
      if (number === 0 || number === 1) {
        return number
      }
      return -1
    },
    isLearningMetaShape(value) {
      if (!value || typeof value != 'object') {
        return false
      }
      return [
        'fileSizeBytes',
        'fileFormat',
        'syncStatus',
        'readingProgress',
        'bookmarks',
        'highlights',
        'readNotes'
      ].some(key => this.pickValue(value, [key]) !== undefined)
    },
    resolveLearningMeta(meta) {
      if (!meta || typeof meta != 'object') {
        return {}
      }
      const nested = this.pickValue(meta, ['learning', 'learngin', 'learn', 'book', 'reader'])
      if (nested && typeof nested == 'object') {
        return nested
      }
      return meta
    },
    extractLearningArray(meta, detail, keys) {
      const direct = this.pickValue(meta, keys)
      const directArray = this.extractArray(direct)
      if (directArray.length > 0) {
        return directArray
      }
      const detailValue = this.pickValue(detail, keys)
      const detailArray = this.extractArray(detailValue)
      if (detailArray.length > 0) {
        return detailArray
      }
      return []
    },
    extractLearningCategoryArray(meta, detail, keys, category) {
      const explicit = this.extractLearningArray(meta, detail, keys)
      if (explicit.length > 0) {
        return explicit
      }
      const candidates = this.collectLearningArrayCandidates(meta).concat(this.collectLearningArrayCandidates(detail))
      const matched = []
      candidates.forEach(list => {
        if (!Array.isArray(list) || list.length == 0) {
          return
        }
        const score = list.reduce((total, item) => total + this.getLearningCategoryScore(item, category), 0)
        if (score > 0 && score >= Math.ceil(list.length / 2)) {
          matched.push(...list)
        }
      })
      return matched
    },
    collectLearningArrayCandidates(source, depth = 0) {
      if (!source || typeof source != 'object' || depth > 3) {
        return []
      }
      if (Array.isArray(source)) {
        return source.some(item => item && typeof item == 'object') ? [source] : []
      }
      return Object.keys(source).reduce((arrays, key) => {
        return arrays.concat(this.collectLearningArrayCandidates(source[key], depth + 1))
      }, [])
    },
    getLearningCategoryScore(raw, category) {
      const item = raw && typeof raw == 'object' ? raw : {}
      if (category == 'annotation') {
        return this.pickValue(item, ['readNoteName', 'imageUrl', 'pageImageUrl', 'annotationImageUrl', 'inkImageUrl']) !== undefined ? 1 : 0
      }
      if (category == 'highlight') {
        return this.pickValue(item, ['highlightName', 'content', 'centent', 'highlightText']) !== undefined ? 1 : 0
      }
      if (category == 'bookmark') {
        const hasBookmarkName = this.pickValue(item, ['bookmarkName', 'bookmarkTitle']) !== undefined
        const hasOtherCategory = this.getLearningCategoryScore(item, 'annotation') > 0 || this.getLearningCategoryScore(item, 'highlight') > 0
        return hasBookmarkName || (!hasOtherCategory && this.pickValue(item, ['pageNo', 'page']) !== undefined) ? 1 : 0
      }
      return 0
    },
    normalizeLearningBookmarks(rawBookmarks) {
      const bookmarks = Array.isArray(rawBookmarks) ? rawBookmarks : []
      return bookmarks.map((bookmark, index) => {
        const item = bookmark && typeof bookmark == 'object' ? bookmark : { title: bookmark }
        const pageNo = this.pickValue(item, ['pageNo', 'page', 'pageIndex', 'index', 'no'])
        return {
          id: this.pickString(item, ['id', 'uuid', 'bookmarkId']) || `learning-bookmark-${index}`,
          title: this.pickString(item, ['bookmarkName', 'title', 'name', 'chapter', 'content', 'text']) || `Bookmark ${index + 1}`,
          pageNo,
          pageLabel: this.getLearningPageLabel(pageNo, index),
          createdAt: this.pickValue(item, ['date', 'createdAt', 'createTime', 'time', 'updatedAt', 'modifyTime']),
          raw: item
        }
      })
    },
    normalizeLearningHighlights(rawHighlights) {
      const highlights = Array.isArray(rawHighlights) ? rawHighlights : []
      return highlights.map((highlight, index) => {
        const item = highlight && typeof highlight == 'object' ? highlight : { content: highlight }
        const pageNo = this.pickValue(item, ['pageNo', 'page', 'pageIndex', 'index', 'no'])
        const content = this.pickString(item, ['content', 'centent', 'text', 'highlightText', 'note', 'description', 'summary'])
        const title = this.pickString(item, ['highlightName', 'title', 'name', 'chapter']) || this.getLearningHighlightTitle(content, index)
        return {
          id: this.pickString(item, ['id', 'uuid', 'highlightId']) || `learning-highlight-${index}`,
          title,
          content: content || title,
          pageNo,
          pageLabel: this.getLearningPageLabel(pageNo, index),
          color: this.pickString(item, ['color', 'highlightColor']),
          createdAt: this.pickValue(item, ['date', 'createdAt', 'createTime', 'time', 'updatedAt', 'modifyTime']),
          raw: item
        }
      })
    },
    normalizeLearningAnnotations(rawAnnotations) {
      const annotations = Array.isArray(rawAnnotations) ? rawAnnotations : []
      return annotations.map((annotation, index) => {
        const item = annotation && typeof annotation == 'object' ? annotation : {}
        const pageNo = this.pickValue(item, ['pageNo', 'page', 'pageIndex', 'index', 'no'])
        const title = this.pickString(item, ['readNoteName', 'title', 'name', 'chapter'])
          || `${this.ui.annotations} ${index + 1}`
        return {
          id: this.pickString(item, ['id', 'uuid', 'annotationId', 'handwritingId']) || `learning-annotation-${index}`,
          title,
          pageNo,
          pageLabel: this.getLearningPageLabel(pageNo, index),
          imageUrl: this.pickPaperBestImageUrl(item) || this.pickString(item, ['imageUrl', 'annotationImageUrl', 'inkImageUrl', 'url', 'src']),
          pageImageUrl: this.pickPaperPageImageUrl(item) || this.pickString(item, ['pageImageUrl', 'backgroundImageUrl', 'baseImageUrl', 'pageUrl', 'pageSrc']),
          createdAt: this.pickValue(item, ['date', 'createdAt', 'createTime', 'time', 'updatedAt', 'modifyTime']),
          raw: item
        }
      }).filter(item => item.imageUrl || item.pageImageUrl || item.title)
    },
    getLearningPageLabel(pageNo, index) {
      const number = Number(pageNo)
      return Number.isFinite(number) && number > 0 ? `Page ${number}` : `Page ${index + 1}`
    },
    getLearningHighlightTitle(content, index) {
      const text = String(content || '').replace(/\s+/g, ' ').trim()
      if (!text) {
        return `${this.ui.highlights} ${index + 1}`
      }
      return text.length > 36 ? `${text.slice(0, 36)}...` : text
    },
    getMockPaperItems() {
      return [
        { id: 'mock-template', resourceId: '', name: '\u6211\u7684\u6a21\u677f', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: true, openFolder: false, template: true },
        { id: 'mock-folder-1', resourceId: '', name: '\u6587\u4ef6\u5939 1', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, isFolder: true, openFolder: true },
        { id: 'mock-folder-2', resourceId: '', name: '\u6587\u4ef6\u5939 2', cover: '', pageCount: 4, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: true, openFolder: false },
        { id: 'mock-paper-1', resourceId: 'mock-paper-1', name: '\u6587\u4ef6\u540d\u79f0\u6587\u4ef6\u540d\u6587\u4ef6\u540d\u79f0\u6587\u4ef6', cover: MOCK_SMALL_PAGE_IMAGE, pageCount: 23, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: false },
        { id: 'mock-paper-2', resourceId: 'mock-paper-2', name: '\u6708\u8ba1\u5212', cover: MOCK_TEMPLATE_IMAGE, pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, isFolder: false },
        { id: 'mock-paper-3', resourceId: 'mock-paper-3', name: '\u6211\u7684\u7b14\u8bb0', cover: '', pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: true, encrypted: true, isFolder: false },
        { id: 'mock-paper-4', resourceId: 'mock-paper-4', name: '\u65e5\u5e38\u8bb0\u5f55', cover: MOCK_PAGE_IMAGE, pageCount: 13, createdAt: '2026/05/14', updatedAt: '2026/05/15', favorite: false, isFolder: false }
      ]
    },
    getMockPaperDetail(item) {
      const name = item && item.name ? item.name : '\u65e5\u5e38\u8bb0\u5f55'
      const pages = []
      for (let i = 1; i <= 23; i++) {
        pages.push({
          pageNo: i,
          imageUrl: i == 1 ? (item && item.cover && !item.encrypted ? item.cover : MOCK_PAGE_IMAGE) : MOCK_PAGE_IMAGE
        })
      }
      return {
        name,
        imagePages: pages,
        recordings: [
          { id: 'mock-recording-1', recordingName: '\u5f55\u97f3\u540d\u79f0', duration: 158000, recordedAtMs: 1780056000000, fileUrl: '', transcriptText: '\u5404\u4f4d\u540c\u4e8b\uff0c\u6211\u4eec\u6765\u5bf9\u4e00\u4e0b\u8fd9\u5468\u7684\u4ea7\u54c1\u8fdb\u5ea6\u3002' },
          { id: 'mock-recording-2', recordingName: '\u5f55\u97f3\u540d\u79f0', duration: 158000, recordedAtMs: 1780056000000, fileUrl: '', transcriptTextUrl: 'mock://recording-2.txt' }
        ]
      }
    },
    parseMeta(value) {
      if (!value) {
        return {}
      }
      if (typeof value == 'object') {
        return value
      }
      const text = String(value).trim()
      if (!text) {
        return {}
      }
      const directMeta = this.parseMetaJson(text)
      if (directMeta) {
        return directMeta
      }
      const decodedText = this.decodeCompressedBase64Text(text)
      if (!decodedText) {
        return {}
      }
      const decodedMeta = this.parseMetaJson(decodedText)
      return decodedMeta || {}
    },
    async parseMetaAsync(value, shouldLogDecoded = false) {
      if (!value) {
        return {}
      }
      if (typeof value == 'object') {
        if (shouldLogDecoded) {
          this.logLearningDecodedMeta(value)
        }
        return value
      }
      const text = String(value).trim()
      if (!text) {
        return {}
      }
      const directMeta = this.parseMetaJson(text)
      if (directMeta) {
        if (shouldLogDecoded) {
          this.logLearningDecodedMeta(directMeta)
        }
        return directMeta
      }
      const bytes = this.decodeBase64Bytes(text)
      if (!bytes || bytes.length == 0) {
        return {}
      }
      if (this.isZipBytes(bytes)) {
        const zipText = await this.decodeZipBytesText(bytes)
        const zipMeta = this.parseMetaJson(zipText)
        if (zipMeta) {
          if (shouldLogDecoded) {
            this.logLearningDecodedMeta(zipMeta)
          }
          return zipMeta
        }
      }
      const decodedText = this.decodeCompressedBytesText(bytes)
      const decodedMeta = this.parseMetaJson(decodedText)
      if (decodedMeta && shouldLogDecoded) {
        this.logLearningDecodedMeta(decodedMeta)
      }
      return decodedMeta || {}
    },
    logLearningDecodedMeta(meta) {
      console.log('[LearningSync] decoded meta:', meta || {})
    },
    parseMetaJson(value) {
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed == 'object' ? parsed : null
      } catch (error) {
        return null
      }
    },
    decodeCompressedBase64Text(value) {
      const bytes = this.decodeBase64Bytes(value)
      return this.decodeCompressedBytesText(bytes)
    },
    decodeCompressedBytesText(bytes) {
      if (!bytes || bytes.length == 0) {
        return ''
      }
      const decoders = [
        input => pako.ungzip(input, { to: 'string' }),
        input => pako.inflate(input, { to: 'string' }),
        input => pako.inflateRaw(input, { to: 'string' }),
        input => this.decodeUtf8Bytes(input)
      ]
      for (let i = 0; i < decoders.length; i++) {
        try {
          const text = decoders[i](bytes)
          if (text && this.parseMetaJson(text)) {
            return text
          }
        } catch (error) {
          // Try the next compression variant.
        }
      }
      return ''
    },
    decodeBase64Bytes(value) {
      try {
        const base64 = String(value || '')
          .trim()
          .replace(/^data:[^,]+,/, '')
          .replace(/\s/g, '')
          .replace(/-/g, '+')
          .replace(/_/g, '/')
        if (!base64) {
          return null
        }
        const padding = base64.length % 4
        const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64
        const binary = typeof window !== 'undefined' && typeof window.atob == 'function'
          ? window.atob(paddedBase64)
          : atob(paddedBase64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        return bytes
      } catch (error) {
        return null
      }
    },
    isZipBytes(bytes) {
      return !!(bytes && bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b)
    },
    async decodeZipBytesText(bytes) {
      try {
        const zip = await JSZip.loadAsync(bytes)
        const files = Object.keys(zip.files)
          .map(name => zip.files[name])
          .filter(file => file && !file.dir)
        const file = files.find(item => /\.json$/i.test(item.name || '')) || files.find(item => /\.(txt|meta)$/i.test(item.name || '')) || files[0]
        return file ? await file.async('string') : ''
      } catch (error) {
        return ''
      }
    },
    decodeUtf8Bytes(bytes) {
      if (typeof TextDecoder !== 'undefined') {
        return new TextDecoder('utf-8').decode(bytes)
      }
      let binary = ''
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return decodeURIComponent(escape(binary))
    },
    resolvePaperMeetingMeta(meta) {
      if (!meta || typeof meta != 'object') {
        return {}
      }
      const nested = this.pickValue(meta, ['paperMeeting', 'paper_meeting', 'paper', 'meeting'])
      if (nested && typeof nested == 'object') {
        return nested
      }
      return meta
    },
    pickValue(source, keys) {
      if (!source || typeof source != 'object') {
        return undefined
      }
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          return source[key]
        }
      }
      const sourceKeys = Object.keys(source)
      for (let i = 0; i < keys.length; i++) {
        const normalized = this.normalizeFieldKey(keys[i])
        const matched = sourceKeys.find(key => this.normalizeFieldKey(key) == normalized)
        if (matched) {
          return source[matched]
        }
      }
      return undefined
    },
    pickString(source, keys) {
      const value = this.pickValue(source, keys)
      if (value === undefined || value === null) {
        return ''
      }
      return String(value).trim()
    },
    pickFirstNonEmptyString(source, keys) {
      if (!source || typeof source != 'object') {
        return ''
      }
      for (let i = 0; i < keys.length; i++) {
        const value = this.pickValue(source, [keys[i]])
        if (value === undefined || value === null) {
          continue
        }
        const normalized = String(value).trim()
        if (normalized) {
          return normalized
        }
      }
      return ''
    },
    pickBoolean(source, keys) {
      const value = this.pickValue(source, keys)
      if (typeof value == 'boolean') {
        return value
      }
      if (value === undefined || value === null || value === '') {
        return false
      }
      const normalized = this.normalizeFieldKey(value)
      return ['true', '1', 'yes', 'y', 'favorite', 'starred', 'deleted', 'folder', 'dir', 'locked', 'encrypted'].includes(normalized)
    },
    isPaperEncryptedItem(item) {
      if (!item) {
        return false
      }
      if (item.encrypted) {
        return true
      }
      const raw = item.raw && typeof item.raw == 'object' ? item.raw : item
      return this.pickBoolean(raw, ['encrypted', 'isEncrypted', 'locked', 'isLock', 'encrypt', 'encryp'])
    },
    normalizeFieldKey(value) {
      return String(value || '').toLowerCase().replace(/[\s_\-/.]/g, '')
    },
    comparePaperItems(a, b) {
      let result = 0
      if (this.sortField == 'name') {
        result = String(a.name || '').localeCompare(String(b.name || ''))
      } else {
        const aValue = this.getSortTime(a, this.sortField == 'createTime' ? 'creationTime' : 'lastModifiedTime')
        const bValue = this.getSortTime(b, this.sortField == 'createTime' ? 'creationTime' : 'lastModifiedTime')
        result = aValue - bValue
      }
      return this.sortDesc ? -result : result
    },
    getSortTime(item, field) {
      const value = item && item[field] ? item[field] : ''
      const timestamp = this.parseDateTime(value)
      return timestamp || 0
    },
    getDisplayDate(item) {
      return item && (item.creationTime || item.createdAt || item.createTime)
    },
    isPaperSelected(item) {
      return !!(item && this.selectedPaperIds.includes(item.id))
    },
    getPaperGridItemClasses(item, index) {
      return {
        folder: item && item.isFolder,
        selected: this.isPaperSelected(item),
        'grid-edge-left': this.isPaperGridLeftEdge(index),
        'grid-edge-right': this.isPaperGridRightEdge(index),
        'grid-edge-top': this.isPaperGridTopEdge(index),
        'grid-edge-bottom': this.isPaperGridBottomEdge(index)
      }
    },
    isLearningAnnotationActive(annotation) {
      if (this.activeLearningTab == 'annotations' && this.learningAnnotationViewMode == 'grid') {
        return false
      }
      return !!(annotation && annotation.id && this.activeLearningAnnotationId && annotation.id == this.activeLearningAnnotationId)
    },
    getLearningAnnotationGridItemClasses(annotation, index) {
      return {
        active: this.isLearningAnnotationActive(annotation),
        'grid-edge-left': this.isLearningAnnotationGridLeftEdge(index),
        'grid-edge-right': this.isLearningAnnotationGridRightEdge(index),
        'grid-edge-top': this.isLearningAnnotationGridTopEdge(index),
        'grid-edge-bottom': this.isLearningAnnotationGridBottomEdge(index)
      }
    },
    isLearningAnnotationGridLeftEdge(index) {
      const columnCount = Math.max(1, Number(this.learningAnnotationGridColumnCount) || 1)
      if (this.isLearningAnnotationGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || index % columnCount == 0
    },
    isLearningAnnotationGridRightEdge(index) {
      const columnCount = Math.max(1, Number(this.learningAnnotationGridColumnCount) || 1)
      if (this.isLearningAnnotationGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || (index + 1) % columnCount == 0
    },
    isLearningAnnotationGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.learningAnnotationGridColumnCount) || 1)
      return index < columnCount
    },
    isLearningAnnotationGridBottomEdge() {
      return false
    },
    isLearningAnnotationGridLastIncompleteRowLastItem(index) {
      const columnCount = Math.max(1, Number(this.learningAnnotationGridColumnCount) || 1)
      const itemCount = this.learningAnnotations.length
      return itemCount > 0 && itemCount % columnCount != 0 && index == itemCount - 1
    },
    updateLearningAnnotationGridColumnCount() {
      if (this.activeLearningTab != 'annotations' || this.learningAnnotationViewMode != 'grid') {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.learningAnnotationGrid
        if (!grid || typeof window === 'undefined') {
          this.learningAnnotationGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.learningAnnotationGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    isLearningAnnotationPreviewGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.learningAnnotationPreviewGridColumnCount) || 1)
      return index < columnCount
    },
    isLearningAnnotationPreviewGridSelectedRowStart(index) {
      const columnCount = Math.max(1, Number(this.learningAnnotationPreviewGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      if (rowStart == 0) {
        return true
      }
      return !this.hasSelectedLearningAnnotationPreviewGridRow(rowStart - columnCount, columnCount)
    },
    isLearningAnnotationPreviewGridSelectionContinuesBelow(index) {
      const columnCount = Math.max(1, Number(this.learningAnnotationPreviewGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      return this.hasSelectedLearningAnnotationPreviewGridRow(rowStart + columnCount, columnCount)
    },
    hasSelectedLearningAnnotationPreviewGridRow(rowStart, columnCount) {
      if (rowStart < 0 || rowStart >= this.learningAnnotations.length) {
        return false
      }
      const rowEnd = Math.min(rowStart + columnCount, this.learningAnnotations.length)
      for (let index = rowStart; index < rowEnd; index += 1) {
        const annotation = this.learningAnnotations[index]
        if (annotation && this.isLearningAnnotationPreviewSelected(annotation.id)) {
          return true
        }
      }
      return false
    },
    updateLearningAnnotationPreviewGridColumnCount() {
      if (!this.learningAnnotationPreviewGridOpen) {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.learningAnnotationPreviewGrid
        if (!grid || typeof window === 'undefined') {
          this.learningAnnotationPreviewGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.learningAnnotationPreviewGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    syncLearningAnnotationPreviewState() {
      const annotationIds = new Set(this.learningAnnotations.map(item => item.id))
      this.learningAnnotationPreviewSelectedIds = this.learningAnnotationPreviewSelectedIds.filter(id => annotationIds.has(id))
      if (this.activeLearningAnnotationId && annotationIds.has(this.activeLearningAnnotationId)) {
        return
      }
      this.activeLearningAnnotationId = this.learningAnnotations[0] ? this.learningAnnotations[0].id : ''
    },
    isPaperGridLeftEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      if (this.isPaperGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || index % columnCount == 0
    },
    isPaperGridRightEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      if (this.isPaperGridLastIncompleteRowLastItem(index)) {
        return false
      }
      return columnCount == 1 || (index + 1) % columnCount == 0
    },
    isPaperGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      return index < columnCount
    },
    isPaperGridBottomEdge() {
      return false
    },
    isPaperGridLastIncompleteRowLastItem(index) {
      const columnCount = Math.max(1, Number(this.paperGridColumnCount) || 1)
      const itemCount = this.visiblePaperItems.length
      return itemCount > 0 && itemCount % columnCount != 0 && index == itemCount - 1
    },
    updatePaperGridColumnCount() {
      if (this.viewMode != 'grid') {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.paperGrid
        if (!grid || typeof window === 'undefined') {
          this.paperGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.paperGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    isPaperPagesGridTopEdge(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      return index < columnCount
    },
    isPaperPagesGridSelectedRowStart(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      if (rowStart == 0) {
        return true
      }
      return !this.hasSelectedPaperPagesGridRow(rowStart - columnCount, columnCount)
    },
    isPaperPagesGridSelectionContinuesBelow(index) {
      const columnCount = Math.max(1, Number(this.paperPagesGridColumnCount) || 1)
      const rowStart = Math.floor(index / columnCount) * columnCount
      return this.hasSelectedPaperPagesGridRow(rowStart + columnCount, columnCount)
    },
    hasSelectedPaperPagesGridRow(rowStart, columnCount) {
      if (rowStart < 0 || rowStart >= this.detailPages.length) {
        return false
      }
      const rowEnd = Math.min(rowStart + columnCount, this.detailPages.length)
      for (let index = rowStart; index < rowEnd; index += 1) {
        const page = this.detailPages[index]
        if (page && this.isDetailPageSelected(page.pageNo)) {
          return true
        }
      }
      return false
    },
    updatePaperPagesGridColumnCount() {
      if (!this.pageGridOpen) {
        return
      }
      this.$nextTick(() => {
        const grid = this.$refs.paperPagesGrid
        if (!grid || typeof window === 'undefined') {
          this.paperPagesGridColumnCount = 1
          return
        }
        const styles = window.getComputedStyle(grid)
        const columns = String(styles.gridTemplateColumns || '')
          .split(/\s+/)
          .filter(column => column && column != 'none')
        this.paperPagesGridColumnCount = Math.max(1, columns.length || 1)
      })
    },
    handlePaperWindowResize() {
      this.updatePaperGridColumnCount()
      this.updatePaperPagesGridColumnCount()
      this.updateLearningAnnotationGridColumnCount()
      this.updateLearningAnnotationPreviewGridColumnCount()
      this.updateDailyGridColumnCount()
      this.updateDailyPreviewGridColumnCount()
      this.resolveOpenDailyFilterMenuPlacement()
      this.syncSelectionBarScrollbarOffset()
      this.selectionBarOffsetY = this.clampSelectionBarOffset(this.selectionBarOffsetY)
      this.dailySelectionBarOffsetY = this.clampDailySelectionBarOffset(this.dailySelectionBarOffsetY)
      if (this.detailMode && !this.pageGridOpen) {
        this.centerPaperPreviewScroll()
      }
      if (this.learningAnnotationPreviewOpen && !this.learningAnnotationPreviewGridOpen) {
        this.centerLearningAnnotationPreviewScroll()
      }
      if (this.dailyPreviewOpen && !this.dailyPreviewGridOpen) {
        this.centerDailyPreviewScroll()
      }
    },
    syncSelectionBarScrollbarOffset() {
      this.$nextTick(() => {
        const area = this.$refs.paperListArea
        const nextOffset = area ? Math.max(0, area.offsetWidth - area.clientWidth) : 0
        if (this.selectionBarScrollbarOffset != nextOffset) {
          this.selectionBarScrollbarOffset = nextOffset
        }
      })
    },
    startSelectionBarDrag(event) {
      if (!event || event.button !== 0) {
        return
      }
      if (event.target && event.target.closest && event.target.closest('button')) {
        return
      }
      event.preventDefault()
      this.selectionBarDragging = true
      this.selectionBarDragState = {
        startY: event.clientY,
        startOffset: this.selectionBarOffsetY
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handleSelectionBarDrag)
        document.addEventListener('mouseup', this.stopSelectionBarDrag)
      }
    },
    handleSelectionBarDrag(event) {
      if (!this.selectionBarDragState || !event) {
        return
      }
      const nextOffset = this.selectionBarDragState.startOffset + event.clientY - this.selectionBarDragState.startY
      this.selectionBarOffsetY = this.clampSelectionBarOffset(nextOffset)
    },
    stopSelectionBarDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handleSelectionBarDrag)
        document.removeEventListener('mouseup', this.stopSelectionBarDrag)
      }
      this.selectionBarDragging = false
      this.selectionBarDragState = null
    },
    clampSelectionBarOffset(value) {
      const number = Number(value) || 0
      const maxOffset = this.getSelectionBarMaxOffset()
      return Math.min(Math.max(0, number), maxOffset)
    },
    getSelectionBarMaxOffset() {
      const workspace = this.$refs.paperWorkspace
      const layer = this.$refs.paperSelectionLayer
      if (!workspace || !layer) {
        return 0
      }
      const layerTop = layer.offsetTop || 0
      const layerHeight = layer.offsetHeight || 0
      return Math.max(0, workspace.clientHeight - layerTop - layerHeight)
    },
    resetSelectionBarOffset() {
      this.stopSelectionBarDrag()
      this.selectionBarOffsetY = 0
    },
    startDailySelectionBarDrag(event) {
      if (!event || event.button !== 0) {
        return
      }
      if (event.target && event.target.closest && event.target.closest('button')) {
        return
      }
      event.preventDefault()
      this.dailySelectionBarDragging = true
      this.dailySelectionBarDragState = {
        startY: event.clientY,
        startOffset: this.dailySelectionBarOffsetY
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handleDailySelectionBarDrag)
        document.addEventListener('mouseup', this.stopDailySelectionBarDrag)
      }
    },
    handleDailySelectionBarDrag(event) {
      if (!this.dailySelectionBarDragState || !event) {
        return
      }
      const nextOffset = this.dailySelectionBarDragState.startOffset + event.clientY - this.dailySelectionBarDragState.startY
      this.dailySelectionBarOffsetY = this.clampDailySelectionBarOffset(nextOffset)
    },
    stopDailySelectionBarDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handleDailySelectionBarDrag)
        document.removeEventListener('mouseup', this.stopDailySelectionBarDrag)
      }
      this.dailySelectionBarDragging = false
      this.dailySelectionBarDragState = null
    },
    clampDailySelectionBarOffset(value) {
      const number = Number(value) || 0
      const maxOffset = this.getDailySelectionBarMaxOffset()
      return Math.min(Math.max(0, number), maxOffset)
    },
    getDailySelectionBarMaxOffset() {
      const workspace = this.$refs.paperWorkspace
      const layer = this.$refs.dailySelectionLayer
      if (!workspace || !layer) {
        return 0
      }
      const layerTop = layer.offsetTop || 0
      const layerHeight = layer.offsetHeight || 0
      return Math.max(0, workspace.clientHeight - layerTop - layerHeight)
    },
    resetDailySelectionBarOffset() {
      this.stopDailySelectionBarDrag()
      this.dailySelectionBarOffsetY = 0
    },
    syncSelectionWithItems() {
      if (this.selectedPaperIds.length == 0) {
        this.selectedPaperId = ''
        this.selectedPaperAnchorId = ''
        return
      }
      const existingIds = new Set(this.paperItems.map(item => item.id))
      this.selectedPaperIds = this.selectedPaperIds.filter(id => existingIds.has(id))
      this.selectedPaperId = this.selectedPaperIds[0] || ''
      if (!existingIds.has(this.selectedPaperAnchorId)) {
        this.selectedPaperAnchorId = this.selectedPaperId
      }
    },
    clearSelection() {
      this.clearPaperItemClickTimer()
      this.selectedPaperIds = []
      this.selectedPaperId = ''
      this.selectedPaperAnchorId = ''
      this.moreMenuId = ''
      this.resetSelectionBarOffset()
    },
    selectPaperItem(item, event) {
      if (!item) {
        return
      }
      const hadSelection = this.selectedPaperIds.length > 0
      const result = selectItemWithWindowsRange(
        this.visiblePaperItems,
        this.selectedPaperIds,
        item,
        this.selectedPaperAnchorId,
        event
      )
      const selectedIds = result.selectedIds

      this.selectedPaperIds = selectedIds
      this.selectedPaperId = selectedIds[0] || ''
      this.selectedPaperAnchorId = result.anchorId
      if (!hadSelection || selectedIds.length == 0) {
        this.resetSelectionBarOffset()
      }
      this.moreMenuId = ''
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.searchOpen = false
    },
    handlePaperItemClick(item, event) {
      if (!item) {
        return
      }
      if (event && event.detail > 1) {
        this.clearPaperItemClickTimer()
        return
      }
      this.clearPaperItemClickTimer()
      const clickEvent = event
        ? { ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey }
        : null
      this.paperItemClickTimer = setTimeout(() => {
        this.paperItemClickTimer = null
        this.selectPaperItem(item, clickEvent)
      }, 260)
    },
    handlePaperItemDblClick(item, event) {
      this.clearPaperItemClickTimer()
      if (event && typeof event.preventDefault == 'function') {
        event.preventDefault()
      }
      if (!this.canOpenPaperResourceFolder(item)) {
        this.openPaperItem(item)
        return
      }
      this.openPaperResourceFolder(item)
    },
    handlePaperItemKeyup(item) {
      if (!item) {
        return
      }
      this.clearPaperItemClickTimer()
      this.selectPaperItem(item)
    },
    clearPaperItemClickTimer() {
      if (this.paperItemClickTimer) {
        clearTimeout(this.paperItemClickTimer)
        this.paperItemClickTimer = null
      }
    },
    canOpenPaperResourceFolder(item) {
      if (!item || !item.resourceId) {
        return false
      }
      const resourceType = Number(item.resourceType)
      if (this.isLearningMode) {
        return resourceType === 0 || !!item.isFolder
      }
      return resourceType === 0 || resourceType === 5
    },
    isPaperDownloadableItem(item) {
      if (!item || !item.resourceId) {
        return false
      }
      if (!this.isLearningMode && this.isNonDownloadResourceId(item.resourceId)) {
        return false
      }
      const resourceType = Number(item.resourceType)
      if (Number.isFinite(resourceType) && (this.isLearningMode ? resourceType === 0 : (resourceType === 0 || resourceType === 5))) {
        return false
      }
      return !item.isFolder && !item.template
    },
    isNonDownloadResourceId(resourceId) {
      const value = Number(resourceId)
      return Number.isFinite(value) && (value === 0 || value === 5)
    },
    resetPaperListTransientState() {
      this.clearSelection()
      this.moreMenuId = ''
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.searchOpen = false
      this.searchKeyword = ''
    },
    openPaperResourceFolder(item) {
      this.resetPaperListTransientState()
      const resourceId = item.resourceId || ''
      this.paperListResourceId = resourceId
      const folder = this.normalizePaperFolderPathItem({
        name: item.name || this.appLabel,
        resourceId,
        resourceType: item.resourceType,
        filePath: item.filePath,
        pathKey: item.pathKey,
        template: item.template === true
      }, this.paperFolderPath.length)
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const existedIndex = folder ? folderPath.findIndex(crumb => crumb.pathKey == folder.pathKey) : -1
      if (existedIndex >= 0) {
        this.paperFolderPath = folderPath.slice(0, existedIndex + 1)
      } else {
        this.paperFolderPath = folder ? folderPath.concat(folder) : folderPath
      }
      this.loadPaperList()
    },
    openPaperBreadcrumb(index) {
      const folderPath = this.normalizeCloudPaperFolderPath(this.paperFolderPath)
      const breadcrumbs = [{ name: this.appLabel, resourceId: '', pathKey: 'root' }].concat(folderPath)
      const target = breadcrumbs[index]
      if (!target || index == breadcrumbs.length - 1) {
        return
      }
      this.resetPaperListTransientState()
      this.paperListResourceId = target.resourceId || ''
      this.paperFolderPath = index <= 0 ? [] : folderPath.slice(0, index)
      this.loadPaperList()
    },
    parseDateTime(value) {
      if (value instanceof Date) {
        return value.getTime()
      }
      const number = Number(value)
      if (Number.isFinite(number) && number > 0) {
        return number < 1000000000000 ? number * 1000 : number
      }
      const text = String(value || '').trim()
      if (!text) {
        return 0
      }
      const direct = Date.parse(text)
      if (Number.isFinite(direct)) {
        return direct
      }
      const normalized = Date.parse(text.replace(/-/g, '/'))
      return Number.isFinite(normalized) ? normalized : 0
    },
    selectFilter(key) {
      if (this.paperFilter == key) {
        this.filterMenuOpen = false
        this.moreMenuId = ''
        return
      }
      this.clearSelection()
      this.paperFilter = key
      this.filterMenuOpen = false
      this.moreMenuId = ''
      this.loadPaperList()
    },
    selectSort(key) {
      if (this.sortField == key) {
        this.sortMenuOpen = false
        this.moreMenuId = ''
        return
      }
      this.sortField = key
      this.sortMenuOpen = false
      this.moreMenuId = ''
      this.loadPaperList()
    },
    toggleSortOrder() {
      this.sortDesc = !this.sortDesc
      this.moreMenuId = ''
      this.loadPaperList()
    },
    setViewMode(mode) {
      this.viewMode = mode
      this.moreMenuId = ''
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen
      this.$nextTick(() => {
        if (this.searchOpen && this.$refs.paperSearchInput) {
          this.$refs.paperSearchInput.focus()
        }
      })
    },
    closeFloatingMenus() {
      this.filterMenuOpen = false
      this.sortMenuOpen = false
      this.moreMenuId = ''
      this.dailyPeriodMenuOpen = false
      this.dailyFinishMenuOpen = false
      this.dailyPeriodMenuDropLeft = false
      this.dailyFinishMenuDropLeft = false
    },
    toggleMoreMenu(id) {
      this.moreMenuId = this.moreMenuId == id ? '' : id
    },
    handleSelectionAction(type) {
      this.performPaperAction(type, this.selectedPaperItems)
    },
    handleItemMenuAction(type, item) {
      this.performPaperAction(type, item ? [item] : [])
    },
    canPerformPaperAction(type, items) {
      const list = Array.isArray(items) ? items : []
      if (list.length == 0) {
        return false
      }
      if (type == 'download') {
        return list.every(item => this.isPaperDownloadableItem(item))
      }
      if (type == 'move' || type == 'delete') {
        return true
      }
      if (list.length != 1) {
        return false
      }
      const item = list[0]
      if (type == 'encrypt') {
        return !item.encrypted
      }
      if (type == 'decrypt') {
        return !!item.encrypted
      }
      if (type == 'unfavorite') {
        return !!item.favorite
      }
      return type == 'rename' || type == 'favorite'
    },
    performPaperAction(type, items) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (!this.canPerformPaperAction(type, list)) {
        return
      }
      this.moreMenuId = ''

      if (type == 'download') {
        if (!this.$canStartFileDownloadBatch(list.length)) {
          return
        }
        const encryptedItem = list.find(item => this.isPaperEncryptedItem(item))
        if (encryptedItem) {
          this.openPaperPasswordModal(encryptedItem, '', false, 'download', list)
          return
        }
        this.openPaperDownloadFormatModal(list)
        return
      }
      if (type == 'favorite') {
        this.toggleFavorite(list[0])
        return
      }
      if (type == 'unfavorite') {
        this.$set(list[0], 'favorite', false)
        if (this.paperFilter == 'favorite') {
          this.clearSelection()
        }
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'encrypt') {
        this.$set(list[0], 'encrypted', true)
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'decrypt') {
        this.$set(list[0], 'encrypted', false)
        this.$message.info(this.ui.fakeTip)
        return
      }
      if (type == 'delete') {
        list.forEach(item => {
          this.$set(item, 'deleted', true)
        })
        this.clearSelection()
        this.$message.info(this.ui.fakeTip)
        return
      }
      this.fakeAction()
    },
    openPaperDownloadFormatModal(items, options = {}) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (list.length == 0) {
        this.$message.info(`\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684 ${this.appLabel} \u6587\u4ef6`)
        return
      }
      const hasUnsupportedItem = list.some(item => !this.isPaperDownloadableItem(item))
      if (hasUnsupportedItem) {
        this.$message.warning(`\u6587\u4ef6\u5939\u548c\u6a21\u677f\u4e0d\u652f\u6301\u4e0b\u8f7d\uff0c\u8bf7\u9009\u62e9 ${this.appLabel} \u6587\u4ef6`)
        return
      }
      if (!this.$canStartFileDownloadBatch(list.length)) {
        return
      }
      this.paperDownloadPassword = options.password || ''
      this.paperDownloadItems = list
      this.paperDownloadDetailPages = []
      this.learningAnnotationDownloadItems = []
      this.dailyDownloadItems = []
      this.dailyDownloadClearSelectionOnStart = false
      this.dailyDownloadSkipSelectionLimit = false
      this.dailyDownloadBaseName = ''
      this.paperDownloadFormatContext = 'items'
      this.paperDownloadFormat = 'pdf'
      this.paperDownloadFormatModalOpen = true
    },
    openDetailPagesDownloadFormatModal(pages) {
      const list = Array.isArray(pages) ? pages.filter(page => page && (page.imageUrl || page.pageImageUrl)) : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, { skipSelectionLimit: true })) {
        return
      }
      this.paperDownloadItems = []
      this.paperDownloadDetailPages = list
      this.learningAnnotationDownloadItems = []
      this.dailyDownloadItems = []
      this.dailyDownloadClearSelectionOnStart = false
      this.dailyDownloadSkipSelectionLimit = false
      this.dailyDownloadBaseName = ''
      this.paperDownloadFormatContext = 'detail-pages'
      this.paperDownloadFormat = 'png'
      this.paperDownloadFormatModalOpen = true
    },
    closePaperDownloadFormatModal() {
      this.paperDownloadFormatModalOpen = false
      this.paperDownloadItems = []
      this.paperDownloadPassword = ''
      this.paperDownloadDetailPages = []
      this.learningAnnotationDownloadItems = []
      this.dailyDownloadItems = []
      this.dailyDownloadClearSelectionOnStart = false
      this.dailyDownloadSkipSelectionLimit = false
      this.dailyDownloadBaseName = ''
      this.paperDownloadFormatContext = 'items'
    },
    confirmPaperDownloadFormat() {
      const format = this.paperDownloadFormat == 'png' ? 'png' : 'pdf'
      if (this.paperDownloadFormatContext == 'detail-pages') {
        const pages = this.paperDownloadDetailPages.slice()
        if (!this.$canStartDownloadBatch(pages.length, 1, { skipSelectionLimit: true })) {
          return
        }
        this.closePaperDownloadFormatModal()
        this.startDetailPagesDownload(pages, format)
        return
      }
      if (this.paperDownloadFormatContext == 'daily-detail-pages') {
        const pages = this.paperDownloadDetailPages.slice()
        const baseName = this.dailyDownloadBaseName || this.dailyPreviewTitle || 'Daily'
        const clearDailySelectionOnStart = this.dailyDownloadClearSelectionOnStart
        const downloadOptions = this.dailyDownloadSkipSelectionLimit ? { skipSelectionLimit: true } : {}
        if (!this.$canStartDownloadBatch(pages.length, 1, downloadOptions)) {
          return
        }
        this.closePaperDownloadFormatModal()
        this.startDailyDetailPagesDownload(pages, baseName, format, downloadOptions)
        if (clearDailySelectionOnStart) {
          this.clearDailySelection()
        }
        return
      }
      if (this.paperDownloadFormatContext == 'daily-pages') {
        const items = this.dailyDownloadItems.slice()
        const baseName = this.dailyDownloadBaseName || this.dailyPreviewTitle || 'Daily'
        const downloadOptions = this.dailyDownloadSkipSelectionLimit ? { skipSelectionLimit: true } : {}
        if (!this.$canStartDownloadBatch(items.length, 1, downloadOptions)) {
          return
        }
        this.closePaperDownloadFormatModal()
        this.startDailyItemsDownload(items, baseName, format, downloadOptions)
        return
      }
      if (this.paperDownloadFormatContext == 'daily-groups') {
        const groups = this.dailyDownloadItems.slice()
        const clearDailySelectionOnStart = this.dailyDownloadClearSelectionOnStart
        const downloadOptions = this.dailyDownloadSkipSelectionLimit ? { skipSelectionLimit: true } : {}
        if (!this.$canStartFileDownloadBatch(groups.length, downloadOptions)) {
          return
        }
        this.closePaperDownloadFormatModal()
        this.startDailyGroupDownloads(groups, format, downloadOptions)
        if (clearDailySelectionOnStart) {
          this.clearDailySelection()
        }
        return
      }
      if (this.paperDownloadFormatContext == 'learning-annotations') {
        const annotations = this.learningAnnotationDownloadItems.slice()
        if (!this.$canStartDownloadBatch(annotations.length)) {
          return
        }
        this.closePaperDownloadFormatModal()
        annotations.forEach(annotation => {
          const task = this.createLearningAnnotationDownloadTask(annotation, format)
          this.$enqueuePaperDownloadTask(task, () => this.runLearningAnnotationDownloadTask(task, annotation, format))
        })
        return
      }
      const items = this.paperDownloadItems.slice()
      if (!this.$canStartFileDownloadBatch(items.length)) {
        return
      }
      const password = this.paperDownloadPassword || ''
      this.closePaperDownloadFormatModal()
      this.enqueuePaperDownloadItems(items, format, password)
      this.clearSelectionAfterPaperDownloadStart(items)
    },
    shouldClearSelectionAfterPaperDownloadStart(items) {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (list.length == 0 || this.selectedPaperIds.length != list.length) {
        return false
      }
      const selectedIds = new Set(this.selectedPaperIds)
      return list.every(item => item && selectedIds.has(item.id))
    },
    clearSelectionAfterPaperDownloadStart(items) {
      if (this.shouldClearSelectionAfterPaperDownloadStart(items)) {
        this.clearSelection()
      }
    },
    enqueuePaperDownloadItems(items, format, password = '') {
      const list = Array.isArray(items) ? items.filter(Boolean) : []
      if (this.$shouldBundleDownloadItems(list)) {
        const task = this.$createDownloadBundleTask(list, format, password, {
          idPrefix: 'daily-bundle-download',
          kind: 'paper-bundle',
          fallbackName: this.appLabel,
          sanitizeFileName: this.sanitizeFileName
        })
        this.$enqueuePaperDownloadTask(task, () => this.runPaperDownloadBundleTask(task))
        return
      }
      list.forEach(item => {
        const task = this.createPaperDownloadTask(item, format, password)
        this.$enqueuePaperDownloadTask(task, () => this.runPaperDownloadTask(task, item, format))
      })
    },
    runPaperDownloadBundleTask(task) {
      this.$runDownloadBundleTask(task, {
        items: task.sourceItems,
        format: task.format,
        password: task.password || '',
        kind: 'paper-bundle',
        fallbackName: this.appLabel,
        sanitizeFileName: this.sanitizeFileName,
        createItemTask: (item, format, password) => this.createPaperDownloadTask(item, format, password),
        runItemTask: (bundleTask, item, format) => this.runPaperDownloadTask(bundleTask, item, format)
      })
    },
    createPaperDownloadTask(item, format, password = '') {
      const baseName = this.$getDownloadBaseName(item && item.name, this.appLabel, this.sanitizeFileName)
      const extension = this.$getPaperDownloadTaskExtension(item, format)
      return {
        id: `paper-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: baseName,
        fileName: `${baseName}.${extension}`,
        format,
        sourceItem: item,
        password: this.isPaperEncryptedItem(item) ? (password || '') : '',
        openUrl: '',
        pages: [],
        pngFiles: [],
        pdfPages: [],
        failedPageIndex: 0,
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    openLearningAnnotationDownloadModal(annotation) {
      const list = annotation
        ? [annotation]
        : (this.activeLearningAnnotation ? [this.activeLearningAnnotation] : [])
      this.openLearningAnnotationDownloadModalWithList(list)
    },
    createLearningAnnotationDownloadTask(annotation, format) {
      const baseName = this.$getDownloadBaseName(annotation && annotation.title, this.ui.annotations, this.sanitizeFileName)
      return {
        id: `learning-annotation-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'learning-annotation',
        name: baseName,
        fileName: `${baseName}.${format == 'pdf' ? 'pdf' : 'png'}`,
        format,
        sourceAnnotation: annotation,
        openUrl: '',
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runLearningAnnotationDownloadTask(task, annotation, format) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, {
          state: 'running',
          progress: Math.max(task.progress || 0, 5),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperPageImageBlob(annotation, percent => {
          this.updatePaperDownloadTask(task, {
            progress: Math.max(5, Math.min(85, Number(percent) || 0)),
            statusText: this.ui.downloading
          })
        }, task)
        this.ensurePaperDownloadTaskActive(task)
        const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize([annotation], task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        if (format == 'pdf') {
          this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u751f\u6210 PDF' })
          const pdfPage = await this.canvasToJpegPage(canvas)
          this.releaseCanvas(canvas)
          this.savePaperDownloadBlob(task, this.buildPdfBlob([pdfPage]))
        } else {
          const framedBlob = await this.canvasToPngBlob(canvas)
          this.releaseCanvas(canvas)
          this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
          this.savePaperDownloadBlob(task, framedBlob)
        }
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    updatePaperDownloadTask(task, patch) {
      patch = typeof this.$normalizePaperDownloadTaskPatch == 'function' ? this.$normalizePaperDownloadTaskPatch(task, patch) : patch
      Object.keys(patch || {}).forEach(key => {
        this.$set(task, key, patch[key])
      })
    },
    async runPaperDownloadTask(task, item, format) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, { state: 'running', progress: Math.max(task.progress || 0, 5), statusText: '\u83b7\u53d6\u8be6\u60c5' })
        const detail = task.detail || await this.fetchPaperDownloadDetail(item, task.password || '')
        const pages = task.pages && task.pages.length > 0 ? task.pages : this.getPaperDetailPagesForDownload(detail)
        if (!this.isLearningMode && this.isPaperPdfResourceDetail(detail)) {
          const compositePages = task.pages && task.pages.length > 0 ? task.pages : this.getPaperDetailPagesForComposite(detail)
          this.updatePaperDownloadTask(task, { detail, pages: compositePages, progress: Math.max(task.progress || 0, 10) })
          await this.downloadPaperPdfResource(task, detail, format)
        } else {
          if (pages.length == 0) {
            throw new Error('No pages')
          }
          this.updatePaperDownloadTask(task, { detail, pages, progress: Math.max(task.progress || 0, 10) })
          if (format == 'png') {
            await this.downloadPaperAsPngZip(task, pages)
          } else {
            await this.downloadPaperAsPdf(task, pages)
          }
        }
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    retryPaperDownloadTask(task) {
      if (!task || task.state != 'error') {
        return
      }
      this.paperDownloadListVisible = true
      this.$set(task, 'cancelled', false)
      this.$set(task, 'abortController', null)
      this.updatePaperDownloadTask(task, {
        progress: task.progress || 0,
        state: 'running',
        statusText: this.ui.continueDownload
      })
      if (task.kind == 'paper-bundle') {
        this.runPaperDownloadBundleTask(task)
        return
      }
      if (task.kind == 'daily-group-bundle') {
        this.runDailyGroupDownloadBundleTask(task)
        return
      }
      if (task.kind == 'daily-group') {
        this.runDailyGroupDownloadTask(task, task.sourceDailyGroup, task.format)
        return
      }
      if (task.kind == 'detail-pages') {
        this.runDetailPagesDownloadTask(task)
        return
      }
      if (task.kind == 'daily-detail-pages') {
        this.runDailyDetailPagesDownloadTask(task)
        return
      }
      if (task.kind == 'recording-file') {
        this.runRecordingDownloadTask(task)
        return
      }
      if (task.kind == 'learning-annotation') {
        this.runLearningAnnotationDownloadTask(task, task.sourceAnnotation, task.format)
        return
      }
      if (task.kind == 'learning-annotation-preview-pages') {
        this.runLearningAnnotationPreviewDownloadTask(task)
        return
      }
      if (task.kind == 'daily-pages') {
        this.runDailyItemsDownloadTask(task)
        return
      }
      this.runPaperDownloadTask(task, task.sourceItem, task.format)
    },
    getPaperDownloadNativeOpener() {
      if (typeof window === 'undefined') {
        return null
      }
      return window.viwoodsNative || window.electronAPI || window.nativeBridge || null
    },
    canOpenPaperDownloadTask(task) {
      if (!task || task.state != 'done') {
        return false
      }
      const nativeOpener = this.getPaperDownloadNativeOpener()
      if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
        return true
      }
      return !!(nativeOpener && typeof nativeOpener.openDownloadFolder == 'function')
    },
    openPaperDownloadTask(task) {
      if (!this.canOpenPaperDownloadTask(task)) {
        return
      }
      const nativeOpener = this.getPaperDownloadNativeOpener()
      if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
        nativeOpener.showItemInFolder(task.filePath)
        return
      }
      if (nativeOpener && typeof nativeOpener.openDownloadFolder == 'function') {
        nativeOpener.openDownloadFolder()
        return
      }
    },
    removePaperDownloadTask(task) {
      if (!task) {
        return
      }
      this.$set(task, 'cancelled', true)
      if (task.abortController && typeof task.abortController.abort == 'function') {
        task.abortController.abort()
      }
      this.revokePaperDownloadTaskUrl(task)
      this.paperDownloadTasks = this.paperDownloadTasks.filter(item => item.id != task.id)
      if (this.paperDownloadTasks.length == 0) {
        this.paperDownloadListVisible = false
      }
    },
    closePaperDownloadList() {
      this.clearPaperDownloadTasks()
    },
    clearPaperDownloadTasks() {
      this.paperDownloadTasks.forEach(task => {
        this.$set(task, 'cancelled', true)
        if (task.abortController && typeof task.abortController.abort == 'function') {
          task.abortController.abort()
        }
        this.revokePaperDownloadTaskUrl(task)
      })
      this.paperDownloadTasks = []
      this.paperDownloadListVisible = false
    },
    async fetchPaperDownloadDetail(item, password = '') {
      const ret = await this.fetchPaperDetailResponse(item, this.isPaperEncryptedItem(item) ? password : '')
      const payload = this.resolveApiPayload(ret)
      if (payload === null) {
        throw new Error(`${this.appLabel} detail failed`)
      }
      return await this.normalizePaperDetail(payload, item)
    },
    revokePaperPreviewObjectUrls(urls) {
      const list = Array.isArray(urls) ? urls : []
      if (typeof this.revokePaperPreviewObjectUrlsSafely == 'function') {
        this.revokePaperPreviewObjectUrlsSafely(list)
        return
      }
      list.forEach(url => {
        if (url && String(url).startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
    },
    clearPaperPreviewObjectUrls(options = {}) {
      if (options.revoke !== false) {
        this.revokePaperPreviewObjectUrls(this.paperPreviewObjectUrls)
      }
      this.paperPreviewObjectUrls = []
    },
    clearPaperDetailCache() {
      const cache = this.paperDetailCache || {}
      Object.keys(cache).forEach(key => {
        this.revokePaperPreviewObjectUrls(cache[key] && cache[key].objectUrls)
      })
      this.paperDetailCache = {}
    },
    async preparePaperPreviewDetail(detail, onProgress) {
      const objectUrls = []
      const reportProgress = progress => {
        if (typeof onProgress == 'function') {
          onProgress(progress)
        }
      }
      const revokePreparedUrls = () => {
        this.revokePaperPreviewObjectUrls(objectUrls)
      }
      reportProgress(30)
      if (this.canUseExternalPaperPdfPreview(detail)) {
        reportProgress(100)
        return {
          detail: this.createExternalPaperPdfPreviewDetail(detail),
          objectUrls
        }
      }
      if (this.isLearningMode) {
        reportProgress(100)
        return { detail, objectUrls }
      }
      if (!this.isPaperPdfResourceDetail(detail)) {
        reportProgress(100)
        return { detail, objectUrls }
      }
      let pdfDocument = null
      try {
        const pdfBlob = await this.fetchPaperBinaryBlob(detail.resourceUrl, percent => {
          reportProgress(30 + (Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.2))
        })
        reportProgress(52)
        pdfDocument = await this.loadPaperPdfDocumentFromBlob(pdfBlob)
        reportProgress(56)
        const pages = this.getPaperPdfCompositePages(detail, pdfDocument.numPages)
        const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, null, percent => {
          reportProgress(56 + Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.04)
        })
        const imagePages = await this.buildRenderedPaperPreviewPages({
          pages,
          objectUrls,
          onProgress: reportProgress,
          startProgress: 56,
          endProgress: 96,
          renderPage: async (page, index, total, progress) => {
            const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, page, index, total, null, null, null, progress, compositeFrameSize)
            try {
              return await this.canvasToPngBlob(canvas)
            } finally {
              this.releaseCanvas(canvas)
            }
          }
        })
        reportProgress(100)
        return { detail: Object.assign({}, detail, { imagePages }), objectUrls }
      } catch (error) {
        revokePreparedUrls()
        throw error
      } finally {
        if (pdfDocument && typeof pdfDocument.destroy == 'function') {
          await pdfDocument.destroy()
        }
      }
    },
    canUseExternalPaperPdfPreview(detail) {
      return !!(!this.isDirectFilePreviewDetail(detail) && this.isPaperPdfResourceDetail(detail) && this.getExternalPaperPdfPreviewUrl(detail.resourceUrl))
    },
    getExternalPaperPdfPreviewUrl(url) {
      return String(url || '').trim()
    },
    createExternalPaperPdfPreviewDetail(detail) {
      const previewUrl = this.getExternalPaperPdfPreviewUrl(detail && detail.resourceUrl)
      return Object.assign({}, detail, {
        pdfPreviewUrl: previewUrl,
        imagePages: [{
          pageNo: 1,
          imageUrl: '',
          pdfPreviewUrl: previewUrl,
          isPdfPreview: true
        }]
      })
    },
    getPaperDetailPagesForDownload(detail) {
      if (this.isLearningMode && detail && Array.isArray(detail.annotations)) {
        return detail.annotations
          .filter(page => page && (page.imageUrl || page.pageImageUrl))
          .slice()
          .sort((a, b) => Number(a.pageNo) - Number(b.pageNo))
      }
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      return pages
        .filter(page => page && (page.imageUrl || page.pageImageUrl))
        .slice()
        .sort((a, b) => Number(a.pageNo) - Number(b.pageNo))
    },
    getPaperDetailPagesForComposite(detail) {
      const pages = detail && Array.isArray(detail.imagePages) ? detail.imagePages : []
      return pages
        .filter(page => page && page.pageNo)
        .slice()
        .sort((a, b) => Number(a.pageNo) - Number(b.pageNo))
    },
    isPaperPdfResourceDetail(detail) {
      if (!detail || !detail.resourceUrl) {
        return false
      }
      if (this.isDirectFilePreviewDetail(detail)) {
        return this.isPaperPdfUrl(detail.resourceUrl)
      }
      if (Number(detail.resourceType) !== 5) {
        return false
      }
      return this.isPaperPdfUrl(detail.resourceUrl)
    },
    isPaperPdfUrl(url) {
      const text = String(url || '').toLowerCase()
      if (!text) {
        return false
      }
      return this.getUrlExtension(text, '') == 'pdf' || text.includes('.pdf')
    },
    getPaperPdfCompositePages(detail, pageCount) {
      const metaPages = this.getPaperDetailPagesForComposite(detail)
      const pagesByNo = {}
      let maxMetaPageNo = 0
      metaPages.forEach(page => {
        const pageNo = Number(page.pageNo)
        if (pageNo > 0) {
          pagesByNo[pageNo] = page
          maxMetaPageNo = Math.max(maxMetaPageNo, pageNo)
        }
      })
      const totalPageCount = Math.max(Number(pageCount) || 0, metaPages.length, maxMetaPageNo)
      const pages = []
      for (let i = 1; i <= totalPageCount; i++) {
        pages.push(Object.assign({ pageNo: i, imageUrl: '' }, pagesByNo[i] || {}))
      }
      return pages
    },
    ensurePaperDownloadTaskActive(task) {
      const isInList = !!(task && this.paperDownloadTasks.some(item => item.id == task.id))
      if (!task || task.cancelled || !isInList) {
        const error = new Error('Download cancelled')
        error.isPaperDownloadCancelled = true
        throw error
      }
    },
    getPaperDownloadImageProgress(index, total, start, end, percent) {
      const safeTotal = Math.max(total || 1, 1)
      const safePercent = Math.max(0, Math.min(Number(percent) || 0, 100))
      return Math.min(end, Math.round(start + ((index + safePercent / 100) / safeTotal) * (end - start)))
    },
    async downloadPaperPdfResource(task, detail, format) {
      if (format == 'png') {
        await this.downloadPaperPdfResourceAsPngZip(task, detail)
        return
      }
      await this.downloadPaperPdfResourceAsPdf(task, detail)
    },
    async getPaperPdfDocument(task, detail) {
      if (task.pdfDocument) {
        return task.pdfDocument
      }
      this.updatePaperDownloadTask(task, {
        progress: Math.max(task.progress || 0, 10),
        statusText: this.ui.downloading
      })
      const pdfBlob = await this.fetchPaperBinaryBlob(detail.resourceUrl, percent => {
        this.updatePaperDownloadTask(task, {
          progress: this.getPaperDownloadImageProgress(0, 1, 10, 25, percent),
          statusText: this.ui.downloading
        })
      }, task)
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 28, statusText: '\u89e3\u6790 PDF' })
      const pdfDocument = await this.loadPaperPdfDocumentFromBlob(pdfBlob)
      task.pdfDocument = pdfDocument
      task.pdfPageCount = pdfDocument.numPages
      return pdfDocument
    },
    async loadPaperPdfDocumentFromBlob(pdfBlob) {
      const bytes = new Uint8Array(await this.blobToArrayBuffer(pdfBlob))
      const loadingTask = pdfjsLib.getDocument({
        data: bytes
      })
      return loadingTask.promise
    },
    async downloadPaperPdfResourceAsPdf(task, detail) {
      const renderedPreviewPages = this.getPaperDetailPagesForDownload(detail)
      if (this.areRenderedPaperPreviewPages(renderedPreviewPages)) {
        await this.downloadPaperAsPdf(task, renderedPreviewPages)
        return
      }
      const pdfDocument = await this.getPaperPdfDocument(task, detail)
      const pages = this.getPaperPdfCompositePages(detail, pdfDocument.numPages)
      this.$updatePaperDownloadTaskFileNameForPages(task, 'pdf', pages)
      const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, task)
      const imagePages = Array.isArray(task.pdfPages) ? task.pdfPages.slice() : []
      this.updatePaperDownloadTask(task, { pages, pdfPages: imagePages })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (imagePages[i]) {
          continue
        }
        const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, pages[i], i, pages.length, task, 30, 85, null, compositeFrameSize)
        imagePages[i] = await this.canvasToJpegPage(canvas)
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pdfPages: imagePages.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u751f\u6210 PDF' })
      const pdfBlob = this.buildPdfBlob(imagePages)
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    async downloadPaperPdfResourceAsPngZip(task, detail) {
      const renderedPreviewPages = this.getPaperDetailPagesForDownload(detail)
      if (this.areRenderedPaperPreviewPages(renderedPreviewPages)) {
        await this.downloadPaperAsPngZip(task, renderedPreviewPages)
        return
      }
      const pdfDocument = await this.getPaperPdfDocument(task, detail)
      const pages = this.getPaperPdfCompositePages(detail, pdfDocument.numPages)
      const compositeFrameSize = await this.getUnifiedPaperPdfCompositeFrameSize(pdfDocument, pages, task)
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      this.updatePaperDownloadTask(task, { pages, pngFiles })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const canvas = await this.renderPaperPdfCompositeCanvas(pdfDocument, pages[i], i, pages.length, task, 30, 85, null, compositeFrameSize)
        const blob = await this.canvasToPngBlob(canvas)
        pngFiles[i] = {
          fileName: this.getPaperPageFileName(pages[i], i, 'png'),
          buffer: await this.blobToArrayBuffer(blob)
        }
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      if (pngFiles.length == 1 && pngFiles[0]) {
        this.updatePaperDownloadTask(task, {
          fileName: `${task.name}.png`,
          progress: 99,
          statusText: this.ui.savingFile
        })
        this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
        return
      }
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    async renderPaperPdfCompositeCanvas(pdfDocument, pageInfo, index, total, task, startProgress, endProgress, onProgress, frameSize) {
      const reportCompositeProgress = progress => {
        if (typeof onProgress == 'function') {
          onProgress(Math.max(0, Math.min(Number(progress) || 0, 100)))
        }
      }
      if (task) {
        this.updatePaperDownloadTask(task, {
          failedPageIndex: index,
          progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 0),
          statusText: this.ui.downloading
        })
      }
      reportCompositeProgress(5)
      let overlayBlob = null
      let overlaySize = null
      const canRenderPdfInParallel = !!(pageInfo && pageInfo.imageUrl) && this.hasPaperPreviewFrameSize(frameSize)
      let pdfCanvasError = null
      const pdfCanvasPromise = canRenderPdfInParallel
        ? this.renderPaperPdfPageCanvas(
          pdfDocument,
          Number(pageInfo.pageNo) || index + 1,
          frameSize,
          !task
        ).catch(error => {
          pdfCanvasError = error
          return null
        })
        : null
      if (pageInfo && pageInfo.imageUrl) {
        try {
          overlayBlob = await this.fetchPaperImageBlob(pageInfo.imageUrl, percent => {
            if (task) {
              this.updatePaperDownloadTask(task, {
                progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, percent),
                statusText: this.ui.downloading
              })
            }
            reportCompositeProgress(10 + Math.max(0, Math.min(Number(percent) || 0, 100)) * 0.45)
          }, task)
          overlaySize = await this.getPaperImageBlobSize(overlayBlob)
          reportCompositeProgress(55)
        } catch (error) {
          this.releasePaperPreviewCanvasWhenReady(pdfCanvasPromise)
          throw error
        }
      }
      const targetFrameSize = frameSize || overlaySize
      const outputScale = this.getPaperPreviewFrameScale(targetFrameSize, !task)
      const canvas = pdfCanvasPromise
        ? await pdfCanvasPromise
        : await this.renderPaperPdfPageCanvas(
          pdfDocument,
          Number(pageInfo.pageNo) || index + 1,
          targetFrameSize,
          !task
        )
      if (pdfCanvasError) {
        throw pdfCanvasError
      }
      this.fitPaperCompositeCanvasToFrame(canvas, targetFrameSize, outputScale, { allowHeightFallback: true })
      reportCompositeProgress(overlayBlob ? 80 : 90)
      if (overlayBlob) {
        await this.drawPaperOverlayImage(canvas, overlayBlob)
        reportCompositeProgress(95)
      }
      if (task) {
        this.updatePaperDownloadTask(task, {
          progress: this.getPaperDownloadImageProgress(index, total, startProgress, endProgress, 100),
          statusText: this.ui.downloading
        })
      }
      reportCompositeProgress(100)
      return canvas
    },
    async renderPaperPdfPageCanvas(pdfDocument, pageNo, targetSize, includeDisplayScale = true) {
      if (!pdfDocument || !pdfDocument.numPages) {
        return this.createBlankPaperPdfCanvas(null, targetSize)
      }
      if (pageNo > pdfDocument.numPages) {
        return this.createBlankPaperPdfCanvas(pdfDocument, targetSize)
      }
      const page = await pdfDocument.getPage(pageNo)
      const baseViewport = page.getViewport({ scale: 1 })
      const renderScale = this.getPaperPreviewRenderScale(baseViewport, targetSize, { includeDisplayScale })
      const viewport = page.getViewport({ scale: renderScale })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)
      const context = canvas.getContext('2d')
      if (context && 'imageSmoothingEnabled' in context) {
        context.imageSmoothingEnabled = true
        if ('imageSmoothingQuality' in context) {
          context.imageSmoothingQuality = 'high'
        }
      }
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: context, viewport }).promise
      if (typeof page.cleanup == 'function') {
        page.cleanup()
      }
      return canvas
    },
    async createBlankPaperPdfCanvas(pdfDocument, targetSize) {
      let width = Math.round(Number(targetSize && targetSize.width) || 0)
      let height = Math.round(Number(targetSize && targetSize.height) || 0)
      if (!(width > 0 && height > 0) && pdfDocument && pdfDocument.numPages) {
        try {
          const page = await pdfDocument.getPage(1)
          const baseViewport = page.getViewport({ scale: 1 })
          const renderScale = this.getPaperPreviewRenderScale(baseViewport, targetSize)
          const viewport = page.getViewport({ scale: renderScale })
          width = Math.ceil(viewport.width)
          height = Math.ceil(viewport.height)
          if (typeof page.cleanup == 'function') {
            page.cleanup()
          }
        } catch (error) {
          // Keep the default A4-like page size.
        }
      }
      if (!(width > 0 && height > 0)) {
        width = 1240
        height = 1754
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext('2d')
      context.fillStyle = '#fff'
      context.fillRect(0, 0, width, height)
      return canvas
    },
    async getPaperImageBlobSize(blob) {
      const bitmapSize = await this.getPreviewPerformanceImageBlobSize(blob)
      if (bitmapSize) {
        return bitmapSize
      }
      try {
        const image = await this.loadImageFromBlob(blob)
        const width = image.naturalWidth || image.width || 0
        const height = image.naturalHeight || image.height || 0
        return width > 0 && height > 0 ? { width, height } : null
      } catch (error) {
        return null
      }
    },
    drawPaperOverlayImage(canvas, blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const context = canvas.getContext('2d')
          if (context && 'imageSmoothingEnabled' in context) {
            context.imageSmoothingEnabled = true
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'high'
            }
          }
          context.drawImage(image, 0, 0, canvas.width, canvas.height)
          URL.revokeObjectURL(url)
          resolve()
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Overlay image load failed'))
        }
        image.src = url
      })
    },
    canvasToJpegPage(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(async blob => {
          if (!blob) {
            reject(new Error('Canvas convert failed'))
            return
          }
          resolve({
            width: canvas.width,
            height: canvas.height,
            data: new Uint8Array(await this.blobToArrayBuffer(blob))
          })
        }, 'image/jpeg', 1)
      })
    },
    canvasToPngBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (!blob) {
            reject(new Error('Canvas convert failed'))
            return
          }
          resolve(blob)
        }, 'image/png')
      })
    },
    releaseCanvas(canvas) {
      if (canvas) {
        canvas.width = 0
        canvas.height = 0
      }
    },
    async downloadPaperAsPngZip(task, pages) {
      this.$updatePaperDownloadTaskFileNameForPages(task, 'png', pages)
      const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(pages, task)
      if (pages.length == 1) {
        const page = pages[0]
        this.updatePaperDownloadTask(task, {
          fileName: `${task.name}.png`,
          progress: Math.max(task.progress || 0, 10),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperPageImageBlob(page, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(0, 1, 10, 95, percent),
            statusText: this.ui.downloading
          })
        }, task)
        this.ensurePaperDownloadTaskActive(task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        const framedBlob = await this.canvasToPngBlob(canvas)
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
        this.savePaperDownloadBlob(task, framedBlob)
        return
      }
      const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
      this.updatePaperDownloadTask(task, { pngFiles })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (pngFiles[i]) {
          continue
        }
        const page = pages[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 80, 0),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperPageImageBlob(page, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 80, percent),
            statusText: this.ui.downloading
          })
        }, task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        const framedBlob = await this.canvasToPngBlob(canvas)
        this.releaseCanvas(canvas)
        pngFiles[i] = {
          fileName: this.getPaperPageFileName(page, i, 'png'),
          buffer: await this.blobToArrayBuffer(framedBlob)
        }
        this.updatePaperDownloadTask(task, {
          pngFiles: pngFiles.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      const zip = new JSZip()
      pngFiles.forEach((file, index) => {
        if (file) {
          zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
        }
      })
      this.updatePaperDownloadTask(task, { progress: 85, statusText: '\u6253\u5305 ZIP' })
      const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
        this.updatePaperDownloadTask(task, {
          progress: Math.min(98, Math.round(85 + (metadata.percent || 0) * 0.13)),
          statusText: '\u6253\u5305 ZIP'
        })
      })
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, zipBlob)
    },
    async downloadPaperAsPdf(task, pages) {
      const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(pages, task)
      const imagePages = Array.isArray(task.pdfPages) ? task.pdfPages.slice() : []
      this.updatePaperDownloadTask(task, { pdfPages: imagePages })
      for (let i = 0; i < pages.length; i++) {
        this.ensurePaperDownloadTaskActive(task)
        if (imagePages[i]) {
          continue
        }
        const page = pages[i]
        this.updatePaperDownloadTask(task, {
          failedPageIndex: i,
          progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 75, 0),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperPageImageBlob(page, percent => {
          this.updatePaperDownloadTask(task, {
            progress: this.getPaperDownloadImageProgress(i, pages.length, 10, 75, percent),
            statusText: this.ui.downloading
          })
        }, task)
        const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
        imagePages[i] = await this.canvasToJpegPage(canvas)
        this.releaseCanvas(canvas)
        this.updatePaperDownloadTask(task, {
          pdfPages: imagePages.slice(),
          failedPageIndex: i + 1
        })
      }
      this.ensurePaperDownloadTaskActive(task)
      this.updatePaperDownloadTask(task, { progress: 85, statusText: '\u751f\u6210 PDF' })
      const pdfBlob = this.buildPdfBlob(imagePages)
      this.ensurePaperDownloadTaskActive(task)
      this.savePaperDownloadBlob(task, pdfBlob)
    },
    savePaperDownloadBlob(task, blob) {
      if (this.$captureDownloadBlob(task, blob, task && task.fileName)) {
        return
      }
      this.revokePaperDownloadTaskUrl(task)
      const openUrl = URL.createObjectURL(blob)
      this.updatePaperDownloadTask(task, { openUrl, progress: Math.max(task.progress || 0, 99) })
      saveAs(blob, task.fileName)
    },
    revokePaperDownloadTaskUrl(task) {
      if (task && task.openUrl && String(task.openUrl).startsWith('blob:')) {
        URL.revokeObjectURL(task.openUrl)
      }
      if (task) {
        this.$set(task, 'openUrl', '')
      }
    },
    async fetchPaperPageImageBlob(page, onProgress, task) {
      const pageInfo = page || {}
      const imageUrl = pageInfo.imageUrl ? String(pageInfo.imageUrl).trim() : ''
      const pageImageUrl = pageInfo.pageImageUrl ? String(pageInfo.pageImageUrl).trim() : ''
      if (!pageImageUrl) {
        return this.fetchPaperImageBlob(imageUrl, onProgress, task)
      }
      if (!imageUrl) {
        return this.fetchPaperImageBlob(pageImageUrl, onProgress, task)
      }
      const report = value => {
        if (typeof onProgress == 'function') {
          onProgress(Math.max(0, Math.min(100, Math.round(Number(value) || 0))))
        }
      }
      const baseBlob = await this.fetchPaperImageBlob(pageImageUrl, percent => {
        report((Number(percent) || 0) * 0.55)
      }, task)
      const overlayBlob = await this.fetchPaperImageBlob(imageUrl, percent => {
        report(55 + (Number(percent) || 0) * 0.4)
      }, task)
      const canvas = await this.composeLearningAnnotationCanvas(baseBlob, overlayBlob, task && task.compositeFrameSize)
      if (task && !task.compositeFrameSize) {
        this.$set(task, 'compositeFrameSize', { width: canvas.width, height: canvas.height })
      }
      try {
        const blob = await this.canvasToPngBlob(canvas)
        report(100)
        return blob
      } finally {
        this.releaseCanvas(canvas)
      }
    },
    composeLearningAnnotationCanvas(baseBlob, overlayBlob, targetSize) {
      return new Promise((resolve, reject) => {
        Promise.all([
          this.loadImageFromBlob(baseBlob),
          this.loadImageFromBlob(overlayBlob)
        ]).then(([baseImage, overlayImage]) => {
          const baseWidth = Math.max(1, Math.round(baseImage.naturalWidth || baseImage.width || 0))
          const baseHeight = Math.max(1, Math.round(baseImage.naturalHeight || baseImage.height || 0))
          const overlayWidth = Math.max(1, Math.round(overlayImage.naturalWidth || overlayImage.width || 0))
          const overlayHeight = Math.max(1, Math.round(overlayImage.naturalHeight || overlayImage.height || 0))
          const useOverlaySize = overlayWidth * overlayHeight > baseWidth * baseHeight
          const fallbackWidth = useOverlaySize ? overlayWidth : baseWidth
          const fallbackHeight = useOverlaySize ? overlayHeight : baseHeight
          const width = Math.max(1, Math.round(Number(targetSize && targetSize.width) || fallbackWidth))
          const height = Math.max(1, Math.round(Number(targetSize && targetSize.height) || fallbackHeight))
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const context = canvas.getContext('2d')
          if (context && 'imageSmoothingEnabled' in context) {
            context.imageSmoothingEnabled = true
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'high'
            }
          }
          context.fillStyle = '#fff'
          context.fillRect(0, 0, width, height)
          context.drawImage(baseImage, 0, 0, width, height)
          context.drawImage(overlayImage, 0, 0, width, height)
          resolve(canvas)
        }).catch(reject)
      })
    },
    loadImageFromBlob(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          URL.revokeObjectURL(url)
          resolve(image)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load failed'))
        }
        image.src = url
      })
    },
    async fetchPaperBinaryBlob(url, onProgress, task) {
      return this.fetchCachedPreviewPerformanceBlob(url, async progressHandler => {
      const requestUrls = this.getPaperImageFetchUrls(url)
      let lastError = null
      for (const requestUrl of requestUrls) {
        let controller = null
        try {
          if (task) {
            this.ensurePaperDownloadTaskActive(task)
          }
          if (typeof AbortController !== 'undefined') {
            controller = new AbortController()
            if (task) {
              this.registerPreviewPerformanceTaskAbortController(task, controller)
            }
          }
          const response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
          if (!response.ok) {
            throw new Error(`File failed: ${response.status}`)
          }
          if (this.isPaperUnexpectedProxyResponse(response, requestUrl, url)) {
            throw new Error('File proxy returned unexpected content')
          }
          return await this.readPaperImageResponseBlob(response, progressHandler)
        } catch (error) {
          if (error && (error.name == 'AbortError' || task && task.cancelled)) {
            const cancelError = new Error('Download cancelled')
            cancelError.isPaperDownloadCancelled = true
            throw cancelError
          }
          error.isCorsLikeDownloadError = true
          lastError = error
        } finally {
          if (task) {
            this.unregisterPreviewPerformanceTaskAbortController(task, controller)
          }
        }
      }
      throw lastError || new Error('File download failed')
      }, { onProgress, task })
    },
    async fetchPaperImageBlob(url, onProgress, task) {
      return this.fetchCachedPreviewPerformanceBlob(url, async progressHandler => {
      if (this.getPaperImageFetchUrls) {
        const requestUrls = this.getPaperImageFetchUrls(url)
        let lastError = null
        for (const requestUrl of requestUrls) {
        let controller = null
        try {
            if (task) {
              this.ensurePaperDownloadTaskActive(task)
            }
            if (typeof AbortController !== 'undefined') {
              controller = new AbortController()
              if (task) {
                this.registerPreviewPerformanceTaskAbortController(task, controller)
              }
            }
            const response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
            if (!response.ok) {
              const error = new Error(`Image failed: ${response.status}`)
              error.isCorsLikeDownloadError = requestUrl != url
              throw error
            }
            const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
            const normalizedContentType = String(contentType).toLowerCase()
            const isImageResponse = normalizedContentType.includes('image/') || normalizedContentType.includes('octet-stream')
            if (contentType && !isImageResponse) {
              const error = new Error('Image proxy returned non-image content')
              error.isCorsLikeDownloadError = true
              throw error
            }
            const blob = await this.readPaperImageResponseBlob(response, progressHandler)
            if (task) {
              this.ensurePaperDownloadTaskActive(task)
            }
            const imageMimeType = this.getPaperImageMimeType(url)
            if (imageMimeType && !String(blob.type || '').toLowerCase().includes('image/')) {
              return new Blob([blob], { type: imageMimeType })
            }
            return blob
          } catch (error) {
            if (error && (error.name == 'AbortError' || task && task.cancelled)) {
              const cancelError = new Error('Download cancelled')
              cancelError.isPaperDownloadCancelled = true
              throw cancelError
            }
            error.isCorsLikeDownloadError = true
            lastError = error
          } finally {
            if (task) {
              this.unregisterPreviewPerformanceTaskAbortController(task, controller)
            }
          }
        }
        throw lastError || new Error('Image download failed')
      }
      const requestUrl = this.getPaperImageFetchUrl(url)
      let response
      let controller = null
      try {
        if (task) {
          this.ensurePaperDownloadTaskActive(task)
        }
        if (typeof AbortController !== 'undefined') {
          controller = new AbortController()
          if (task) {
            this.registerPreviewPerformanceTaskAbortController(task, controller)
          }
        }
        response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined)
      } catch (error) {
        if (error && (error.name == 'AbortError' || task && task.cancelled)) {
          const cancelError = new Error('Download cancelled')
          cancelError.isPaperDownloadCancelled = true
          throw cancelError
        }
        error.isCorsLikeDownloadError = true
        throw error
      } finally {
        if (task) {
          this.unregisterPreviewPerformanceTaskAbortController(task, controller)
        }
      }
      if (!response.ok) {
        throw new Error(`Image failed: ${response.status}`)
      }
      const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
      if (contentType && !String(contentType).toLowerCase().includes('image/')) {
        const error = new Error('\u56fe\u7247\u4ee3\u7406\u672a\u751f\u6548\uff0c\u8fd4\u56de\u5185\u5bb9\u4e0d\u662f\u56fe\u7247')
        error.isCorsLikeDownloadError = true
        throw error
      }
      const blob = await response.blob()
      if (progressHandler) {
        progressHandler(100)
      }
      return blob
      }, { onProgress, task })
    },
    async readPaperImageResponseBlob(response, onProgress) {
      const contentType = response.headers && response.headers.get ? response.headers.get('content-type') : ''
      const contentLength = Number(response.headers && response.headers.get ? response.headers.get('content-length') : 0)
      if (!response.body || typeof response.body.getReader != 'function' || !contentLength) {
        const blob = await response.blob()
        if (onProgress) {
          onProgress(100)
        }
        return blob
      }
      const reader = response.body.getReader()
      const chunks = []
      let receivedLength = 0
      let result = await reader.read()
      while (!result.done) {
        chunks.push(result.value)
        receivedLength += result.value.length
        if (onProgress) {
          onProgress(Math.min(100, Math.round((receivedLength / contentLength) * 100)))
        }
        result = await reader.read()
      }
      if (onProgress) {
        onProgress(100)
      }
      return new Blob(chunks, { type: contentType || 'application/octet-stream' })
    },
    getPaperImageFetchUrl(url) {
      return url || ''
    },
    getPaperImageFetchUrls(url) {
      return url ? [url] : []
    },
    getPaperProxyFetchUrl(url) {
      return url || ''
    },
    getPaperProxyBaseUrl() {
      return ''
    },
    canUsePaperImageProxy() {
      return false
    },
    isPaperUnexpectedProxyResponse() {
      return false
    },
    getPaperImageMimeType(url) {
      const extension = this.getUrlExtension(url, '').toLowerCase()
      if (extension == 'jpg' || extension == 'jpeg') {
        return 'image/jpeg'
      }
      if (extension == 'png') {
        return 'image/png'
      }
      if (extension == 'webp') {
        return 'image/webp'
      }
      if (extension == 'gif') {
        return 'image/gif'
      }
      if (extension == 'bmp') {
        return 'image/bmp'
      }
      return ''
    },
    isLikelyCorsDownloadError(error) {
      return !!(error && (error.isCorsLikeDownloadError || error.name == 'TypeError' || String(error.message || '').toLowerCase().includes('failed to fetch')))
    },
    downloadExternalUrl(url, fileName) {
      if (!url) {
        return
      }
      const link = document.createElement('a')
      link.href = url
      link.download = this.sanitizeFileName(fileName) || ''
      link.target = '_blank'
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    getPaperPageFileName(page, index, extension) {
      const pageNo = page && page.pageNo ? page.pageNo : index + 1
      return `${pageNo}.${extension}`
    },
    blobToArrayBuffer(blob) {
      if (blob && typeof blob.arrayBuffer == 'function') {
        return blob.arrayBuffer()
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsArrayBuffer(blob)
      })
    },
    imageBlobToJpegPage(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const image = new Image()
        image.onload = () => {
          const width = image.naturalWidth || image.width || 1
          const height = image.naturalHeight || image.height || 1
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const context = canvas.getContext('2d')
          if (context && 'imageSmoothingEnabled' in context) {
            context.imageSmoothingEnabled = true
            if ('imageSmoothingQuality' in context) {
              context.imageSmoothingQuality = 'high'
            }
          }
          context.fillStyle = '#fff'
          context.fillRect(0, 0, width, height)
          context.drawImage(image, 0, 0, width, height)
          URL.revokeObjectURL(url)
          canvas.toBlob(async jpegBlob => {
            if (!jpegBlob) {
              reject(new Error('Image convert failed'))
              return
            }
            resolve({
              width,
              height,
              data: new Uint8Array(await this.blobToArrayBuffer(jpegBlob))
            })
          }, 'image/jpeg', 1)
        }
        image.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load failed'))
        }
        image.src = url
      })
    },
    buildPdfBlob(imagePages) {
      const encoder = new TextEncoder()
      const chunks = []
      const offsets = []
      let offset = 0
      const writeString = value => {
        const bytes = encoder.encode(value)
        chunks.push(bytes)
        offset += bytes.length
      }
      const writeBytes = bytes => {
        chunks.push(bytes)
        offset += bytes.length
      }
      const objectCount = 2 + imagePages.length * 3
      const writeObject = (id, parts) => {
        offsets[id] = offset
        writeString(`${id} 0 obj\n`)
        parts.forEach(part => {
          if (typeof part == 'string') {
            writeString(part)
          } else {
            writeBytes(part)
          }
        })
        writeString('\nendobj\n')
      }
      writeString('%PDF-1.4\n')
      writeObject(1, ['<< /Type /Catalog /Pages 2 0 R >>'])
      const pageIds = imagePages.map((page, index) => 3 + index * 3)
      writeObject(2, [`<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`])
      imagePages.forEach((page, index) => {
        const pageId = 3 + index * 3
        const contentId = pageId + 1
        const imageId = pageId + 2
        const layout = this.$getDownloadPdfPageLayout(page)
        const imageName = `Im${index + 1}`
        const content = `q\n${layout.drawWidth} 0 0 ${layout.drawHeight} ${layout.offsetX} ${layout.offsetY} cm\n/${imageName} Do\nQ`
        writeObject(pageId, [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${layout.pageWidth} ${layout.pageHeight}] /Resources << /XObject << /${imageName} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`])
        writeObject(contentId, [`<< /Length ${encoder.encode(content).length} >>\nstream\n${content}\nendstream`])
        writeObject(imageId, [
          `<< /Type /XObject /Subtype /Image /Width ${layout.imageWidth} /Height ${layout.imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.data.length} >>\nstream\n`,
          page.data,
          '\nendstream'
        ])
      })
      const xrefOffset = offset
      writeString(`xref\n0 ${objectCount + 1}\n`)
      writeString('0000000000 65535 f \n')
      for (let id = 1; id <= objectCount; id++) {
        writeString(`${String(offsets[id]).padStart(10, '0')} 00000 n \n`)
      }
      writeString(`trailer\n<< /Size ${objectCount + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`)
      return new Blob(chunks, { type: 'application/pdf' })
    },
    toggleFavorite(item) {
      if (!item) {
        return
      }
      this.$set(item, 'favorite', !item.favorite)
      this.moreMenuId = ''
      if (this.paperFilter == 'favorite' && !item.favorite) {
        this.selectedPaperIds = this.selectedPaperIds.filter(id => id != item.id)
        this.selectedPaperId = this.selectedPaperIds[0] || ''
      }
    },
    openPaperItem(item) {
      if (!item) {
        return
      }
      if (item.isFolder || !item.resourceId) {
        this.clearSelection()
        this.fakeAction(item.isFolder ? '\u6253\u5f00\u6587\u4ef6\u5939' : '\u6253\u5f00\u8d44\u6e90')
        return
      }
      if (this.isPaperEncryptedItem(item)) {
        this.clearSelection()
        this.resetPaperDetailBeforePasswordPrompt()
        this.openPaperPasswordModal(item)
        return
      }
      this.openPaperDetail(item)
    },
    async openPaperDetail(item, password = '', options = {}) {
      this.clearSelection()
      this.detailMode = true
      this.currentPaper = item
      this.currentPaperPassword = password || ''
      this.clearPreviewZoomTip()
      return this.loadPaperDetail(item, Object.assign({}, options, { password: this.currentPaperPassword }))
    },
    resetPaperDetailBeforePasswordPrompt() {
      this.paperDetailRequestSeq += 1
      this.detailMode = false
      this.currentPaper = null
      this.currentPaperPassword = ''
      this.paperDetail = null
      this.paperDetailLoading = false
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.selectedDetailPageNos = []
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.resetLearningDetailState()
      this.stopPaperPreviewDrag()
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
    },
    openPaperPasswordModal(item, error = '', keepInput = false, action = 'open', items = []) {
      this.paperPasswordTarget = item
      this.paperPasswordAction = action == 'download' ? 'download' : 'open'
      this.paperPasswordItems = Array.isArray(items) ? items.filter(Boolean) : []
      this.paperPasswordError = error || ''
      this.paperPasswordVisible = false
      this.paperPasswordSubmitting = false
      if (!keepInput) {
        this.paperPasswordInput = ''
      }
      this.paperPasswordModalOpen = true
      this.$nextTick(() => {
        const input = this.$refs.paperPasswordInput
        if (input && typeof input.focus == 'function') {
          input.focus()
        }
      })
    },
    closePaperPasswordModal() {
      if (this.paperPasswordSubmitting) {
        return
      }
      this.paperPasswordRequestSeq += 1
      this.paperPasswordModalOpen = false
      this.paperPasswordTarget = null
      this.paperPasswordInput = ''
      this.paperPasswordVisible = false
      this.paperPasswordError = ''
      this.paperPasswordAction = 'open'
      this.paperPasswordItems = []
    },
    togglePaperPasswordVisible() {
      this.paperPasswordVisible = !this.paperPasswordVisible
    },
    async confirmPaperPassword() {
      if (this.paperPasswordSubmitting) {
        return
      }
      const item = this.paperPasswordTarget
      const rawPassword = String(this.paperPasswordInput || '')
      if (!rawPassword) {
        this.paperPasswordError = this.ui.passwordRequired
        this.$nextTick(() => {
          const input = this.$refs.paperPasswordInput
          if (input && typeof input.focus == 'function') {
            input.focus()
          }
        })
        return
      }
      if (!item) {
        this.closePaperPasswordModal()
        return
      }
      const action = this.paperPasswordAction == 'download' ? 'download' : 'open'
      const actionItems = Array.isArray(this.paperPasswordItems) ? this.paperPasswordItems.slice() : []
      this.paperPasswordSubmitting = true
      this.paperPasswordError = ''
      const requestId = this.paperPasswordRequestSeq + 1
      this.paperPasswordRequestSeq = requestId
      try {
        const hashedPassword = await this.encryptPaperPasswordToSHA256String(rawPassword)
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        const ret = await checkCloudPassword({ password: hashedPassword }, { skipGlobalErrorMessage: true })
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        if (this.isPaperApiCodeFailed(ret)) {
          this.paperPasswordError = this.getPaperPasswordErrorMessage()
          return
        }
        this.paperPasswordModalOpen = false
        this.paperPasswordSubmitting = false
        this.paperPasswordTarget = null
        this.paperPasswordError = ''
        this.paperPasswordInput = ''
        this.paperPasswordVisible = false
        this.paperPasswordAction = 'open'
        this.paperPasswordItems = []
        if (action == 'download') {
          this.openPaperDownloadFormatModal(actionItems.length > 0 ? actionItems : [item], { password: hashedPassword })
          return
        }
        await this.openPaperDetail(item, hashedPassword)
      } catch (error) {
        if (requestId != this.paperPasswordRequestSeq) {
          return
        }
        if (this.isPaperPasswordCodeError(error) || this.isPaperApiCodeFailed(error)) {
          this.paperPasswordError = this.getPaperPasswordErrorMessage()
        } else {
          this.paperPasswordError = this.getPaperApiErrorMessage(error) || this.getPaperPasswordErrorMessage()
        }
      } finally {
        if (requestId == this.paperPasswordRequestSeq) {
          this.paperPasswordSubmitting = false
        }
      }
    },
    async encryptPaperPasswordToSHA256String(pass) {
      if (!pass) {
        return ''
      }
      if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
        throw new Error('\u5f53\u524d\u6d4f\u89c8\u5668\u4e0d\u652f\u6301\u5bc6\u7801\u52a0\u5bc6')
      }
      const bytes = new TextEncoder().encode(pass)
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', bytes)
      return Array.from(new Uint8Array(hashBuffer))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()
    },
    closePaperDetail() {
      this.paperDetailRequestSeq += 1
      this.detailMode = false
      this.currentPaper = null
      this.currentPaperPassword = ''
      this.paperDetail = null
      this.paperDetailLoading = false
      this.paperDetailProgress = 0
      this.paperDetailError = ''
      this.clearPaperPreviewObjectUrls({ revoke: false })
      this.currentPageNo = 1
      this.pageGridOpen = false
      this.pageGridReturnPageNo = 1
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = ''
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.resetLearningDetailState()
      this.stopPaperPreviewDrag()
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
    },
    handleDetailBack() {
      if (this.paperDetailLoading) {
        this.closePaperDetail()
        if (this.detailBackRoot) {
          this.$emit('back-root')
        }
        return
      }
      if (this.pageGridOpen) {
        const targetPageNo = Number(this.pageGridReturnPageNo) || Number(this.currentPageNo) || 1
        const returnZoomLevel = this.takePreviewGridZoomLevel('paper')
        this.pageGridOpen = false
        this.currentPageNo = targetPageNo
        this.setPaperZoomLevel(returnZoomLevel)
        return
      }
      this.closePaperDetail()
      if (this.detailBackRoot) {
        this.$emit('back-root')
      }
    },
    toggleRecordPanel() {
      if (this.paperDetailLoading) {
        return
      }
      this.recordPanelOpen = !this.recordPanelOpen
      if (!this.recordPanelOpen) {
        this.recordTextOpen = false
        this.activeRecording = null
        this.resetRecordTextState()
      }
    },
    closeRecordPanel() {
      if (this.paperDetailLoading) {
        return
      }
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
    },
    togglePageGrid() {
      if (this.paperDetailLoading || this.pageGridOpen) {
        return
      }
      this.pageGridReturnPageNo = Number(this.currentPageNo) || 1
      this.rememberPreviewGridZoomLevel('paper', this.zoomLevel)
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = this.pageGridReturnPageNo
      this.pageGridOpen = true
      this.recordPanelOpen = false
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
      this.clearPreviewZoomTip()
    },
    selectCurrentDetailPageInGrid() {
      this.selectedDetailPageNos = []
      this.selectedDetailPageAnchorNo = Number(this.currentPageNo) || 1
    },
    isDetailPageSelected(pageNo) {
      return this.selectedDetailPageNos.includes(pageNo)
    },
    toggleDetailPageSelection(pageNo, event) {
      if (this.paperDetailLoading) {
        return
      }
      const result = selectItemWithWindowsRange(
        this.detailPages,
        this.selectedDetailPageNos,
        { pageNo },
        this.selectedDetailPageAnchorNo || this.currentPageNo,
        event,
        page => page && page.pageNo
      )
      this.selectedDetailPageNos = result.selectedIds
      this.selectedDetailPageAnchorNo = result.anchorId
    },
    openDetailPageFromGrid(pageNo) {
      if (this.paperDetailLoading) {
        return
      }
      const isSamePage = Number(pageNo) == Number(this.pageGridReturnPageNo)
      const returnZoomLevel = this.takePreviewGridZoomLevel('paper')
      this.currentPageNo = pageNo
      this.pageGridOpen = false
      if (isSamePage) {
        this.setPaperZoomLevel(returnZoomLevel)
        return
      }
      this.resetPaperZoom()
      this.clearPreviewZoomTip()
    },
    selectDetailPage(pageNo) {
      if (this.paperDetailLoading) {
        return
      }
      this.currentPageNo = pageNo
      this.pageGridOpen = false
      this.clearPreviewZoomTip()
      this.centerPaperPreviewScroll()
    },
    goPrevPage() {
      if (this.paperDetailLoading) {
        return
      }
      if (this.currentPageIndex <= 0) {
        return
      }
      this.currentPageNo = this.detailPages[this.currentPageIndex - 1].pageNo
      this.clearPreviewZoomTip()
    },
    goNextPage() {
      if (this.paperDetailLoading) {
        return
      }
      if (this.currentPageIndex >= this.detailPages.length - 1) {
        return
      }
      this.currentPageNo = this.detailPages[this.currentPageIndex + 1].pageNo
      this.clearPreviewZoomTip()
    },
    zoomIn() {
      if (this.paperDetailLoading || this.pageGridOpen) {
        return
      }
      const nextLevel = this.setPaperZoomLevel(this.zoomLevel + 1)
      this.showPreviewZoomTip('paper', 'in', nextLevel, this.ui)
    },
    zoomOut() {
      if (this.paperDetailLoading || this.pageGridOpen) {
        return
      }
      const nextLevel = this.setPaperZoomLevel(this.zoomLevel - 1)
      this.showPreviewZoomTip('paper', 'out', nextLevel, this.ui)
    },
    resetPaperZoom() {
      this.setPaperZoomLevel(0)
    },
    setPaperZoomLevel(level) {
      const nextLevel = this.clampPreviewZoomLevel(level)
      this.zoomLevel = nextLevel
      this.zoomValue = this.getPaperZoomValue(nextLevel)
      this.centerPaperPreviewScroll()
      return nextLevel
    },
    getPaperZoomValue(level) {
      return this.getPreviewZoomValue(level)
    },
    centerPaperPreviewScroll() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame == 'function') {
          window.requestAnimationFrame(this.applyPaperPreviewCenterScroll)
          return
        }
        this.applyPaperPreviewCenterScroll()
      })
    },
    applyPaperPreviewCenterScroll() {
      const shell = this.$refs.paperPreviewShell
      if (!shell) {
        return
      }
      shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2)
    },
    startPaperPreviewDrag(event) {
      const shell = this.$refs.paperPreviewShell
      if (this.paperDetailLoading || !event || event.button !== 0 || !shell) {
        return
      }
      if (shell.scrollWidth <= shell.clientWidth && shell.scrollHeight <= shell.clientHeight) {
        return
      }
      event.preventDefault()
      this.paperPreviewDragState = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: shell.scrollLeft,
        scrollTop: shell.scrollTop
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('mousemove', this.handlePaperPreviewDrag)
        document.addEventListener('mouseup', this.stopPaperPreviewDrag)
      }
    },
    handlePaperPreviewDrag(event) {
      const shell = this.$refs.paperPreviewShell
      const state = this.paperPreviewDragState
      if (!shell || !state || !event) {
        return
      }
      shell.scrollLeft = state.scrollLeft - (event.clientX - state.x)
      shell.scrollTop = state.scrollTop - (event.clientY - state.y)
    },
    stopPaperPreviewDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handlePaperPreviewDrag)
        document.removeEventListener('mouseup', this.stopPaperPreviewDrag)
      }
      this.paperPreviewDragState = null
    },
    resetRecordTextState() {
      this.recordTextRequestSeq += 1
      this.recordTextLoading = false
      this.recordTextError = this.ui.loadFailed
    },
    closeRecordingText() {
      this.recordTextOpen = false
      this.activeRecording = null
      this.resetRecordTextState()
    },
    openRecordingText(recording) {
      if (!this.hasTranscript(recording)) {
        return
      }
      this.resetRecordTextState()
      this.activeRecording = recording
      this.recordTextOpen = true
      if (!recording.transcriptText && recording.transcriptTextUrl) {
        this.loadRecordingTranscript(recording)
      }
    },
    async loadRecordingTranscript(recording) {
      const url = recording && recording.transcriptTextUrl ? String(recording.transcriptTextUrl).trim() : ''
      if (!url) {
        return
      }
      const requestId = this.recordTextRequestSeq + 1
      this.recordTextRequestSeq = requestId
      this.recordTextLoading = true
      this.recordTextError = this.ui.loadFailed
      try {
        let text = ''
        if (url.startsWith('mock://')) {
          text = ''
        } else {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Transcript failed: ${response.status}`)
          }
          text = this.normalizeTranscriptText(await response.text())
        }
        if (requestId != this.recordTextRequestSeq || this.activeRecording !== recording) {
          return
        }
        this.$set(recording, 'transcriptText', text || this.ui.noTranscript)
      } catch (error) {
        if (requestId == this.recordTextRequestSeq && this.activeRecording === recording) {
          this.recordTextError = this.ui.loadFailed
        }
      } finally {
        if (requestId == this.recordTextRequestSeq && this.activeRecording === recording) {
          this.recordTextLoading = false
        }
      }
    },
    normalizeTranscriptText(value) {
      const text = String(value || '').trim()
      if (!text) {
        return ''
      }
      const parsed = this.parseMetaJson(text)
      if (parsed) {
        const extracted = this.extractTranscriptTextPayload(parsed)
        if (extracted) {
          return extracted
        }
      }
      return text
    },
    extractTranscriptTextPayload(payload) {
      if (payload === undefined || payload === null) {
        return ''
      }
      if (Array.isArray(payload)) {
        return payload.map(item => this.extractTranscriptTextPayload(item)).filter(Boolean).join('\n')
      }
      if (typeof payload == 'object') {
        const direct = this.pickValue(payload, ['transcriptText', 'text', 'content', 'transcript', 'data', 'result'])
        if (direct !== undefined) {
          return this.extractTranscriptTextPayload(direct)
        }
        return ''
      }
      return String(payload).trim()
    },
    hasTranscript(recording) {
      return !!(recording && (recording.transcriptText || recording.transcriptTextUrl))
    },
    enqueueRecordingDownload(recording) {
      const url = recording && recording.fileUrl ? String(recording.fileUrl).trim() : ''
      if (!url || url.startsWith('mock://')) {
        this.fakeAction(this.ui.download)
        return
      }
      if (!this.$canStartDownloadBatch(1, 1)) {
        return
      }
      const task = this.createRecordingDownloadTask(recording)
      this.$enqueuePaperDownloadTask(task, () => this.runRecordingDownloadTask(task))
    },
    createRecordingDownloadTask(recording) {
      const url = recording && recording.fileUrl ? String(recording.fileUrl).trim() : ''
      const fileName = this.sanitizeFileName(this.getRecordingDownloadName(recording)) || this.getUrlDownloadName(url, this.ui.recordings)
      return {
        id: `paper-recording-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'recording-file',
        name: this.sanitizeFileName(recording && recording.recordingName ? recording.recordingName : this.ui.recordings) || this.ui.recordings,
        fileName,
        sourceRecording: recording || null,
        sourceUrl: url,
        openUrl: '',
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runRecordingDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const url = task && task.sourceUrl ? String(task.sourceUrl).trim() : ''
        if (!url || url.startsWith('mock://')) {
          throw new Error('No recording file')
        }
        this.updatePaperDownloadTask(task, {
          state: 'running',
          progress: Math.max(task.progress || 0, 5),
          statusText: this.ui.downloading
        })
        const blob = await this.fetchPaperBinaryBlob(url, percent => {
          this.updatePaperDownloadTask(task, {
            progress: Math.max(5, Math.min(95, Number(percent) || 0)),
            statusText: this.ui.downloading
          })
        }, task)
        this.ensurePaperDownloadTaskActive(task)
        this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
        this.savePaperDownloadBlob(task, blob)
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: error && error.message ? error.message : this.ui.downloadFailed
        })
      }
    },
    downloadCurrentPage() {
      if (this.paperDetailLoading) {
        return
      }
      if (this.pageGridOpen) {
        if (this.selectedDetailPages.length > 0) {
          this.openDetailPagesDownloadFormatModal(this.selectedDetailPages)
          return
        }
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (this.currentPage && (this.currentPage.imageUrl || this.currentPage.pageImageUrl)) {
        this.openDetailPagesDownloadFormatModal([this.currentPage])
        return
      }
      this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
    },
    startDetailPagesDownload(pages, format = 'png') {
      const list = Array.isArray(pages) ? pages.filter(page => page && (page.imageUrl || page.pageImageUrl)) : []
      if (list.length == 0) {
        this.$message.info('\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u9875\u9762')
        return
      }
      if (!this.$canStartDownloadBatch(list.length, 1, { skipSelectionLimit: true })) {
        return
      }
      const task = this.createDetailPagesDownloadTask(list, format)
      this.$enqueuePaperDownloadTask(task, () => this.runDetailPagesDownloadTask(task))
      if (this.pageGridOpen) {
        this.selectedDetailPageNos = []
        this.selectedDetailPageAnchorNo = ''
      }
    },
    createDetailPagesDownloadTask(pages, format = 'png') {
      const baseName = this.$getDownloadBaseName(this.paperDetailTitle, 'Paper', this.sanitizeFileName)
      const safeFormat = format == 'png' ? 'png' : 'pdf'
      const isSingle = pages.length == 1
      const extension = safeFormat == 'pdf' ? 'pdf' : (isSingle ? 'png' : 'zip')
      return {
        id: `paper-page-download-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        kind: 'detail-pages',
        name: baseName,
        fileName: isSingle ? `${baseName}-\u7b2c${pages[0].pageNo || 1}\u9875.${extension}` : `${baseName}.${extension}`,
        format: safeFormat,
        pages: pages.slice(),
        pngFiles: [],
        pdfPages: [],
        failedPageIndex: 0,
        abortController: null,
        cancelled: false,
        progress: 0,
        state: 'running',
        statusText: this.ui.downloadPreparing
      }
    },
    async runDetailPagesDownloadTask(task) {
      try {
        this.ensurePaperDownloadTaskActive(task)
        const pages = Array.isArray(task.pages) ? task.pages : []
        if (pages.length == 0) {
          throw new Error('No pages')
        }
        if (task.format == 'pdf') {
          await this.downloadPaperAsPdf(task, pages)
        } else {
          const compositeFrameSize = await this.getUnifiedPaperCompositeFrameSize(pages, task)
          const pngFiles = Array.isArray(task.pngFiles) ? task.pngFiles.slice() : []
          for (let i = 0; i < pages.length; i++) {
            this.ensurePaperDownloadTaskActive(task)
            if (pngFiles[i]) {
              continue
            }
            const page = pages[i]
            this.updatePaperDownloadTask(task, {
              failedPageIndex: i,
              progress: this.getPaperDownloadImageProgress(i, pages.length, 5, 85, 0),
              statusText: this.ui.downloading
            })
            const blob = await this.fetchPaperPageImageBlob(page, percent => {
              this.updatePaperDownloadTask(task, {
                progress: this.getPaperDownloadImageProgress(i, pages.length, 5, 85, percent),
                statusText: this.ui.downloading
              })
            }, task)
            const canvas = await this.createPaperImageFrameCanvas(blob, compositeFrameSize)
            const framedBlob = await this.canvasToPngBlob(canvas)
            this.releaseCanvas(canvas)
            pngFiles[i] = {
              fileName: this.getPaperPageFileName(page, i, 'png'),
              buffer: await this.blobToArrayBuffer(framedBlob)
            }
            this.updatePaperDownloadTask(task, {
              pngFiles: pngFiles.slice(),
              failedPageIndex: i + 1
            })
          }
          this.ensurePaperDownloadTaskActive(task)
          if (pages.length == 1 && pngFiles[0]) {
            this.updatePaperDownloadTask(task, { progress: 99, statusText: this.ui.savingFile })
            this.savePaperDownloadBlob(task, new Blob([pngFiles[0].buffer], { type: 'image/png' }))
          } else {
            const zip = new JSZip()
            pngFiles.forEach((file, index) => {
              if (file) {
                zip.file(file.fileName || this.getPaperPageFileName(pages[index], index, 'png'), file.buffer)
              }
            })
            this.updatePaperDownloadTask(task, { progress: 90, statusText: '\u6253\u5305 ZIP' })
            const zipBlob = await zip.generateAsync({ type: 'blob' }, metadata => {
              this.updatePaperDownloadTask(task, {
                progress: Math.min(98, Math.round(90 + (metadata.percent || 0) * 0.08)),
                statusText: '\u6253\u5305 ZIP'
              })
            })
            this.ensurePaperDownloadTaskActive(task)
            this.savePaperDownloadBlob(task, zipBlob)
          }
        }
        this.updatePaperDownloadTask(task, {
          progress: 100,
          state: 'done',
          statusText: this.ui.downloadDone
        })
      } catch (error) {
        if (error && error.isPaperDownloadCancelled) {
          return
        }
        const isCorsLikeError = this.isLikelyCorsDownloadError(error)
        this.updatePaperDownloadTask(task, {
          state: 'error',
          progress: task.progress || 0,
          statusText: isCorsLikeError ? this.ui.imageReadFailed : (error && error.message ? error.message : this.ui.downloadFailed)
        })
      }
    },
    async downloadUrl(url, fileName) {
      if (url && !String(url).startsWith('mock://')) {
        const safeName = this.sanitizeFileName(fileName) || this.getUrlDownloadName(url, this.paperDetailTitle)
        try {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`)
          }
          const blob = await response.blob()
          saveAs(blob, safeName)
        } catch (error) {
          const link = document.createElement('a')
          link.href = url
          link.download = safeName
          link.target = '_blank'
          link.rel = 'noopener'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
        return
      }
      this.fakeAction(this.ui.download)
    },
    getCurrentPageDownloadName() {
      const pageNo = this.currentPage && this.currentPage.pageNo ? this.currentPage.pageNo : this.currentPageIndex + 1
      const extension = this.getUrlExtension(this.currentPage && this.currentPage.imageUrl, 'png')
      const baseName = this.$getDownloadBaseName(this.paperDetailTitle, this.ui.download, this.sanitizeFileName)
      return `${baseName}-\u7b2c${pageNo}\u9875.${extension}`
    },
    getRecordingDownloadName(recording) {
      const name = recording && recording.recordingName ? recording.recordingName : this.ui.recordings
      const extension = this.getUrlExtension(recording && recording.fileUrl, 'mp4')
      return this.$getDownloadFileName(name, extension, this.ui.recordings, this.sanitizeFileName)
    },
    getUrlDownloadName(url, fallbackName) {
      const extension = this.getUrlExtension(url, '')
      const name = this.$getDownloadBaseName(fallbackName, this.ui.download, this.sanitizeFileName)
      return extension ? `${name}.${extension}` : name
    },
    getUrlExtension(url, fallback = '') {
      const cleanPath = String(url || '').split(/[?#]/)[0]
      const fileName = cleanPath.split(/[\\/]/).pop() || ''
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex < 0 || dotIndex == fileName.length - 1) {
        return fallback
      }
      return fileName.slice(dotIndex + 1).toLowerCase()
    },
    sanitizeFileName(name) {
      return String(name || '')
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
    },
    fakeAction() {
      this.moreMenuId = ''
      this.$message.info(this.ui.fakeTip)
    },
    markCoverFailed(item) {
      if (item) {
        this.$set(item, 'coverFailed', true)
      }
    },
    shouldDisplayCover(item) {
      return !!(item && item.cover && !item.coverFailed && !item.isFolder && !item.encrypted)
    },
    shouldDisplayGridCover(item) {
      return !!(item && this.getPaperGridCoverImageUrl(item) && !item.isFolder && !item.encrypted)
    },
    getFallbackCover(item, mode = this.viewMode) {
      const isGrid = mode == 'grid'
      if (item && item.encrypted) {
        return isGrid ? this.paperIcons.fileGridEncrypt : this.paperIcons.fileListEncrypt
      }
      if (item && item.isFolder) {
        const hasChildren = this.getFolderChildSize(item) > 0
        if (item.template) {
          if (isGrid) {
            return hasChildren ? this.paperIcons.templateGridFilled : this.paperIcons.templateGridEmpty
          }
          return hasChildren ? this.paperIcons.templateListFilled : this.paperIcons.templateListEmpty
        }
        if (isGrid) {
          return hasChildren ? this.paperIcons.folderGridFilled : this.paperIcons.folderGridEmpty
        }
        return hasChildren ? this.paperIcons.folderListFilled : this.paperIcons.folderListEmpty
      }
      const extension = this.getPaperFileExtension(item)
      const pdfIcon = isGrid ? this.paperIcons.fileGridPdf : this.paperIcons.fileListPdf
      const paperIcon = isGrid ? this.paperIcons.fileGridPaper : this.paperIcons.fileListPaper
      return extension == 'pdf' ? pdfIcon : paperIcon
    },
    getPaperFileExtension(item) {
      const rawPath = item && item.filePath ? String(item.filePath).trim() : ''
      const cleanPath = rawPath.split(/[?#]/)[0]
      const fileName = cleanPath.split(/[\\/]/).pop() || ''
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex < 0 || dotIndex == fileName.length - 1) {
        return ''
      }
      return fileName.slice(dotIndex + 1).toLowerCase()
    },
    getFolderChildSize(item) {
      const value = item && item.childSize !== undefined && item.childSize !== null && item.childSize !== ''
        ? Number(item.childSize)
        : 0
      return Number.isFinite(value) ? value : 0
    },
    formatPageCount(item) {
      return this.formatResourceCountLabel(item, {
        language: this.language,
        itemUnit: this.ui.item,
        pageUnit: this.ui.page,
        emptyText: '-'
      })
    },
    formatDate(value) {
      const time = this.parseDateTime(value)
      if (!time) {
        return '-'
      }
      const date = new Date(time)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${date.getFullYear()}/${month}/${day}`
    },
    formatDateTime(value) {
      const time = this.parseDateTime(value)
      if (!time) {
        return '-'
      }
      const date = new Date(time)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      const hour = `${date.getHours()}`.padStart(2, '0')
      const minute = `${date.getMinutes()}`.padStart(2, '0')
      return `${date.getFullYear()}/${month}/${day} ${hour}:${minute}`
    },
    formatDuration(value) {
      const ms = Number(value) || 0
      const totalSeconds = Math.max(0, Math.floor(ms / 1000))
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = `${totalSeconds % 60}`.padStart(2, '0')
      return `${minutes}:${seconds}`
    },
    formatByteSize(value) {
      const bytes = Number(value)
      if (!Number.isFinite(bytes) || bytes <= 0) {
        return '-'
      }
      const units = ['B', 'KB', 'MB', 'GB']
      let size = bytes
      let unitIndex = 0
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex += 1
      }
      const precision = unitIndex == 0 || Number.isInteger(size) ? 0 : 2
      return `${Number(size.toFixed(precision))}${units[unitIndex]}`
    }
  }
}
</script>

<style scoped>
.paper-workspace {
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #f8f4ef;
  color: #181d1f;
  overflow: hidden;
  font-family: PingFangSC, PingFang SC, Helvetica Neue, Arial, sans-serif;
}

.paper-list-header,
.paper-detail-header {
  height: 64px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 32px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-list-header {
  height: 72px;
}

.paper-breadcrumb {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0;
  color: #202428;
  overflow: hidden;
}

.paper-breadcrumb-root {
  border: 0;
  background: transparent;
  padding: 0;
  color: #202428;
  font-size: 24px;
  line-height: 34px;
  font-weight: 700;
  cursor: pointer;
  flex: 0 0 auto;
}

.paper-breadcrumb-segment {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
}

.paper-breadcrumb-segment:not(:last-child) {
  flex: 0 0 auto;
}

.paper-breadcrumb-link {
  max-width: 160px;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0;
  color: #1f2326;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-breadcrumb-separator {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.paper-breadcrumb strong {
  min-width: 0;
  max-width: 240px;
  color: #1f2326;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-header-actions,
.paper-detail-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.paper-icon-button,
.paper-tool-button,
.paper-order-button,
.paper-back-button,
.paper-filter-button,
.paper-sort-button,
.paper-view-hotspot,
.paper-more-button,
.paper-favorite,
.record-action,
.record-text-head button,
.paper-page-controls button {
  font: inherit;
}

.paper-icon-button,
.paper-tool-button {
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-icon-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.paper-import-button {
  width: 128px;
  height: 44px;
  border: 0;
  background: #1f3a2d;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  padding: 0;
}

.paper-import-button img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.paper-toolbar {
  height: 78px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 32px 0;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-filter,
.paper-sort {
  position: relative;
}

.paper-filter-button,
.paper-sort-button {
  height: 36px;
  border: 1px solid #c8c8c5;
  background: transparent;
  color: #202428;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
}

.paper-filter-button {
  width: 138px;
  gap: 10px;
  padding: 0 12px;
}

.paper-sort-button {
  min-width: 88px;
  justify-content: center;
  padding: 0 14px;
}

.paper-filter-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex: 0 0 22px;
  display: block;
}

.filter-arrow {
  width: 9px;
  height: 9px;
  border-right: 1px solid #111;
  border-bottom: 1px solid #111;
  transform: rotate(45deg) translateY(-3px);
  margin-left: auto;
}

.filter-arrow.open {
  transform: rotate(225deg) translate(-2px, -1px);
}

.paper-filter-menu,
.paper-sort-menu {
  position: absolute;
  top: 40px;
  left: auto;
  right: 0;
  z-index: 30;
  min-width: 130px;
  width: max-content;
  max-width: calc(100vw - 24px);
  background: #fff;
  border: 1px solid #d8d8d8;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
}

.paper-sort-menu {
  left: auto;
  right: 0;
}

.paper-filter-menu button,
.paper-sort-menu button {
  width: max-content;
  min-width: 100%;
  height: 40px;
  border: 0;
  background: #fff;
  color: #171b1d;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 17px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  white-space: nowrap;
}

.paper-filter-menu button.active,
.paper-sort-menu button.active {
  background: #dce2d8;
}

.paper-menu-icon {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  object-fit: contain;
  display: block;
}

.filter-check {
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-left: auto;
}

.paper-toolbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.paper-order-button {
  width: 36px;
  height: 36px;
  border: 1px solid #c8c8c5;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-order-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.paper-view-switch {
  width: 72px;
  height: 36px;
  position: relative;
  flex: 0 0 72px;
}

.paper-view-switch img {
  width: 72px;
  height: 36px;
  display: block;
  object-fit: contain;
}

.paper-view-hotspot {
  position: absolute;
  top: 0;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.paper-view-list {
  left: 0;
}

.paper-view-grid {
  right: 0;
}

.paper-selection-layer {
  position: absolute;
  top: 72px;
  right: 0;
  left: 0;
  z-index: 40;
  padding: 12px 12px 10px;
  box-sizing: border-box;
  pointer-events: none;
  will-change: transform;
}

.paper-selection-layer.dragging {
  user-select: none;
}

.paper-selection-bar {
  height: 48px;
  width: 100%;
  min-width: 0;
  padding: 0 16px;
  background: #DDE4D9;
  color: #333;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 18px;
  box-sizing: border-box;
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  pointer-events: auto;
  cursor: grab;
  scrollbar-width: thin;
}

.paper-selection-layer.dragging .paper-selection-bar {
  cursor: grabbing;
}

.paper-selection-close {
  width: 24px;
  height: 24px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  position: relative;
}

.paper-selection-close span {
  width: 14px;
  height: 14px;
  display: block;
  position: relative;
}

.paper-selection-close span::before,
.paper-selection-close span::after {
  content: '';
  width: 14px;
  height: 1.5px;
  background: #111;
  position: absolute;
  left: 0;
  top: 6px;
}

.paper-selection-close span::before {
  transform: rotate(45deg);
}

.paper-selection-close span::after {
  transform: rotate(-45deg);
}

.paper-selection-bar > span {
  color: #606462;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  flex: 0 0 auto;
  white-space: nowrap;
}

.paper-selection-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 34px;
}

.paper-selection-actions button {
  height: 32px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #1f2326;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  white-space: nowrap;
}

.paper-selection-actions button:disabled {
  color: #9da39f;
  cursor: not-allowed;
  opacity: .45;
}

.paper-selection-actions img {
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
}

.paper-search-line {
  height: 46px;
  margin: -10px 32px 16px;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.paper-search-line img {
  width: 22px;
  height: 22px;
}

.paper-search-line input {
  min-width: 0;
  flex: 1;
  height: 44px;
  border: 0;
  outline: none;
  background: transparent;
  color: #202428;
  font-size: 15px;
}

.paper-search-line button {
  width: 18px;
  height: 18px;
  border: 0;
  background: transparent;
  position: relative;
  cursor: pointer;
}

.paper-search-line button::before,
.paper-search-line button::after {
  content: "";
  width: 16px;
  height: 1px;
  background: #777;
  position: absolute;
  left: 1px;
  top: 8px;
}

.paper-search-line button::before {
  transform: rotate(45deg);
}

.paper-search-line button::after {
  transform: rotate(-45deg);
}

.paper-list-area {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 24px 40px 32px;
  box-sizing: border-box;
}

.paper-list-area /deep/ .ant-spin-nested-loading,
.paper-list-area /deep/ .ant-spin-container {
  min-height: calc(100vh - 150px);
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading {
  min-height: calc(100vh - 150px);
}

.paper-list-content {
  min-height: calc(100vh - 150px);
}

.paper-list-area.is-grid .paper-list-content {
  min-width: calc(148px * 4 + 15px * 3);
  padding-top: 7.5px;
  box-sizing: border-box;
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading > div > .ant-spin {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  max-height: none;
  padding-top: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  color: #275B52;
}

.paper-list-area /deep/ .paper-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot {
  position: static;
  margin: 0;
}

.paper-list-area /deep/ .paper-list-spin .ant-spin-dot-item {
  background-color: #275B52;
}

.paper-empty {
  min-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #15191b;
  font-size: 14px;
  gap: 12px;
}

.paper-empty img {
  width: 130px;
  height: auto;
}

.paper-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  gap: 29.5px 15px;
  align-items: start;
  min-width: calc(148px * 4 + 15px * 3);
}

.paper-card {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
  --paper-card-height: 245.4px;
  --paper-grid-column-gap: 15px;
  --paper-grid-row-gap: 44px;
  --paper-grid-column-gap-half: calc(var(--paper-grid-column-gap) / 2);
  --paper-grid-row-gap-half: calc(var(--paper-grid-row-gap) / 2);
}

.paper-card.selected {
  z-index: 1;
}

.paper-card.selected::before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--paper-grid-column-gap-half));
  right: calc(-1 * var(--paper-grid-column-gap-half));
  bottom: calc(-1 * var(--paper-grid-row-gap-half));
  left: calc(-1 * var(--paper-grid-column-gap-half));
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.paper-card.selected.grid-edge-bottom::before {
  bottom: 0;
}

.paper-card.selected.grid-edge-top::before {
  top: calc(-1 * var(--paper-grid-column-gap-half));
}

.paper-card > * {
  position: relative;
  z-index: 1;
}

.paper-card.selected .paper-cover,
.paper-card:hover .paper-cover,
.paper-card:focus-visible .paper-cover {
  border-color: #b6b8b4;
  background: #f8f8f6;
}

.paper-card.selected .paper-cover {
  background: #fff;
}

.paper-card.selected .paper-cover.template {
  border-style: solid;
}

.paper-cover {
  width: 148px;
  height: 196.4px;
  flex: 0 0 196.4px;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
}

.paper-cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.paper-cover-default {
  width: 46px;
  height: auto;
  object-fit: contain;
  display: block;
}

.paper-cover-default.folder-icon {
  width: 46px;
  height: auto;
}

.paper-cover-default.encrypt-icon {
  width: 46px;
  height: auto;
}

.paper-cover.template {
  border-style: solid;
}

.paper-card-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  min-width: 0;
}

.paper-card-name {
  display: block;
  min-width: 0;
  flex: 1 1 auto;
  height: 22px;
  color: #0f1112;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-card-meta {
  height: 19px;
  flex: 0 0 19px;
  margin-top: 0;
  color: #9a9a9a;
  font-size: 13px;
  line-height: 19px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.paper-favorite {
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.paper-card .paper-favorite {
  position: absolute;
  right: 0;
  bottom: 17px;
  justify-content: flex-end;
  z-index: 2;
}

.paper-favorite img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.paper-favorite-status {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.paper-card-favorite-status {
  flex: 0 0 18px;
}

.paper-table {
  margin: 0 -12px 0 -20px;
}

.paper-row {
  min-height: 78px;
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: 66px minmax(220px, 1fr) 120px 48px 170px 48px 52px 48px 56px;
  align-items: center;
  gap: 0;
  padding: 0 16px;
  box-sizing: border-box;
  cursor: pointer;
  outline: none;
}

.paper-row:hover,
.paper-row.selected,
.paper-row:focus-visible {
  background: #f1eee8;
}

.paper-row-cover {
  width: 54px;
  height: 54px;
  border: 1px solid transparent;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.paper-row-cover-image {
  width: 46px;
  height: auto;
  object-fit: cover;
  display: block;
}

.paper-row-cover-default {
  width: 46px;
  height: auto;
  object-fit: contain;
  display: block;
}

.paper-row-cover-default.folder-icon {
  width: 46px;
  height: auto;
}

.paper-row-cover-default.encrypt-icon {
  width: 46px;
  height: auto;
}

.paper-row strong {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 78px;
  min-width: 0;
  color: #121618;
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-row > span:not(.paper-row-cover):not(.paper-more-wrap) {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
  color: #7d8284;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}

.paper-row-count {
  grid-column: 3;
}

.paper-row-date {
  grid-column: 5;
}

.paper-more-wrap {
  align-self: center;
  grid-column: 9;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
}

.paper-row .paper-favorite {
  align-self: center;
  grid-column: 7;
  justify-self: center;
  height: 78px;
}

.paper-row-favorite-status {
  grid-column: 7;
}

.paper-more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 48px;
  border: 0;
  background: transparent;
  color: #7B7C87;
  font-size: 0;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.paper-more-icon {
  display: block;
  flex: 0 0 12px;
  width: 12px;
  min-width: 12px;
  max-width: 12px;
  height: 4px;
  min-height: 4px;
  max-height: 4px;
  fill: #7B7C87;
  overflow: hidden;
  pointer-events: none;
  shape-rendering: auto;
}

.paper-row:hover .paper-more-button,
.paper-more-button:focus-visible {
  background: #F1EEE8;
}

.paper-more-menu {
  width: 168px;
  position: absolute;
  right: 16px;
  top: 47px;
  z-index: 50;
  border: 1px solid #d8d8d8;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
  padding: 5px 0;
}

.paper-more-menu button {
  width: 100%;
  height: 48px;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 18px;
  color: #171b1d;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
}

.paper-more-menu button:hover {
  background: #F1EEE8;
}

.paper-more-menu button:disabled {
  color: #9da39f;
  cursor: not-allowed;
  opacity: .45;
}

.paper-more-menu button:disabled:hover {
  background: #fff;
}

.paper-more-menu img {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  object-fit: contain;
  display: block;
}

.page-fallback {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #183d2d;
  font-size: 13px;
  font-weight: 600;
}

.paper-preview-pdf {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}

.paper-detail-title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.paper-back-button {
  width: 26px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.paper-back-button span,
.record-text-head button span {
  width: 12px;
  height: 12px;
  border-left: 2px solid #111;
  border-bottom: 2px solid #111;
  transform: rotate(45deg);
  display: block;
}

.paper-detail-title strong {
  min-width: 0;
  color: #14191b;
  font-size: 22px;
  line-height: 32px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-tool-button img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.paper-tool-button.active {
  background: #ebe8e2;
}

.paper-tool-button:disabled {
  opacity: .35;
  cursor: default;
}

.paper-back-button:disabled {
  opacity: .35;
  cursor: default;
}

.paper-detail-body {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  --paper-preview-base-height: max(480px, calc(100vh - 64px));
  --paper-preview-base-width: calc(var(--paper-preview-base-height) * 0.75);
}

.paper-detail-body.is-resolving {
  cursor: progress;
}

.paper-detail-body.is-resolving::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 30;
  cursor: progress;
}

.paper-detail-body /deep/ .ant-spin-nested-loading,
.paper-detail-body /deep/ .ant-spin-container {
  height: 100%;
}

.paper-preview-shell {
  height: calc(100vh - 64px);
  overflow: auto;
  display: block;
  padding: 0 96px 0 32px;
  box-sizing: border-box;
  --paper-preview-zoom: 1;
  cursor: grab;
  user-select: none;
}

.paper-detail-resolving {
  height: calc(100vh - 64px);
  padding: 0 96px 0 32px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}

.paper-detail-progress-box {
  width: var(--paper-preview-base-width);
  flex: 0 0 var(--paper-preview-base-width);
  margin: 0 auto;
  color: #15191b;
}

.paper-detail-progress-box strong,
.paper-detail-progress-box span {
  display: block;
  text-align: center;
}

.paper-detail-progress-box strong {
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}

.paper-detail-progress-box span {
  margin-top: 10px;
  color: #59605d;
  font-size: 16px;
  line-height: 22px;
}

.paper-detail-progress-track {
  width: 100%;
  height: 8px;
  margin-top: 18px;
  background: #d8ddd8;
  overflow: hidden;
}

.paper-detail-progress-track i {
  display: block;
  height: 100%;
  width: 0;
  background: #1f3a2d;
  transition: width .18s ease;
}

.paper-detail-progress-box p {
  margin: 14px 0 0;
  color: #7a3b33;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.paper-detail-progress-box button {
  width: 96px;
  height: 40px;
  border: 0;
  background: #1f3a2d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto 0;
  padding: 0;
  font-size: 15px;
  line-height: 20px;
  cursor: pointer;
}

.paper-preview-shell.dragging {
  cursor: grabbing;
}

.paper-preview-zoom {
  width: calc(var(--paper-preview-base-width) * var(--paper-preview-zoom));
  height: calc(var(--paper-preview-base-height) * var(--paper-preview-zoom));
  margin: 0 auto;
}

.paper-preview-stage {
  width: var(--paper-preview-base-width);
  height: var(--paper-preview-base-height);
  margin-top: 0;
  background: #fff;
  border: 1px solid #d7d9d6;
  transform-origin: top left;
  transform: scale(var(--paper-preview-zoom));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.paper-preview-stage img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.paper-preview-empty {
  width: 100%;
  height: 100%;
  background: #fff;
}

.paper-pages-grid {
  height: calc(100vh - 64px);
  overflow: auto;
  padding: 20px 20px 44px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  align-content: start;
  justify-content: start;
  gap: 29.5px 15px;
  min-width: calc(148px * 4 + 15px * 3);
  box-sizing: border-box;
}

.paper-pages-grid button {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
}

.paper-pages-grid button.selected {
  z-index: 1;
}

.paper-pages-grid button.selected::before {
  content: '';
  position: absolute;
  top: -7.5px;
  right: -7.5px;
  bottom: -22px;
  left: -7.5px;
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.paper-pages-grid button.selected.grid-edge-top::before {
  top: -7.5px;
}

.paper-pages-grid button.selected:not(.grid-selected-row-start)::before {
  top: -7.5px;
}

.paper-pages-grid button.selected.grid-selection-continues-below::before {
  bottom: -22px;
}

.paper-pages-grid button > * {
  position: relative;
  z-index: 1;
}

.paper-pages-grid button span {
  width: 148px;
  height: 196.4px;
  flex: 0 0 196.4px;
  border: 1px solid #d7d9d6;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.paper-pages-grid button.selected span {
  border-color: #b6b8b4;
  background: #fff;
}

.paper-pages-grid button.active span {
  border-bottom: 5px solid #050505;
}

.paper-pages-grid img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.paper-pages-grid strong {
  display: block;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  color: #121618;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.paper-page-controls {
  position: absolute;
  right: 24px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.paper-page-controls button {
  width: 36px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f8f4ef;
  position: relative;
  cursor: pointer;
}

.paper-page-controls button::before {
  content: "";
  width: 9px;
  height: 9px;
  border-left: 1px solid #111;
  border-bottom: 1px solid #111;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-35%, -50%) rotate(45deg);
}

.paper-page-controls button:last-child::before {
  transform: translate(-65%, -50%) rotate(225deg);
}

.paper-page-controls button:disabled {
  background: #f8f4ef;
  cursor: default;
}

.paper-page-controls button:disabled::before {
  border-left-color: #b7bbb8;
  border-bottom-color: #b7bbb8;
}

.paper-page-count {
  display: flex;
  align-items: center;
  gap: 10px;
}

.paper-page-count span {
  min-width: 66px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f8f4ef;
  color: #5d6062;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-sizing: border-box;
}

.paper-password-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .32);
  z-index: 100;
}

.paper-password-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 372px;
  min-height: 180px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 101;
  padding: 22px 20px 20px;
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(0, 0, 0, .12);
}

.paper-password-modal header {
  color: #1d1d1f;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  margin-bottom: 18px;
}

.paper-password-field {
  height: 35px;
  border: 1px solid #c9c9c9;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.paper-password-field.error {
  border-color: #c85f58;
}

.paper-password-field input {
  min-width: 0;
  flex: 1;
  height: 33px;
  border: 0;
  outline: 0;
  padding: 0 10px;
  color: #1d1d1f;
  font-size: 14px;
  box-sizing: border-box;
}

.paper-password-field input::placeholder {
  color: #b9b9b9;
}

.paper-password-field button {
  width: 36px;
  height: 33px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-password-field button:disabled {
  cursor: default;
  opacity: .55;
}

.paper-password-field button span {
  width: 18px;
  height: 11px;
  border: 2px solid #5b5f62;
  border-radius: 50%;
  position: relative;
  display: block;
  box-sizing: border-box;
}

.paper-password-field button span::before {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #5b5f62;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.paper-password-field button span::after {
  content: "";
  width: 21px;
  height: 2px;
  background: #5b5f62;
  position: absolute;
  left: -3px;
  top: 3px;
  transform: rotate(-38deg);
}

.paper-password-field button span.visible::after {
  display: none;
}

.paper-password-error {
  min-height: 18px;
  margin: 6px 0 0;
  color: #c85f58;
  font-size: 12px;
  line-height: 18px;
}

.paper-password-modal footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  margin-top: 32px;
}

.paper-password-modal footer > div {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.paper-password-forgot {
  display: none;
  color: #5e6b63;
  font-size: 13px;
  line-height: 20px;
}

.paper-password-modal footer > div button {
  min-width: 60px;
  height: 32px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}

.paper-password-modal footer > div button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}

.paper-password-modal footer > div button:disabled {
  cursor: default;
  opacity: .6;
}

.paper-download-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .38);
  z-index: 80;
}

.paper-download-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 474px;
  min-height: 223px;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 81;
  padding: 24px 28px 26px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.paper-download-modal header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #111;
  font-size: 19px;
  line-height: 27px;
  font-weight: 500;
}

.paper-download-modal header button {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.paper-download-modal header button span {
  width: 18px;
  height: 18px;
  position: relative;
  display: block;
}

.paper-download-modal header button span::before,
.paper-download-modal header button span::after {
  content: "";
  width: 18px;
  height: 2px;
  background: #111;
  position: absolute;
  left: 0;
  top: 8px;
}

.paper-download-modal header button span::before {
  transform: rotate(45deg);
}

.paper-download-modal header button span::after {
  transform: rotate(-45deg);
}

.paper-download-options {
  padding: 30px 0 38px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 44px;
}

.paper-download-options label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #111;
  font-size: 17px;
  line-height: 23px;
  cursor: pointer;
}

.paper-download-options input {
  width: 20px;
  height: 20px;
  accent-color: #111;
}

.paper-download-modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.paper-download-modal footer button {
  min-width: 80px;
  height: 40px;
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}

.paper-download-modal footer button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}



.paper-record-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .28);
  z-index: 55;
}

.paper-record-panel {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(842px, calc(100% - 96px));
  min-height: 532px;
  max-height: calc(100% - 108px);
  transform: translate(-50%, -50%);
  background: #fff;
  box-shadow: 0 8px 18px rgba(0, 0, 0, .1);
  z-index: 60;
  display: flex;
  flex-direction: column;
}

.paper-record-close {
  position: absolute;
  top: 18px;
  right: 28px;
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.paper-record-close:hover {
  background: #F1EEE8;
}

.paper-record-close span {
  width: 18px;
  height: 18px;
  position: relative;
  display: block;
}

.paper-record-close span::before,
.paper-record-close span::after {
  content: "";
  width: 20px;
  height: 2px;
  background: #111;
  position: absolute;
  left: -1px;
  top: 8px;
}

.paper-record-close span::before {
  transform: rotate(45deg);
}

.paper-record-close span::after {
  transform: rotate(-45deg);
}

.paper-record-panel > header {
  height: 76px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  padding: 0 40px;
  padding-right: 82px;
  box-sizing: border-box;
  color: #14191b;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  flex-shrink: 0;
}

.record-list {
  padding: 0 36px;
  overflow: auto;
}

.record-row {
  height: 80px;
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 56px 56px;
  align-items: center;
  gap: 0;
}

.record-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.record-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.record-main strong {
  color: #14191b;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-main span {
  color: #6e7578;
  font-size: 13px;
  line-height: 18px;
}

.record-main em {
  display: inline-block;
  width: 1px;
  height: 12px;
  background: #d8d8d8;
  margin: 0 16px;
  vertical-align: -2px;
}

.record-action {
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.record-action:disabled {
  opacity: .25;
  cursor: default;
}

.record-action img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.record-empty {
  min-height: 430px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.record-empty img {
  width: 140px;
  height: 102px;
  object-fit: contain;
}

.record-text-head {
  gap: 16px;
}

.record-text-head button {
  width: 28px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.record-text-head strong {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-text-body {
  padding: 32px;
  color: #161b1d;
  font-size: 16px;
  line-height: 24px;
  white-space: pre-wrap;
  overflow: auto;
}

.learning-detail-mask,
.learning-preview-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .24);
  z-index: 70;
}

.learning-detail-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(842px, calc(100% - 96px));
  height: min(548px, calc(100% - 108px));
  transform: translate(-50%, -50%);
  background: #fff;
  box-shadow: 0 8px 18px rgba(0, 0, 0, .1);
  z-index: 72;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.learning-detail-head {
  height: 68px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px 0 40px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.learning-detail-head strong {
  min-width: 0;
  color: #14191b;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-detail-head button {
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 40px;
}

.learning-detail-head button:hover {
  background: #F1EEE8;
}

.learning-detail-head button span {
  width: 18px;
  height: 18px;
  position: relative;
  display: block;
}

.learning-detail-head button span::before,
.learning-detail-head button span::after {
  content: "";
  width: 20px;
  height: 2px;
  background: #111;
  position: absolute;
  left: -1px;
  top: 8px;
}

.learning-detail-head button span::before {
  transform: rotate(45deg);
}

.learning-detail-head button span::after {
  transform: rotate(-45deg);
}

.learning-detail-resolving {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 44px 80px;
  box-sizing: border-box;
}

.learning-detail-resolving .paper-detail-progress-box {
  width: min(420px, 100%);
  flex: 0 1 min(420px, 100%);
}

.learning-stat-panel {
  height: 112px;
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: minmax(280px, 420px) minmax(220px, 1fr);
  align-items: center;
  column-gap: 34px;
  padding: 18px 40px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.learning-progress-card {
  min-width: 0;
}

.learning-progress-card strong {
  display: block;
  color: #14191b;
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  text-align: center;
}

.learning-progress-track {
  height: 6px;
  margin-top: 10px;
  background: #d8ddd8;
  overflow: hidden;
}

.learning-progress-track i {
  display: block;
  width: 0;
  height: 100%;
  background: #1f3a2d;
}

.learning-progress-card span {
  display: block;
  margin-top: 8px;
  color: #66706b;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
}

.learning-meta-stats {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.learning-meta-stats span {
  min-width: 0;
  height: 38px;
  border: 1px solid #e0e0dc;
  background: #faf9f5;
  color: #515755;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  box-sizing: border-box;
  font-size: 13px;
  line-height: 18px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.learning-tabs {
  height: 56px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 28px 0 34px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.learning-tabs-main {
  display: flex;
  align-items: center;
  gap: 42px;
  min-width: 0;
}

.learning-tab-button {
  height: 56px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #6d7474;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 2px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  cursor: pointer;
}

.learning-tab-button.active {
  color: #15191b;
  border-bottom-color: #c91f26;
}

.learning-tab-button em {
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: #edf0ea;
  color: #5f6664;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  font-style: normal;
  font-size: 12px;
  line-height: 18px;
  box-sizing: border-box;
}

.learning-tabs-tools {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
}

.learning-tabs-right {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.learning-tabs-back {
  width: 24px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  flex: 0 0 24px;
}

.learning-tabs-back:hover {
  background: #F1EEE8;
}

.learning-tabs-back span {
  display: block;
  width: 10px;
  height: 10px;
  border-left: 1px solid #151715;
  border-bottom: 1px solid #151715;
  transform: rotate(45deg);
  margin-left: 0;
}

.learning-tabs-main .learning-tab-button + .learning-tab-button {
  margin-left: 50px;
}

.learning-tabs-close {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 1;
  z-index: 1;
}

.learning-tabs-close:hover {
  background: #F1EEE8;
}

.learning-tabs-close span {
  position: relative;
  display: block;
  width: 14px;
  height: 14px;
  margin: 7px auto;
}

.learning-tabs-close span::before,
.learning-tabs-close span::after {
  content: "";
  position: absolute;
  left: -1px;
  top: 6px;
  width: 16px;
  height: 1px;
  background: #000;
}

.learning-tabs-close span::before {
  transform: rotate(45deg);
}

.learning-tabs-close span::after {
  transform: rotate(-45deg);
}

.learning-detail-content {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 30px 38px 36px;
  box-sizing: border-box;
}

.learning-list-box {
  width: min(620px, 100%);
  margin: 0 auto;
  border: 1px solid #dededb;
  background: #fff;
}

.learning-info-row,
.learning-annotation-row {
  width: 100%;
  min-height: 60px;
  border: 0;
  border-bottom: 1px solid #ecebe6;
  background: #fff;
  color: #15191b;
  display: grid;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  text-align: left;
}

.learning-info-row:last-child,
.learning-annotation-row:last-child {
  border-bottom: 0;
}

.learning-info-row {
  grid-template-columns: 86px minmax(0, 1fr) 112px;
  gap: 14px;
  padding: 0 18px;
}

.learning-info-row:hover,
.learning-info-row.active {
  background: #F1EEE8;
}

.learning-row-index,
.learning-info-row em,
.learning-annotation-row span,
.learning-annotation-row em {
  color: #8a8f8e;
  font-size: 13px;
  line-height: 18px;
  font-style: normal;
}

.learning-info-row strong,
.learning-annotation-row strong,
.learning-annotation-card strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-info-row strong,
.learning-annotation-row strong {
  color: #15191b;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}

.learning-detail-content.is-highlight {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 300px);
  gap: 22px;
}

.learning-highlight-list {
  width: 100%;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.learning-highlight-detail {
  min-height: 218px;
  border: 1px solid #dededb;
  background: #fff;
  padding: 20px;
  box-sizing: border-box;
  overflow: auto;
}

.learning-highlight-detail header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.learning-highlight-detail strong {
  min-width: 0;
  color: #15191b;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-highlight-detail span {
  color: #8a8f8e;
  font-size: 13px;
  white-space: nowrap;
}

.learning-highlight-detail p {
  margin: 0;
  color: #222726;
  font-size: 14px;
  line-height: 24px;
  white-space: pre-wrap;
}

.learning-view-switch {
  width: 72px;
  height: 36px;
  position: relative;
  display: block;
  flex: 0 0 72px;
}

.learning-view-switch img {
  width: 72px;
  height: 36px;
  object-fit: contain;
  display: block;
}

.learning-view-hotspot {
  position: absolute;
  top: 0;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.learning-view-list {
  left: 0;
}

.learning-view-grid {
  right: 0;
}

.learning-annotation-grid {
  width: min(620px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, 124px);
  justify-content: start;
  gap: 34px 28px;
}

.learning-annotation-card {
  width: 124px;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.learning-annotation-cover {
  width: 124px;
  height: 166px;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: block;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
}

.learning-annotation-cover.small {
  width: 48px;
  height: 64px;
  flex: 0 0 48px;
}

.learning-annotation-cover.preview {
  width: min(420px, 70vw);
  height: min(560px, 70vh);
  border-color: #cfd2cc;
}

.learning-annotation-page,
.learning-annotation-ink {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.learning-annotation-ink {
  z-index: 1;
  pointer-events: none;
}

.learning-annotation-card strong {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  color: #121618;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-annotation-card em {
  display: block;
  color: #8a8f8e;
  font-size: 12px;
  line-height: 18px;
  font-style: normal;
}

.learning-annotation-list {
  width: min(620px, 100%);
}

.learning-annotation-row {
  grid-template-columns: 64px minmax(0, 1fr) 90px 110px 58px;
  column-gap: 12px;
  min-height: 78px;
  padding: 0 16px;
}

.learning-annotation-row i {
  color: #1f3a2d;
  font-style: normal;
  font-size: 13px;
  line-height: 18px;
}

.learning-empty-box {
  width: min(620px, 100%);
  min-height: 220px;
  margin: 0 auto;
  border: 1px solid #dededb;
  background: #fff;
  color: #8a8f8e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.learning-preview-mask {
  background: rgba(0, 0, 0, .42);
  z-index: 82;
}

.learning-preview-modal {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(720px, calc(100% - 96px));
  height: min(720px, calc(100% - 80px));
  transform: translate(-50%, -50%);
  background: #fff;
  box-shadow: 0 10px 26px rgba(0, 0, 0, .18);
  z-index: 84;
  display: flex;
  flex-direction: column;
}

.learning-preview-stage {
  flex: 1;
  min-height: 0;
  background: #f4f1ec;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 24px;
  box-sizing: border-box;
}

.learning-preview-modal footer {
  height: 64px;
  border-top: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 28px;
  box-sizing: border-box;
}

.learning-preview-modal footer button {
  min-width: 82px;
  height: 36px;
  border: 0;
  background: #1f3a2d;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
}

.learning-detail-mask {
  background: transparent;
  z-index: 70;
}

.learning-detail-modal {
  width: min(930px, calc(100% - 96px));
  height: min(560px, calc(100% - 96px));
  max-height: none;
  border: 1px solid #efede8;
  box-shadow: 0 10px 24px rgba(29, 31, 28, .13);
  overflow: hidden;
}

.learning-detail-resolving {
  min-height: 100%;
  padding: 82px 138px;
  background: #fff;
}

.learning-summary-panel {
  position: relative;
  height: 100%;
  padding: 0;
  background: #fff;
  box-sizing: border-box;
}

.learning-panel-close {
  position: absolute;
  right: 10px;
  top: 10px;
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  padding: 0;
  opacity: 1;
  cursor: pointer;
}

.learning-panel-close:hover {
  opacity: 1;
  background: #F1EEE8;
}

.learning-panel-close span {
  position: relative;
  display: block;
  width: 14px;
  height: 14px;
  margin: 7px auto;
}

.learning-panel-close span::before,
.learning-panel-close span::after {
  content: "";
  position: absolute;
  left: -1px;
  top: 6px;
  width: 16px;
  height: 1px;
  background: #000;
}

.learning-panel-close span::before {
  transform: rotate(45deg);
}

.learning-panel-close span::after {
  transform: rotate(-45deg);
}

.learning-summary-head {
  display: flex;
  align-items: flex-start;
  height: 132px;
  padding: 34px 44px 24px;
  background: #f7f5f0;
  box-sizing: border-box;
}

.learning-summary-cover {
  width: 66px;
  height: 88px;
  flex: 0 0 66px;
  border: 1px solid #e1ded5;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.learning-summary-cover img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.learning-summary-main {
  min-width: 0;
  flex: 1;
}

.learning-summary-main strong {
  display: block;
  color: #202423;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-summary-meta {
  display: flex;
  align-items: center;
  gap: 13px;
  margin: 13px 0 0;
  color: #59615e;
  font-size: 13px;
  line-height: 20px;
  overflow: hidden;
  white-space: nowrap;
}

.learning-meta-size {
  color: #59615e;
  font-size: 13px;
  line-height: 26px;
  font-weight: 500;
}

.learning-meta-chip {
  min-width: 42px;
  height: 28px;
  border: 1px solid #ccd3cd;
  background: #fbfcf9;
  color: #1F3A2D;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.learning-meta-chip.is-sync {
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
}

.learning-meta-chip.is-sync i {
  position: relative;
  width: 13px;
  height: 13px;
  border: 1px solid #52635b;
  border-radius: 50%;
  box-sizing: border-box;
  flex: 0 0 13px;
}

.learning-meta-chip.is-sync i::before,
.learning-meta-chip.is-sync i::after {
  content: "";
  position: absolute;
  box-sizing: border-box;
}

.learning-meta-chip.is-synced i {
  border-color: #1F3A2D;
}

.learning-meta-chip.is-synced i::before {
  display: none;
}

.learning-meta-chip.is-synced i::after {
  left: 50%;
  top: 50%;
  width: 4px;
  height: 7px;
  border-right: 1px solid #1F3A2D;
  border-bottom: 1px solid #1F3A2D;
  transform: translate(-50%, -57%) rotate(45deg);
}

.learning-meta-chip.is-unsynced i {
  border-color: #d84d45;
}

.learning-meta-chip.is-unsynced i::before,
.learning-meta-chip.is-unsynced i::after {
  left: 50%;
  top: 50%;
  width: 7px;
  height: 1px;
  background: #d84d45;
  transform-origin: center;
}

.learning-meta-chip.is-unsynced i::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.learning-meta-chip.is-unsynced i::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.learning-meta-chip.is-unknown i {
  border-color: #aeb6b1;
}

.learning-meta-chip.is-unknown i::before {
  display: none;
}

.learning-meta-chip.is-unknown i::after {
  left: 3px;
  top: 5px;
  width: 4px;
  height: 1px;
  background: #aeb6b1;
}

.learning-summary-list {
  width: 100%;
  max-width: none;
  margin: 0;
  border-top: 0;
}

.learning-summary-row {
  width: 100%;
  height: 96px;
  border: 0;
  border-bottom: 1px solid #e9e9e5;
  background: #fff;
  display: grid;
  grid-template-columns: 108px minmax(0, 1fr) 92px;
  column-gap: 24px;
  align-items: center;
  padding: 0 44px;
  box-sizing: border-box;
  color: #202423;
  text-align: left;
  cursor: pointer;
}

.learning-summary-row.is-progress {
  cursor: default;
}

.learning-summary-row:hover:not(.is-progress) {
  background: #F1EEE8;
}

.learning-summary-row strong {
  grid-column: 1;
  color: #232827;
  font-size: 17px;
  line-height: 24px;
  font-weight: 700;
}

.learning-summary-row em {
  grid-column: 3;
  width: 100%;
  color: #18372c;
  font-size: 23px;
  line-height: 32px;
  font-weight: 700;
  font-style: normal;
  text-align: center;
  justify-self: stretch;
}

.learning-summary-progress {
  grid-column: 2;
  position: relative;
  height: 7px;
  background: #e0e2df;
  overflow: hidden;
}

.learning-summary-progress i {
  display: block;
  height: 100%;
  background: #1d4435;
}

.learning-tabs {
  height: 92px;
  position: relative;
  justify-content: space-between;
  gap: 24px;
  padding: 0 74px 0 34px;
  border-bottom: 1px solid #ebe8e0;
  background: #F6F4F0;
  box-sizing: border-box;
}

.learning-tabs-main {
  gap: 18px;
}

.learning-tabs-right {
  gap: 12px;
}

.learning-tabs .learning-tabs-close {
  width: 28px;
  height: 28px;
}

.learning-tabs .learning-tab-button {
  height: 92px;
  border-bottom-width: 4px;
  color: #3d413f;
  font-size: 19px;
  font-weight: 600;
  gap: 0;
  padding: 2px 10px 0;
}

.learning-tabs .learning-tab-button.active {
  color: #1d201f;
  border-bottom-color: #0e100f;
}

.learning-tabs .learning-tab-button em {
  display: none;
}

.learning-detail-content {
  padding: 0;
  flex: 1;
  max-height: none;
  min-height: 0;
  overflow: auto;
  background: #fff;
}

.learning-list-box {
  width: 100%;
  border: 0;
}

.learning-bookmark-list {
  padding: 0;
  box-sizing: border-box;
}

.learning-info-row {
  min-height: 72px;
  grid-template-columns: minmax(0, 1fr) 72px 18px;
  column-gap: 14px;
  padding: 0 74px;
  border-bottom-color: #eeeae2;
}

.learning-info-row strong {
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
}

.learning-bookmark-row {
  min-height: 112px;
  grid-template-columns: minmax(0, 1fr) 46px;
  column-gap: 14px;
  padding: 0 42px 0 34px;
  border-bottom-color: #ecece8;
  cursor: default;
}

.learning-bookmark-row:hover {
  background: #F1EEE8;
}

.learning-bookmark-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
}

.learning-bookmark-main strong {
  color: #252927;
  font-size: 19px;
  line-height: 25px;
  font-weight: 700;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.learning-bookmark-main em {
  color: #a6aca8;
  font-size: 15px;
  line-height: 20px;
  font-style: normal;
  font-weight: 500;
}

.learning-highlight-row {
  min-height: 112px;
  grid-template-columns: minmax(0, 1fr) 18px;
  column-gap: 22px;
  padding: 0 42px 0 34px;
  border-bottom-color: #ecece8;
}

.learning-highlight-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 11px;
  padding: 18px 0;
}

.learning-highlight-main strong {
  color: #252927;
  font-size: 19px;
  line-height: 25px;
  font-weight: 700;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.learning-highlight-main em {
  color: #a6aca8;
  font-size: 15px;
  line-height: 20px;
  font-style: normal;
  font-weight: 500;
}

.learning-row-page {
  color: #9ca29f;
  font-size: 15px;
  line-height: 20px;
  text-align: right;
  align-self: center;
}

.learning-info-row i {
  width: 9px;
  height: 9px;
  border-top: 1px solid #9ba09b;
  border-right: 1px solid #9ba09b;
  transform: rotate(45deg);
  justify-self: end;
}

.learning-highlight-row i {
  width: 10px;
  height: 10px;
  border-top: 2px solid #555d58;
  border-right: 2px solid #555d58;
}

.learning-info-row:hover,
.learning-info-row.active {
  background: #F1EEE8;
}

.learning-info-row.learning-bookmark-row:hover {
  background: #F1EEE8;
}

.learning-info-row.learning-highlight-row:hover,
.learning-info-row.learning-highlight-row.active {
  background: #F1EEE8;
}

.learning-highlight-page {
  background: #fff;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.learning-highlight-page header {
  height: 92px;
  position: relative;
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  align-items: center;
  border-bottom: 1px solid #ebe8e0;
  padding: 0 74px 0 34px;
  background: #F6F4F0;
  box-sizing: border-box;
}

.learning-highlight-page header .learning-highlight-back {
  width: 24px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
}

.learning-highlight-page header .learning-highlight-back span {
  display: block;
  width: 10px;
  height: 10px;
  border-left: 1px solid #151715;
  border-bottom: 1px solid #151715;
  transform: rotate(45deg);
  margin-left: 0;
}

.learning-highlight-page header strong {
  color: #1d201f;
  font-size: 18px;
  line-height: 26px;
  font-weight: 700;
  text-align: left;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.learning-highlight-head-page {
  color: #59615e;
  font-size: 12px;
  line-height: 18px;
  font-style: normal;
  padding: 4px 10px;
  border: 1px solid #d7d6cf;
  border-radius: 999px;
  background: #fbfcf9;
}

.learning-highlight-page article {
  flex: 1;
  min-height: 0;
  padding: 30px 34px 34px 34px;
  background: #fff;
  overflow: auto;
  box-sizing: border-box;
}

.learning-highlight-body {
  min-height: auto;
  border: 0;
  background: transparent;
  box-shadow: none;
  padding: 0;
  box-sizing: border-box;
}

.learning-highlight-meta {
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: #8f9590;
  font-size: 14px;
  line-height: 20px;
}

.learning-highlight-meta em {
  font-style: normal;
  color: #51655a;
  background: #eef3ee;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  line-height: 18px;
}

.learning-highlight-head-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.learning-highlight-text {
  margin: 0;
  color: #252a27;
  font-size: 16px;
  line-height: 30px;
  white-space: pre-wrap;
  word-break: break-word;
}

.learning-detail-content.is-annotations {
  position: relative;
  padding-top: 0;
}

.learning-view-switch {
  width: 68px;
  height: 36px;
  flex-basis: 68px;
}

.learning-view-switch img {
  width: 68px;
  height: 36px;
}

.learning-view-hotspot {
  width: 34px;
  height: 36px;
}

.learning-annotation-grid {
  width: 100%;
  max-height: none;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  gap: 29.5px 15px;
  padding: 36px 34px 40px;
  overflow: auto;
  box-sizing: border-box;
}

.learning-annotation-card {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
  --paper-grid-column-gap: 15px;
  --paper-grid-row-gap: 44px;
  --paper-grid-column-gap-half: calc(var(--paper-grid-column-gap) / 2);
  --paper-grid-row-gap-half: calc(var(--paper-grid-row-gap) / 2);
}

.learning-annotation-card > * {
  position: relative;
  z-index: 1;
}

.learning-annotation-card.active {
  z-index: 1;
}

.learning-annotation-card.active::before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--paper-grid-column-gap-half));
  right: calc(-1 * var(--paper-grid-column-gap-half));
  bottom: calc(-1 * var(--paper-grid-row-gap-half));
  left: calc(-1 * var(--paper-grid-column-gap-half));
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.learning-annotation-card.active.grid-edge-bottom::before {
  bottom: 0;
}

.learning-annotation-card.active .learning-annotation-cover,
.learning-annotation-card:hover .learning-annotation-cover,
.learning-annotation-card:focus-visible .learning-annotation-cover {
  border-color: #b6b8b4;
  background: #f8f8f6;
}

.learning-annotation-card.active .learning-annotation-cover {
  background: #fff;
}

.learning-annotation-cover {
  width: 148px;
  height: 196.4px;
  border-color: #e0ddd5;
}

.learning-annotation-card strong {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-annotation-card em {
  font-size: 12px;
  line-height: 18px;
  text-align: left;
}

.learning-annotation-list {
  width: 100%;
}

.learning-annotation-row {
  min-height: 78px;
  grid-template-columns: 66px minmax(0, 1fr) 170px;
  column-gap: 0;
  padding: 0 34px;
  border-bottom-color: #dededb;
}

.learning-annotation-cover.small {
  width: 54px;
  height: 54px;
  flex-basis: 54px;
}

.learning-annotation-row strong {
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
}

.learning-annotation-row em {
  color: #7d8284;
  font-size: 14px;
  line-height: 22px;
  justify-self: end;
  text-align: right;
}

.learning-annotation-row:hover,
.learning-annotation-row.active {
  background: #f1eee8;
}

.learning-empty-box {
  width: 100%;
  min-height: 100%;
  border: 0;
}

.learning-preview-modal {
  inset: 0;
  width: auto;
  height: auto;
  transform: none;
  background: #ededeb;
  box-shadow: none;
  z-index: 84;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.learning-annotation-preview-head {
  height: 64px;
  border-bottom: 1px solid #dededb;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 32px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.learning-annotation-preview-title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.learning-annotation-preview-back {
  width: 26px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.learning-annotation-preview-back span {
  width: 12px;
  height: 12px;
  border-left: 2px solid #111;
  border-bottom: 2px solid #111;
  transform: rotate(45deg);
  display: block;
}

.learning-annotation-preview-title strong {
  min-width: 0;
  color: #14191b;
  font-size: 22px;
  line-height: 32px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-annotation-preview-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.learning-annotation-preview-tool {
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.learning-annotation-preview-tool img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.learning-annotation-preview-tool.active {
  background: #ebe8e2;
}

.learning-annotation-preview-tool:disabled,
.learning-annotation-preview-back:disabled {
  opacity: .35;
  cursor: default;
}

.learning-annotation-preview-body {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  background: #ededeb;
  --learning-annotation-preview-base-height: max(480px, calc(100vh - 64px));
  --learning-annotation-preview-base-width: calc(var(--learning-annotation-preview-base-height) * 0.75);
}

.learning-annotation-preview-shell {
  height: 100%;
  overflow: auto;
  display: block;
  padding: 0 96px 0 32px;
  box-sizing: border-box;
  --learning-annotation-preview-zoom: 1;
  cursor: grab;
  user-select: none;
}

.learning-annotation-preview-shell.dragging {
  cursor: grabbing;
}

.learning-annotation-preview-zoom {
  width: calc(var(--learning-annotation-preview-base-width) * var(--learning-annotation-preview-zoom));
  height: calc(var(--learning-annotation-preview-base-height) * var(--learning-annotation-preview-zoom));
  margin: 0 auto;
}

.learning-annotation-preview-stage {
  width: var(--learning-annotation-preview-base-width);
  height: var(--learning-annotation-preview-base-height);
  background: #fff;
  border: 1px solid #d7d9d6;
  transform-origin: top left;
  transform: scale(var(--learning-annotation-preview-zoom));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.learning-annotation-preview-page {
  width: 100%;
  height: 100%;
  position: relative;
  background: #fff;
}

.learning-annotation-preview-page .learning-annotation-page,
.learning-annotation-preview-page .learning-annotation-ink {
  object-fit: contain;
}

.learning-annotation-preview-empty {
  width: 100%;
  height: 100%;
  background: #fff;
}

.learning-annotation-preview-pages-grid {
  height: 100%;
  overflow: auto;
  padding: 20px 20px 44px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  align-content: start;
  justify-content: start;
  gap: 29.5px 15px;
  min-width: calc(148px * 4 + 15px * 3);
  box-sizing: border-box;
}

.learning-annotation-preview-pages-grid button {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
}

.learning-annotation-preview-pages-grid button.selected {
  z-index: 1;
}

.learning-annotation-preview-pages-grid button.selected::before {
  content: '';
  position: absolute;
  top: -7.5px;
  right: -7.5px;
  bottom: -22px;
  left: -7.5px;
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.learning-annotation-preview-pages-grid button.selected.grid-edge-top::before {
  top: -7.5px;
}

.learning-annotation-preview-pages-grid button.selected:not(.grid-selected-row-start)::before {
  top: -7.5px;
}

.learning-annotation-preview-pages-grid button.selected.grid-selection-continues-below::before {
  bottom: -22px;
}

.learning-annotation-preview-pages-grid button > * {
  position: relative;
  z-index: 1;
}

.learning-annotation-preview-pages-grid button > span {
  width: 148px;
  height: 196.4px;
  flex: 0 0 196.4px;
  border: 1px solid #d7d9d6;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.learning-annotation-preview-page-thumb {
  width: 100%;
  height: 100%;
  display: block;
  position: relative;
}

.learning-annotation-preview-pages-grid button.selected > span {
  border-color: #b6b8b4;
  background: #fff;
}

.learning-annotation-preview-pages-grid button.active > span {
  border-bottom: 5px solid #050505;
}

.learning-annotation-preview-pages-grid strong {
  display: block;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  color: #121618;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.learning-annotation-preview-controls {
  position: absolute;
  right: 24px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.learning-annotation-preview-controls button {
  width: 36px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: transparent;
  position: relative;
  cursor: pointer;
}

.learning-annotation-preview-controls button::before {
  content: "";
  width: 9px;
  height: 9px;
  border-left: 1px solid #111;
  border-bottom: 1px solid #111;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-35%, -50%) rotate(45deg);
}

.learning-annotation-preview-controls button:last-child::before {
  transform: translate(-65%, -50%) rotate(225deg);
}

.learning-annotation-preview-controls button:disabled {
  opacity: .3;
  cursor: default;
}

.learning-annotation-preview-count {
  display: flex;
  align-items: center;
  gap: 10px;
}

.learning-annotation-preview-count span {
  min-width: 66px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f8f4ef;
  color: #5d6062;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-sizing: border-box;
}

.daily-header {
  height: 64px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px 0 32px;
  box-sizing: border-box;
  flex-shrink: 0;
  background: #f8f4ef;
}

.daily-title-line {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.daily-title-line strong {
  color: #202428;
  font-size: 24px;
  line-height: 34px;
  font-weight: 700;
}

.daily-title-line i {
  width: 1px;
  height: 18px;
  background: #d0cec8;
  display: block;
}

.daily-title-date {
  color: #7a7e7d;
  font-size: 14px;
  line-height: 18px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.daily-title-date-part {
  display: inline-flex;
  align-items: center;
  height: 18px;
  line-height: 18px;
}

.daily-title-date-separator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex: 0 0 auto;
  height: 18px;
}

.daily-title-date-separator.line {
  width: 6px;
}

.daily-title-date-separator.line::before {
  content: '';
  width: 4px;
  height: 1px;
  background: currentColor;
  opacity: .8;
}

.daily-title-date-separator.text {
  min-width: 8px;
  line-height: 18px;
}

.daily-search-button {
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}

.daily-search-button img {
  width: 19px;
  height: 19px;
  object-fit: contain;
  filter: brightness(0);
}

.daily-page {
  min-height: 0;
  flex: 1;
  display: flex;
  background: #f8f4ef;
}

.daily-date-panel {
  width: 376px;
  flex: 0 0 376px;
  border-right: 1px solid #d8d6cf;
  padding: 34px 28px 0 26px;
  box-sizing: border-box;
  background: #f8f4ef;
  position: relative;
  font-family: PingFangSC, PingFang SC, Helvetica Neue, Arial, sans-serif;
}

.daily-date-panel::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 416px;
  height: 1px;
  background: #d8d6cf;
}

.daily-calendar-head {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 26px;
}

.daily-calendar-head strong {
  color: #111;
  font-size: 18px;
  line-height: 25px;
  font-weight: 600;
  margin-right: 34px;
  white-space: nowrap;
}

.daily-calendar-head > span {
  min-width: 43px;
  color: #111;
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  text-align: center;
  margin: 0 7px;
}

.daily-calendar-head button {
  width: 24px;
  height: 24px;
  border: 0;
  background: transparent;
  cursor: pointer;
  position: relative;
  padding: 0;
  font-family: inherit;
}

.daily-calendar-prev::before,
.daily-calendar-next::before {
  content: '';
  width: 8px;
  height: 8px;
  border-left: 2px solid #111;
  border-bottom: 2px solid #111;
  position: absolute;
  top: 7px;
  left: 8px;
  transform: rotate(45deg);
}

.daily-calendar-next::before {
  left: 6px;
  transform: rotate(225deg);
}

.daily-calendar-today {
  margin-left: auto;
  width: 35px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.daily-calendar-today img {
  width: 31px;
  height: 31px;
  display: block;
  object-fit: contain;
}

.daily-calendar-today span {
  position: absolute;
  z-index: 1;
  left: 50%;
  top: calc(54% + 1px);
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1px;
  color: #111;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  pointer-events: none;
  text-shadow: -1px 0 #f8f4ef, 1px 0 #f8f4ef, 0 -1px #f8f4ef, 0 1px #f8f4ef;
}

.daily-weekdays,
.daily-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.daily-weekdays {
  margin-bottom: 15px;
  color: #111;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  text-align: center;
}

.daily-calendar-spin /deep/ .ant-spin-container {
  min-height: 278px;
}

.daily-date-cell {
  height: 43px;
  border: 0;
  background: transparent;
  color: #111;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-family: inherit;
}

.daily-date-cell span {
  width: 37px;
  height: 37px;
  border-radius: 2px;
  border: 1px solid transparent;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  gap: 0;
}

.daily-date-cell.muted {
  color: #8e928f;
}

.daily-date-cell.week-selected::before {
  content: '';
  position: absolute;
  z-index: 0;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: #E0E1D9;
  pointer-events: none;
}

.daily-date-cell.week-start::before {
  border-radius: 2px 0 0 2px;
}

.daily-date-cell.week-end::before {
  border-radius: 0 2px 2px 0;
}

.daily-date-cell.today span {
  color: #fff;
  background: #25403C;
  border-radius: 0;
  font-weight: 700;
}

.daily-date-cell.selected span {
  border-color: transparent;
  border-radius: 0;
  box-shadow: inset 0 0 0 2px #25403C;
}

.daily-date-cell.today.selected span {
  box-shadow: inset 0 0 0 2px #111;
}

.daily-date-markers {
  position: absolute;
  top: calc(50% + 11px);
  left: 50%;
  width: 10px;
  height: 4px;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 4px;
  line-height: 0;
  color: #111;
  font-weight: 400;
}

.daily-date-markers.empty {
  visibility: hidden;
}

.daily-date-markers.single {
  gap: 0;
}

.daily-date-day {
  position: absolute;
  top: 50%;
  left: 50%;
  display: block;
  min-height: 14px;
  line-height: 14px;
  font-style: normal;
  transform: translate(-50%, -50%);
}

.daily-date-marker {
  width: 4px;
  height: 4px;
  display: block;
  flex: 0 0 4px;
  position: relative;
  box-sizing: border-box;
  object-fit: contain;
}

.daily-main-panel {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8f4ef;
  position: relative;
}

.daily-list-head {
  height: 74px;
  border-bottom: 1px solid #d8d6cf;
  padding: 0 37px 0 30px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.daily-tabs {
  display: flex;
  align-items: center;
  gap: 49px;
  height: 100%;
}

.daily-tabs button {
  height: 100%;
  border: 0;
  background: transparent;
  color: #111;
  font-size: 16px;
  font-weight: 400;
  padding: 0;
  cursor: pointer;
  position: relative;
}

.daily-tabs button.active {
  color: #101313;
  font-weight: 700;
}

.daily-tabs button.active::after {
  content: '';
  height: 4px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #111;
  position: absolute;
}

.daily-list-actions {
  display: flex;
  align-items: center;
  gap: 26px;
}

.daily-filter-wrap {
  position: relative;
  width: max-content;
  min-width: 110px;
  height: 36px;
  flex: 0 0 auto;
}

.daily-filter-button {
  width: 110px;
  height: 36px;
  border: 1px solid #c8c8c5;
  background: transparent;
  color: #202428;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  box-sizing: border-box;
  cursor: pointer;
}

.daily-filter-button.has-calendar {
  justify-content: flex-start;
  gap: 8px;
  padding-left: 10px;
  padding-right: 10px;
}

.daily-filter-button.has-calendar > i {
  margin-left: auto;
}

.daily-filter-button em,
.daily-filter-menu span {
  font-style: normal;
  font-size: 14px;
  font-weight: 500;
  color: #111;
  white-space: nowrap;
}

.daily-filter-button em {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.daily-filter-button > i {
  width: 7px;
  height: 7px;
  border-right: 1.5px solid #111;
  border-bottom: 1.5px solid #111;
  transform: rotate(45deg);
  margin-top: -3px;
}

.daily-filter-calendar {
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
  flex: 0 0 18px;
}

.daily-filter-menu {
  position: absolute;
  left: auto;
  right: 0;
  top: 40px;
  min-width: 100%;
  width: max-content;
  max-width: calc(100vw - 24px);
  box-sizing: border-box;
  border: 1px solid #d8d8d8;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .04);
  z-index: 20;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
}

.daily-filter-wrap.drop-left .daily-filter-menu {
  left: auto;
  right: 0;
}

.daily-period-menu {
  min-width: 100%;
  width: max-content;
}

.daily-finish-menu {
  left: auto;
  right: 0;
  min-width: 100%;
  width: max-content;
}

.daily-filter-menu button {
  width: max-content;
  min-width: 100%;
  max-width: 100%;
  height: 40px;
  border: 0;
  background: #fff;
  color: #171b1d;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 16px;
  padding: 0 20px;
  box-sizing: border-box;
  cursor: pointer;
  white-space: nowrap;
}

.daily-filter-menu button > span,
.daily-period-label > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.daily-period-menu button {
  padding-left: 18px;
  padding-right: 18px;
}

.daily-period-label {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.daily-period-icon {
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
  flex: 0 0 18px;
}

.daily-filter-menu button.active {
  background: #dce2d8;
  font-weight: 500;
}

.daily-filter-menu button > i {
  width: 20px;
  height: 20px;
  display: block;
  background: url('~@/assets/pc/daily/filter-check-active.png') calc(50% + 3px) center / contain no-repeat;
  flex: 0 0 20px;
}

.daily-view-switch {
  width: 72px;
  height: 36px;
  position: relative;
  flex: 0 0 72px;
}

.daily-view-switch img {
  width: 72px;
  height: 36px;
  display: block;
  object-fit: contain;
}

.daily-view-hotspot {
  position: absolute;
  top: 0;
  width: 36px;
  height: 36px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.daily-view-list {
  left: 0;
}

.daily-view-grid {
  right: 0;
}

.daily-selection-layer {
  position: absolute;
  top: 12px;
  right: 0;
  left: 0;
  z-index: 40;
  padding: 0 12px;
  box-sizing: border-box;
  pointer-events: none;
  will-change: transform;
}

.daily-selection-layer.dragging {
  user-select: none;
}

.daily-selection-bar {
  height: 48px;
  width: 100%;
  min-width: 0;
  padding: 0 16px;
  background: #DDE4D9;
  color: #333;
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  pointer-events: auto;
  cursor: grab;
}

.daily-selection-layer.dragging .daily-selection-bar {
  cursor: grabbing;
}

.daily-selection-bar button {
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1f2326;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.daily-selection-bar button:first-child span {
  width: 14px;
  height: 14px;
  position: relative;
  display: inline-block;
}

.daily-selection-bar button:first-child span::before,
.daily-selection-bar button:first-child span::after {
  content: '';
  width: 14px;
  height: 1.5px;
  background: #111;
  position: absolute;
  left: 0;
  top: 6px;
}

.daily-selection-bar button:first-child span::before {
  transform: rotate(45deg);
}

.daily-selection-bar button:first-child span::after {
  transform: rotate(-45deg);
}

.daily-selection-bar strong {
  flex: 1;
  color: #606462;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
}

.daily-selection-bar img {
  width: 20px;
  height: 20px;
}

.daily-content {
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.daily-content /deep/ .daily-list-spin.ant-spin-nested-loading,
.daily-content /deep/ .ant-spin-container {
  height: 100%;
}

.daily-content-inner {
  height: 100%;
  overflow: auto;
  padding: 0 0 28px;
  box-sizing: border-box;
}

.daily-content.is-grid .daily-content-inner {
  padding: 24px 28px 40px;
}

.daily-empty {
  height: 100%;
  min-height: 340px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a8aaa7;
  font-size: 12px;
}

.daily-empty img {
  width: 130px;
  height: auto;
  margin-bottom: 12px;
}

.daily-empty.is-todo-empty img {
  width: 140px;
  margin-bottom: 0;
}

.daily-todo-list {
  padding: 0 12px;
}

.daily-todo-row {
  min-height: 78px;
  border-bottom: 1px solid #ebe7df;
  display: grid;
  grid-template-columns: 21px minmax(0, 1fr) 170px 52px 48px;
  align-items: center;
  column-gap: 12px;
  color: #111;
  font-size: 14px;
  box-sizing: border-box;
  padding: 0 18px 0 13px;
  cursor: default;
  transition: background-color .15s ease;
}

.daily-todo-row:hover {
  background: #F1EEE8;
}

.daily-todo-row.done {
  color: #9e9e9e;
}

.daily-todo-row.done .daily-todo-level {
  opacity: 0.45;
}

.daily-todo-row.done .daily-todo-content img {
  filter: grayscale(100%);
  opacity: 0.45;
}

.daily-todo-level {
  grid-column: 1;
  width: 21px;
  height: 21px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.daily-todo-level.empty {
  display: none;
}

.daily-todo-level img {
  width: 20px;
  height: 20px;
  display: block;
  object-fit: contain;
}

.daily-todo-level.important img {
  width: 21px;
  height: 21px;
}

.daily-todo-content {
  grid-column: 2;
  min-width: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.daily-todo-level.empty + .daily-todo-content {
  grid-column: 1 / 3;
}

.daily-todo-content strong {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: anywhere;
  display: block;
}

.daily-todo-content img {
  width: auto;
  height: 62px;
  max-width: 100%;
  max-height: none;
  object-fit: contain;
  background: transparent;
  border: 0;
  display: block;
}

.daily-todo-date {
  grid-column: 3;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  color: #7d8284;
  font-size: 14px;
  line-height: 22px;
  white-space: nowrap;
}

.daily-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  column-gap: 15px;
  row-gap: 29.5px;
  align-content: start;
  min-width: calc(148px * 4 + 15px * 3);
}

.daily-card {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 0;
  cursor: pointer;
  position: relative;
  color: #0f1112;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
  --daily-grid-column-gap-half: calc(15px / 2);
  --daily-grid-row-gap-half: calc(44px / 2);
}

.daily-card.selected {
  z-index: 1;
}

.daily-card.selected::before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--daily-grid-column-gap-half));
  right: calc(-1 * var(--daily-grid-column-gap-half));
  bottom: calc(-1 * var(--daily-grid-row-gap-half));
  left: calc(-1 * var(--daily-grid-column-gap-half));
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.daily-card.selected.grid-edge-top::before {
  top: calc(-1 * var(--daily-grid-column-gap-half));
}

.daily-card > * {
  position: relative;
  z-index: 1;
}

.daily-card-cover {
  width: 148px;
  height: 196.4px;
  flex: 0 0 196.4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d7d9d6;
  box-sizing: border-box;
  margin-bottom: 0;
  overflow: hidden;
}

.daily-card:hover .daily-card-cover,
.daily-card.selected .daily-card-cover,
.daily-card:focus-visible .daily-card-cover {
  border-color: #b6b8b4;
  background: #f8f8f6;
}

.daily-card.selected .daily-card-cover {
  background: #fff;
}

.daily-grid.all-empty .daily-card:hover .daily-card-cover,
.daily-grid.all-empty .daily-card.selected .daily-card-cover,
.daily-grid.all-empty .daily-card:focus-visible .daily-card-cover {
  background: #fff;
}

.daily-card-cover img {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center center;
  display: block;
}

.daily-card-cover img.daily-empty-cover-icon,
.daily-row-cover img.daily-empty-cover-icon {
  width: 46px;
  height: 46px;
  max-width: 46px;
  max-height: 46px;
}

.daily-card-cover em {
  width: 100%;
  height: 100%;
  background: #fff;
  display: block;
  border-radius: 0;
}

.daily-grid.all-empty .daily-card-cover em {
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 0;
}

.daily-row-cover em,
.daily-preview-grid em {
  width: 40px;
  height: 40px;
  background: #e8eee8;
  display: block;
  border-radius: 2px;
}

.daily-card-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  min-width: 0;
}

.daily-card strong {
  display: block;
  min-width: 0;
  flex: 1 1 auto;
  height: 22px;
  color: #0f1112;
  font-size: 17px;
  line-height: 22px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.daily-card-count {
  display: block;
  width: 100%;
  height: 19px;
  flex: 0 0 19px;
  margin-top: 0;
  color: #9a9a9a;
  font-size: 13px;
  line-height: 19px;
  text-align: right;
}

.daily-table {
  padding: 0 12px;
}

.daily-favorite-status {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}

.daily-card-favorite-status {
  flex: 0 0 18px;
}

.daily-row {
  min-height: 78px;
  border-bottom: 1px solid #dededb;
  display: grid;
  grid-template-columns: 66px minmax(220px, 1fr) 120px 170px 52px 48px;
  align-items: center;
  gap: 0;
  cursor: pointer;
  color: #121618;
  outline: none;
  padding: 0 18px;
  box-sizing: border-box;
}

.daily-row:hover,
.daily-row:focus-visible {
  background: #F1EEE8;
  outline: none;
}

.daily-row.selected {
  background: #DDE4D9;
  outline: none;
}

.daily-row-cover {
  width: 46px;
  height: 62px;
  border: 1px solid #efe7e7;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.daily-row-cover em {
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 0;
}

.daily-row-cover img {
  width: 46px;
  height: auto;
  max-width: 46px;
  max-height: 60px;
  object-fit: contain;
  display: block;
}

.daily-row strong {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 78px;
  min-width: 0;
  color: #121618;
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.daily-row-count {
  grid-column: 3;
}

.daily-row-date {
  grid-column: 4;
}

.daily-row-favorite-status {
  grid-column: 5;
}

.daily-row > span:not(.daily-row-cover):not(.paper-more-wrap) {
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
  color: #7d8284;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}

.daily-row .paper-more-wrap {
  grid-column: 6;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 78px;
  position: relative;
}

.daily-row:hover .paper-more-button,
.daily-row .paper-more-button:focus-visible {
  background: #F1EEE8;
}

.daily-preview-page {
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8f4ef;
  position: relative;
}

.daily-preview-page .paper-pages-grid img {
  object-fit: contain;
}

.daily-preview-head {
  height: 64px;
  flex: 0 0 64px;
  border-bottom: 1px solid #dededb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 32px;
  box-sizing: border-box;
}

.daily-preview-title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.daily-preview-title strong {
  min-width: 0;
  color: #111;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.daily-preview-back {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  position: relative;
}

.daily-preview-back span,
.daily-preview-back span::before {
  position: absolute;
  display: block;
}

.daily-preview-back span {
  width: 18px;
  height: 2px;
  background: #1d2223;
  left: 5px;
  top: 13px;
}

.daily-preview-back span::before {
  content: '';
  width: 9px;
  height: 9px;
  border-left: 2px solid #1d2223;
  border-bottom: 2px solid #1d2223;
  left: 0;
  top: -4px;
  transform: rotate(45deg);
}

.daily-preview-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.daily-preview-tool {
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.daily-preview-tool img {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.daily-preview-tool.active {
  background: #e4ebe1;
}

.daily-preview-tool:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.daily-preview-body {
  min-height: 0;
  flex: 1;
  position: relative;
  overflow: hidden;
  --daily-preview-base-height: max(480px, calc(100vh - 64px));
  --daily-preview-base-width: calc(var(--daily-preview-base-height) * 0.75);
}

.daily-preview-body.is-resolving::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 30;
  cursor: progress;
}

.daily-preview-body /deep/ .daily-preview-spin.ant-spin-nested-loading,
.daily-preview-body /deep/ .ant-spin-container {
  height: 100%;
}

.daily-preview-shell {
  height: 100%;
  overflow: auto;
  cursor: grab;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  --daily-preview-zoom: 1;
}

.daily-preview-shell.dragging {
  cursor: grabbing;
  user-select: none;
}

.daily-preview-zoom {
  width: calc(var(--daily-preview-base-width) * var(--daily-preview-zoom));
  height: calc(var(--daily-preview-base-height) * var(--daily-preview-zoom));
  flex: 0 0 auto;
}

.daily-preview-stage {
  width: var(--daily-preview-base-width);
  min-height: var(--daily-preview-base-height);
  transform: scale(var(--daily-preview-zoom));
  transform-origin: top center;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #fff;
  box-shadow: 0 0 0 1px #ebe7df;
}

.daily-preview-stage img {
  width: 100%;
  height: auto;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.daily-preview-empty {
  width: 100%;
  height: var(--daily-preview-base-height);
  background: #fff;
}

.daily-preview-grid {
  height: 100%;
  overflow: auto;
  padding: 32px 32px 50px;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 148px));
  column-gap: 15px;
  row-gap: 29.5px;
  align-content: start;
  min-width: calc(148px * 4 + 15px * 3);
}

.daily-preview-grid button {
  width: 148px;
  height: 245.4px;
  min-height: 245.4px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  outline: none;
  z-index: 0;
}

.daily-preview-grid button.selected {
  z-index: 1;
}

.daily-preview-grid button.selected::before {
  content: '';
  position: absolute;
  top: -7.5px;
  right: -7.5px;
  bottom: -22px;
  left: -7.5px;
  background: #DDE4D9;
  pointer-events: none;
  z-index: 0;
}

.daily-preview-grid button.selected.grid-edge-top::before {
  top: -7.5px;
}

.daily-preview-grid button.selected:not(.grid-selected-row-start)::before {
  top: -7.5px;
}

.daily-preview-grid button.selected.grid-selection-continues-below::before {
  bottom: -22px;
}

.daily-preview-grid button > * {
  position: relative;
  z-index: 1;
}

.daily-preview-grid button span {
  width: 148px;
  height: 196.4px;
  flex: 0 0 196.4px;
  border: 1px solid #d7d9d6;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
}

.daily-preview-grid button span em {
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 0;
}

.daily-preview-grid button.selected span {
  background: #fff;
  border-color: #b6b8b4;
}

.daily-preview-grid button.active span {
  border-bottom: 5px solid #050505;
}

.daily-preview-grid img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.daily-preview-grid strong {
  display: block;
  width: 100%;
  height: 22px;
  flex: 0 0 22px;
  box-sizing: border-box;
  margin-top: 8px;
  color: #0f1112;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daily-preview-controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16px;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.daily-preview-count {
  display: inline-flex;
  align-items: center;
  pointer-events: auto;
}

.daily-preview-controls button {
  width: 38px;
  height: 36px;
  border: 1px solid #d8d8d8;
  background: #f8f4ef;
  cursor: pointer;
  position: relative;
}

.daily-preview-controls button::before {
  content: '';
  width: 8px;
  height: 8px;
  border-left: 2px solid #6f7371;
  border-bottom: 2px solid #6f7371;
  position: absolute;
  left: 15px;
  top: 13px;
  transform: rotate(45deg);
}

.daily-preview-controls button:last-child::before {
  left: 12px;
  transform: rotate(225deg);
}

.daily-preview-controls button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.daily-preview-count span {
  min-width: 66px;
  height: 36px;
  border-top: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
  background: #f8f4ef;
  color: #5d6062;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-sizing: border-box;
}
</style>

